// ============================================================================
// AccountingSettingsPage.jsx - Admin-only Settings under Accounting
// ============================================================================
// Tabs:
//   1. General & Accounting
//   2. Timezone
//   3. Currency & FX (enhanced with auto-refresh)
//   4. Colours (enhanced with team colours)
//   5. Categories
//   6. Hiring
//   7. Invoice PDF
//   8. Email Templates
//   9. Database
// ============================================================================
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Globe, DollarSign, Palette, Tag, Database, Settings,
  Plus, Edit2, Trash2, Save, Upload, Download, Copy, Star,
  RefreshCw, Check, X, AlertTriangle, ChevronUp, ChevronDown, Archive,
  Zap, Link2, Users, Mail, Eye, FileText, Lock, TrendingUp
} from 'lucide-react'; // eslint-disable-line no-unused-vars
import { BrioMonthPicker, useFullScreenPage, BrioFullScreenButton } from '../../components/BrioComponents';
import { MBTI_COLORS } from '../../utils/personalityHelpers';
import { useSalaryLedger } from '../../hooks/useSalaryLedger';
import { RULE_META, DEFAULT_RULE_CONFIGS } from '../../hooks/useRadarSettings';
import useSupabaseEmailTemplates from '../../hooks/useSupabaseEmailTemplates';
import { useSpecialismOptions } from '../../hooks/useSpecialismOptions';
import { DROPBOX_WEBHOOK_URL, fetchDropboxWatchState, runDropboxWatchScan } from '../comms/folderAlerts';
import { getDefaultTemplate, getTemplateDefinition, getAvailablePlaceholders, validateTemplatePlaceholders } from '../../utils/emailTemplates';
import {
  resolveNotesTokens,
  resolveQuantityTokens,
  INTERIOR_SECTIONS,
  INTERIOR_SECTION_LABELS,
  INTERIOR_SECTION_ORDER,
} from '../../utils/contractScopeHelpers';
import { supabase } from '../../supabaseClient';
import { resolveBrandingImageSrc } from '../../utils/brandingImages';
import { uploadBrandingImage } from '../../utils/brandingImageUpload';
import { DEFAULT_TEMPLATE_HTML } from '../../hooks/useMOMData';
// ============================================================================
// CONSTANTS
// ============================================================================
const COMMON_TIMEZONES = [
  'Europe/Amsterdam','Europe/London','Europe/Berlin','Europe/Paris',
  'Europe/Rome','Europe/Madrid','Europe/Zurich','Europe/Brussels',
  'Europe/Moscow','Europe/Istanbul',
  'Asia/Kolkata','Asia/Dubai','Asia/Singapore','Asia/Tokyo',
  'Asia/Shanghai','Asia/Hong_Kong','Asia/Seoul','Asia/Jakarta',
  'Asia/Bangkok','Asia/Karachi','Asia/Colombo','Asia/Manila',
  'America/New_York','America/Chicago','America/Los_Angeles',
  'America/Toronto','America/Sao_Paulo','America/Mexico_City',
  'Africa/Johannesburg','Africa/Cairo','Africa/Lagos','Africa/Nairobi',
  'Australia/Sydney','Pacific/Auckland','UTC'
].sort();

const EXPORT_TABLES = [
  // Settings & Configuration
  { key: 'app_settings',              label: 'App Settings',              def: true,  group: 'Settings' },
  { key: 'fx_rates_monthly',          label: 'FX Rates',                  def: true,  group: 'Settings' },
  { key: 'project_categories',        label: 'Project Categories',        def: true,  group: 'Settings' },
  { key: 'color_schemes',             label: 'Color Schemes',             def: true,  group: 'Settings' },
  { key: 'holidays',                  label: 'Holidays',                  def: true,  group: 'Settings' },
  { key: 'leave_policies',            label: 'Leave Policies',            def: true,  group: 'Settings' },
  { key: 'notice_policies',           label: 'Notice Policies',           def: true,  group: 'Settings' },
  { key: 'inflation_cpi_yearly',      label: 'Inflation CPI Yearly',      def: false, group: 'Settings' },
  { key: 'email_templates',           label: 'Email Templates',           def: false, group: 'Settings' },
  { key: 'radar_settings',            label: 'Radar Settings',            def: false, group: 'Settings' },
  { key: 'settings_audit_log',        label: 'Settings Audit Log',        def: false, group: 'Settings' },
  // Users & Team
  { key: 'users',                     label: 'Users',                     def: false, group: 'Team' },
  { key: 'user_profiles',             label: 'User Profiles',             def: false, group: 'Team' },
  { key: 'user_addresses',            label: 'User Addresses',            def: false, group: 'Team' },
  { key: 'user_education',            label: 'User Education',            def: false, group: 'Team' },
  { key: 'user_employers',            label: 'User Employers',            def: false, group: 'Team' },
  { key: 'user_role_history',         label: 'User Role History',         def: false, group: 'Team' },
  { key: 'user_salary_history',       label: 'User Salary History',       def: false, group: 'Team' },
  { key: 'user_performance_reviews',  label: 'Performance Reviews',       def: false, group: 'Team' },
  { key: 'user_evaluation_reports',   label: 'Evaluation Reports',        def: false, group: 'Team' },
  { key: 'user_review_criteria',      label: 'Review Criteria',           def: false, group: 'Team' },
  { key: 'user_review_agreements',    label: 'Review Agreements',         def: false, group: 'Team' },
  { key: 'user_notes',                label: 'User Notes',                def: false, group: 'Team' },
  { key: 'teams',                     label: 'Teams',                     def: false, group: 'Team' },
  { key: 'team_projects',             label: 'Team Projects',             def: false, group: 'Team' },
  { key: 'employee_leave_entitlements', label: 'Leave Entitlements',      def: false, group: 'Team' },
  // Clients & Projects
  { key: 'clients',                   label: 'Clients',                   def: false, group: 'Projects' },
  { key: 'client_addresses',          label: 'Client Addresses',          def: false, group: 'Projects' },
  { key: 'client_attributes',         label: 'Client Attributes',         def: false, group: 'Projects' },
  { key: 'client_people',             label: 'Client People',             def: false, group: 'Projects' },
  { key: 'projects',                  label: 'Projects',                  def: false, group: 'Projects' },
  { key: 'project_geocodes',          label: 'Project Geocodes',          def: false, group: 'Projects' },
  { key: 'location_geocodes',         label: 'Location Geocodes',         def: false, group: 'Projects' },
  { key: 'project_account_names',     label: 'Project Account Names',     def: false, group: 'Projects' },
  { key: 'project_stage_status',      label: 'Project Stage Status',      def: false, group: 'Projects' },
  { key: 'project_stage_entries',     label: 'Project Stage Entries',     def: false, group: 'Projects' },
  { key: 'tasks',                     label: 'Tasks',                     def: false, group: 'Projects' },
  { key: 'address_directory',         label: 'Address Directory',         def: false, group: 'Projects' },
  // Time Tracking & Leave
  { key: 'time_entries',              label: 'Time Entries',              def: false, group: 'Time' },
  { key: 'time_off_requests',         label: 'Time Off Requests',         def: false, group: 'Time' },
  { key: 'leave_audit_log',           label: 'Leave Audit Log',           def: false, group: 'Time' },
  { key: 'calendar_events',           label: 'Calendar Events',           def: false, group: 'Time' },
  { key: 'tt_allocations',            label: 'Allocations',               def: false, group: 'Time' },
  // Invoicing & Fees
  { key: 'invoices',                  label: 'Invoices',                  def: false, group: 'Invoicing' },
  { key: 'invoice_files',             label: 'Invoice Files',             def: false, group: 'Invoicing' },
  { key: 'fee_revisions',             label: 'Fee Revisions',             def: false, group: 'Invoicing' },
  { key: 'fee_factor_definitions',    label: 'Fee Factor Definitions',    def: false, group: 'Invoicing' },
  { key: 'fee_inquiry_models',        label: 'Fee Inquiry Models',        def: false, group: 'Invoicing' },
  { key: 'fee_inquiry_model_factors', label: 'Fee Model Factors',         def: false, group: 'Invoicing' },
  { key: 'fee_inquiry_model_comparables', label: 'Fee Model Comparables', def: false, group: 'Invoicing' },
  // Salary & Accounting
  { key: 'salary_monthly_ledger',     label: 'Salary Monthly Ledger',     def: false, group: 'Salary' },
  { key: 'salary_driver_events',      label: 'Salary Driver Events',      def: false, group: 'Salary' },
  { key: 'salary_matrix_entries',     label: 'Salary Matrix Entries',     def: false, group: 'Salary' },
  { key: 'salary_matrix_history',     label: 'Salary Matrix History',     def: false, group: 'Salary' },
  { key: 'salary_matrix_history_nl',  label: 'Salary Matrix History NL',  def: false, group: 'Salary' },
  { key: 'salary_import_batches',     label: 'Salary Import Batches',     def: false, group: 'Salary' },
  { key: 'salary_import_staging',     label: 'Salary Import Staging',     def: false, group: 'Salary' },
  { key: 'expenses',                  label: 'Expenses',                  def: false, group: 'Salary' },
  { key: 'office_overheads_monthly',  label: 'Office Overheads Monthly',  def: false, group: 'Salary' },
  { key: 'office_overheads_yearly',   label: 'Office Overheads Yearly',   def: false, group: 'Salary' },
  { key: 'stage_window_ledger',       label: 'Stage Window Ledger',       def: false, group: 'Salary' },
  // Planning (HFB)
  { key: 'weekly_team_assignments',       label: 'Weekly Team Assignments',       def: false, group: 'Planning' },
  { key: 'weekly_project_allocations',    label: 'Weekly Project Allocations',    def: false, group: 'Planning' },
  { key: 'weekly_project_role_targets',   label: 'Weekly Role Targets',           def: false, group: 'Planning' },
  { key: 'project_weekly_tasks',          label: 'Project Weekly Tasks',          def: false, group: 'Planning' },
  { key: 'project_weekly_subtasks',       label: 'Project Weekly Subtasks',       def: false, group: 'Planning' },
  { key: 'capacity_gap_monthly',          label: 'Capacity Gap Monthly',          def: false, group: 'Planning' },
  { key: 'forecast_runs',                 label: 'Forecast Runs',                 def: false, group: 'Planning' },
  // Hiring
  { key: 'hiring_candidates',         label: 'Hiring Candidates',         def: false, group: 'Hiring' },
  { key: 'hiring_interviews',         label: 'Hiring Interviews',         def: false, group: 'Hiring' },
  { key: 'hiring_recommendations',    label: 'Hiring Recommendations',    def: false, group: 'Hiring' },
  // Inquiries
  { key: 'inquiries',                 label: 'Inquiries',                 def: false, group: 'Inquiries' },
  { key: 'inquiry_followups',         label: 'Inquiry Follow-ups',        def: false, group: 'Inquiries' },
  { key: 'inquiry_intake_messages',   label: 'Inquiry Intake Messages',   def: false, group: 'Inquiries' },
  // Radar & Analytics
  { key: 'radar_item_status',         label: 'Radar Item Status',         def: false, group: 'Analytics' },
  { key: 'recommended_actions',       label: 'Recommended Actions',       def: false, group: 'Analytics' },
  // Integrations
  { key: 'bank_payment_events',       label: 'Bank Payment Events',       def: false, group: 'Integrations' },
  { key: 'email_template_usage_log',  label: 'Email Template Usage Log',  def: false, group: 'Integrations' },
];

const ROLE_LIST = [
  'Director','Partner','Associate','Senior Architect',
  'Project Architect','Medior Architect','Junior Architect','Intern','Support'
];

const CATEGORY_PALETTE_KEYS = [
  'cultural','educational','houses','housing','hospitality',
  'institutional','interior','ngo','public','brio'
];

const TABS = [
  { id: 'general',    label: 'General & Accounting', icon: Settings },
  { id: 'timezone',   label: 'Timezone',             icon: Globe },
  { id: 'currency',   label: 'Currency & FX',        icon: DollarSign },
  { id: 'inflation',  label: 'Inflation & Indices',  icon: TrendingUp },
  { id: 'colors',     label: 'Colours',              icon: Palette },
  { id: 'categories', label: 'Categories',           icon: Tag },
  { id: 'taskcats',   label: 'Task Categories',       icon: Tag },
  { id: 'hiring',     label: 'Hiring',               icon: Users },
  { id: 'invoicepdf', label: 'Invoice PDF',           icon: Archive },
  { id: 'storage',    label: 'Storage',               icon: Link2 },
  { id: 'emailtpls',  label: 'Email Templates',       icon: Mail },
  { id: 'radar',      label: 'Radar Settings',        icon: Eye },
  { id: 'excelexport', label: 'Excel Export',         icon: Download },
  { id: 'scopepdf',    label: 'Scope PDF',             icon: FileText },
  { id: 'sowtemplates', label: 'Base Templates',       icon: FileText },
  { id: 'momtemplates', label: 'MOM Templates',        icon: FileText },
  { id: 'database',   label: 'Database',             icon: Database }
];

// Default 20-color team palette (matches useSettings.js DEFAULT_TEAM_PALETTE)
const DEFAULT_TEAM_PALETTE = [
  '#5B7A99', '#CC9966', '#6B9B7B', '#B36B6B', '#8B7BA3',
  '#9B8B7A', '#7A9B9B', '#9B7A8B', '#8B9B7A', '#9B7A7A',
  '#6B8B8B', '#8B6B7A', '#7A8B6B', '#9B6B8B', '#6B7A9B',
  '#8B9B6B', '#7A6B8B', '#9B8B6B', '#6B9B8B', '#8B7A6B'
];

// MBTI personality type groups for colour editing
const MBTI_GROUPS = [
  { label: 'Analysts (NT)', types: ['INTJ', 'INTP', 'ENTJ', 'ENTP'] },
  { label: 'Diplomats (NF)', types: ['INFJ', 'INFP', 'ENFJ', 'ENFP'] },
  { label: 'Sentinels (SJ)', types: ['ISTJ', 'ISFJ', 'ESTJ', 'ESFJ'] },
  { label: 'Explorers (SP)', types: ['ISTP', 'ISFP', 'ESTP', 'ESFP'] },
];

// ============================================================================
// SHARED STYLES (BRIO design: compact, 11px, Raleway, muted)
// ============================================================================
const font = "'Raleway', system-ui, sans-serif";
const inputStyle = {
  fontFamily: font, fontSize: 11, fontWeight: 300, padding: '6px 10px',
  border: '1px solid rgba(0,0,0,0.15)', borderRadius: 4, outline: 'none',
  width: '100%', boxSizing: 'border-box', transition: 'border 0.15s ease'
};
const thStyle = { fontSize: 10, fontWeight: 400, padding: '8px 10px', textAlign: 'left', color: '#666', borderBottom: '1px solid rgba(0,0,0,0.1)' };
const tdStyle = { fontSize: 11, fontWeight: 300, padding: '7px 10px', color: '#333' };
const btnSmall = {
  fontFamily: font, fontSize: 10, fontWeight: 400, padding: '4px 12px',
  border: '1px solid rgba(0,0,0,0.15)', borderRadius: 3, background: '#fff',
  cursor: 'pointer', color: '#333', display: 'inline-flex', alignItems: 'center',
  transition: 'all 0.15s ease'
};
const btnGold = { ...btnSmall, background: '#9F902A', color: '#fff', border: '1px solid #9F902A' };
const btnIcon = { ...btnSmall, padding: '3px 6px', border: 'none', background: 'transparent', color: '#666' };
const btnDanger = { ...btnSmall, background: '#934948', color: '#fff', border: '1px solid #934948' };

// ============================================================================
// SMALL COMPONENTS
// ============================================================================
function SectionCard({ title, subtitle, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 6, padding: 24, marginBottom: 16 }}>
      {title && <h3 style={{ fontFamily: font, fontSize: 13, fontWeight: 400, margin: 0, color: '#111' }}>{title}</h3>}
      {subtitle && <p style={{ fontFamily: font, fontSize: 10, fontWeight: 300, fontStyle: 'italic', color: '#888', margin: '3px 0 16px' }}>{subtitle}</p>}
      {children}
    </div>
  );
}

function FieldLabel({ label, children }) {
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ fontFamily: font, fontSize: 10, fontWeight: 400, color: '#666', marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}

function ModalOverlay({ onClose, width, children }) {
  return (
    // Inset by the nav bars, like the full-screen surface itself: this overlay
    // lives inside that surface's stacking context, so it cannot paint above
    // the nav and would otherwise slide its top edge under it.
    <div style={{ position: 'fixed', top: 'var(--brio-nav-height, 0px)', left: 0, right: 0, bottom: 'var(--brio-nav-bottom-height, 0px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} onClick={onClose} />
      <div style={{ position: 'relative', background: '#fff', borderRadius: 8, maxWidth: width || 440, width: '100%', maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.15)' }}>
        {children}
      </div>
    </div>
  );
}

function ColorSwatch({ color, size = 20, onClick, selected }) {
  return (
    <button
      onClick={onClick}
      style={{
        width: size, height: size, borderRadius: 3, border: selected ? '2px solid #111' : '1px solid rgba(0,0,0,0.15)',
        background: color || '#ccc', cursor: onClick ? 'pointer' : 'default', padding: 0, flexShrink: 0
      }}
      title={color}
    />
  );
}

function StatusBadge({ ok, label }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 400,
      padding: '2px 8px', borderRadius: 10,
      background: ok ? 'rgba(132,164,140,0.15)' : 'rgba(147,73,72,0.1)',
      color: ok ? '#49685a' : '#934948'
    }}>
      {ok ? <Check style={{ width: 10, height: 10 }} /> : <X style={{ width: 10, height: 10 }} />}
      {label || (ok ? 'Saved' : 'Error')}
    </span>
  );
}

function SaveFeedback({ status }) {
  if (!status) return null;
  const ok = status === 'saved';
  return (
    <span style={{ fontSize: 10, fontWeight: 400, marginLeft: 8, color: ok ? '#49685a' : '#934948' }}>
      {ok ? '\u2713 Saved' : '\u2717 Error'}
    </span>
  );
}

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================
export default function AccountingSettingsPage({ settingsHook, activeOfficeOverheads, radarSettingsHook, scopeTemplatesHook }) {
  const [activeTab, setActiveTab] = useState('general');
  const s = settingsHook;
  const { isFullScreen, toggle: toggleFullScreen, rootStyle, rootClassName } = useFullScreenPage(true);

  if (s.loading) {
    return (
      <div style={{ fontFamily: font, maxWidth: 875, margin: '0 auto', padding: 24, textAlign: 'center' }}>
        <RefreshCw style={{ width: 20, height: 20, margin: '32px auto 12px', animation: 'spin 1s linear infinite', color: '#9F902A' }} />
        <p style={{ fontSize: 11, fontWeight: 300, color: '#666' }}>Loading settings...</p>
      </div>
    );
  }

  return (
    <div className={rootClassName} style={{ ...rootStyle }}>
      {/* This page lays out its own wrapper rather than using BrioFrame, so the
          875px cap has to be lifted here: the .brio-fullscreen-content rule that
          releases a BrioFrame cannot reach an inline max-width. */}
      <div style={{ fontFamily: font, maxWidth: isFullScreen ? 'none' : 875, margin: '0 auto', width: '100%', boxSizing: 'border-box', padding: isFullScreen ? '20px 24px' : 0 }}>
      <div style={{ marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 15, fontWeight: 400, margin: 0, color: '#111' }}>Accounting Settings</h2>
          <p style={{ fontSize: 11, fontWeight: 300, fontStyle: 'italic', color: '#666', margin: '3px 0 0' }}>
            General config, timezone, currency, FX rates, colours, project categories, and database management
          </p>
        </div>
        <BrioFullScreenButton isFullScreen={isFullScreen} onToggle={toggleFullScreen} />
      </div>

      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid rgba(0,0,0,0.1)', marginBottom: 20, flexWrap: 'wrap' }}>
        {TABS.map(tab => {
          const Icon = tab.icon;
          const active = activeTab === tab.id;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
              display: 'flex', alignItems: 'center', gap: 5, padding: '7px 14px',
              border: 'none', background: 'none', cursor: 'pointer', fontFamily: font,
              fontSize: 11, fontWeight: active ? 400 : 300, color: active ? '#111' : '#666',
              borderBottom: active ? '2px solid #111' : '2px solid transparent',
              marginBottom: -1, transition: 'all 0.15s ease'
            }}>
              <Icon style={{ width: 12, height: 12 }} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'general' && <GeneralSection s={s} activeOfficeOverheads={activeOfficeOverheads} />}
      {activeTab === 'timezone' && <TimezoneSection s={s} />}
      {activeTab === 'currency' && <CurrencySection s={s} />}
      {activeTab === 'inflation' && <InflationSection s={s} />}
      {activeTab === 'colors' && <ColorsSection s={s} />}
      {activeTab === 'categories' && <>
        <CategoriesSection s={s} />
        <div style={{ marginTop: 24 }} />
        <SpecialismsSection />
        <div style={{ marginTop: 24 }} />
        <PrioritiesSection s={s} />
      </>}
      {activeTab === 'taskcats' && <TaskCategoriesSection s={s} />}
      {activeTab === 'hiring' && <HiringRolesSection s={s} />}
      {activeTab === 'invoicepdf' && <InvoicePdfSection s={s} />}
      {activeTab === 'storage' && <StorageSection s={s} />}
      {activeTab === 'emailtpls' && <EmailTemplatesTab />}
      {activeTab === 'radar' && <RadarSettingsSection radarSettingsHook={radarSettingsHook} />}
      {activeTab === 'excelexport' && <ExcelExportSection s={s} />}
      {activeTab === 'scopepdf' && <ScopePdfExportSection s={s} />}
      {activeTab === 'sowtemplates' && <SOWTemplatesSection scopeTemplatesHook={scopeTemplatesHook} />}
      {activeTab === 'momtemplates' && <MOMTemplatesSection />}
      {activeTab === 'database' && <DatabaseSection s={s} />}
      </div>
    </div>
  );
}


// ============================================================================
// 0. GENERAL & ACCOUNTING (NEW)
// ============================================================================
function GeneralSection({ s, activeOfficeOverheads }) {
  const [roundingVal, setRoundingVal] = useState(String(s.roundingInterval));
  const [localeVal, setLocaleVal] = useState(s.defaultLocale);
  const [wheelSpeedVal, setWheelSpeedVal] = useState(String(s.wheelPickerSpeed ?? 0.5));
  const [profitVal, setProfitVal] = useState(String(s.essentialProfitPercentage));
  const [dormancyVal, setDormancyVal] = useState(String(s.dormancyPoolPct ?? 3));
  const [markupDraft, setMarkupDraft] = useState(() => {
    const m = s.manualSalaryMarkup || {};
    return Object.entries(m).map(([year, value]) => ({ year, value: String(value) }));
  });

  // ── Salary ledger for calculated markup (same method as MarkupFactorPage) ──
  const { getAllForYear } = useSalaryLedger();
  const [ledgerByYear, setLedgerByYear] = useState({});
  const fetchLedgerForYears = useCallback(async (years) => {
    if (years.length === 0) return;
    const needed = years.filter(y => !(y in ledgerByYear));
    if (needed.length === 0) return;
    const results = await Promise.all(
      needed.map(y => getAllForYear(Number(y)).then(rows => ({ year: y, rows })))
    );
    setLedgerByYear(prev => {
      const next = { ...prev };
      for (const { year, rows } of results) next[year] = rows || [];
      return next;
    });
  }, [getAllForYear, ledgerByYear]);

  useEffect(() => {
    const years = markupDraft.map(r => String(r.year).trim()).filter(Boolean);
    fetchLedgerForYears(years);
  }, [markupDraft, fetchLedgerForYears]); // eslint-disable-line react-hooks/exhaustive-deps

  const calcMarkupForYear = useCallback((year) => {
    const yr = Number(year);
    const ledgerRows = ledgerByYear[String(yr)] || [];
    const totalSalaries = ledgerRows.reduce((sum, row) =>
      sum + (Number(row.base_salary) || 0) + (Number(row.bonus) || 0), 0);
    if (totalSalaries === 0) return null;
    const totalOverheads = (activeOfficeOverheads || [])
      .filter(o => o.year === yr)
      .reduce((sum, o) => sum + (o.amount || 0), 0);
    const essentialProfitPct = s.essentialProfitPercentage || 20;
    const targetRevenue = totalSalaries + totalOverheads;
    const essentialProfit = targetRevenue * (essentialProfitPct / 100);
    return (totalSalaries + totalOverheads + essentialProfit) / totalSalaries;
  }, [ledgerByYear, activeOfficeOverheads, s.essentialProfitPercentage]);
  const [newMarkupYear, setNewMarkupYear] = useState('');
  const [newMarkupValue, setNewMarkupValue] = useState('');
  const [invoiceRules, setInvoiceRules] = useState(() => s.invoiceDateRules || { ruleType: 'manual', dayOfMonth: 1 });
  const [paidMode, setPaidMode] = useState(s.paidConfirmationMode || 'manual');
  const [syncConfig, setSyncConfig] = useState(() => s.calendarAutoSync || { enabled: false, frequency: 'daily', syncRangeDays: 30 });
  const [feedback, setFeedback] = useState({});

  const showFeedback = (key, ok) => {
    setFeedback(prev => ({ ...prev, [key]: ok ? 'saved' : 'error' }));
    setTimeout(() => setFeedback(prev => { const n = { ...prev }; delete n[key]; return n; }), 2000);
  };

  const saveField = async (key, value, fbKey) => {
    const res = await s.updateSetting(key, value);
    showFeedback(fbKey || key, res.success);
  };

  const saveMarkup = async () => {
    const obj = {};
    markupDraft.forEach(row => {
      const y = String(row.year).trim();
      const v = parseFloat(row.value);
      if (y && Number.isFinite(v)) obj[y] = v;
    });
    if (process.env.NODE_ENV !== 'production') {
      console.log('[AccountingSettings] Saving manualSalaryMarkup to app_settings:', obj);
    }
    const res = await s.updateSetting('manualSalaryMarkup', obj);
    if (process.env.NODE_ENV !== 'production') {
      console.log('[AccountingSettings] Save result:', res.success ? '✅ OK' : `❌ ${res.error}`);
    }
    showFeedback('markup', res.success);
  };

  const addMarkupRow = () => {
    if (!newMarkupYear.trim() || !newMarkupValue.trim()) return;
    setMarkupDraft(prev => [...prev.filter(r => r.year !== newMarkupYear.trim()), { year: newMarkupYear.trim(), value: newMarkupValue.trim() }]);
    setNewMarkupYear('');
    setNewMarkupValue('');
  };

  const removeMarkupRow = (year) => setMarkupDraft(prev => prev.filter(r => r.year !== year));

  return (
    <div>
      {/* General */}
      <SectionCard title="General" subtitle="Time rounding and locale defaults">
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
          <FieldLabel label="Time rounding interval (minutes)">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="number" min="1" max="60" value={roundingVal} onChange={e => setRoundingVal(e.target.value)} style={{ ...inputStyle, width: 80 }} />
              <button onClick={() => { const v = parseInt(roundingVal, 10); if (v > 0) saveField('roundingInterval', v, 'rounding'); }} style={btnSmall}>
                <Save style={{ width: 10, height: 10, marginRight: 4 }} />Save
              </button>
              <SaveFeedback status={feedback.rounding} />
            </div>
          </FieldLabel>
          <FieldLabel label="Default locale">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <select value={localeVal} onChange={e => setLocaleVal(e.target.value)} style={{ ...inputStyle, width: 160 }}>
                <option value="nl-NL">nl-NL (Netherlands)</option>
                <option value="en-IN">en-IN (India)</option>
                <option value="en-US">en-US (US)</option>
                <option value="en-GB">en-GB (UK)</option>
                <option value="de-DE">de-DE (Germany)</option>
              </select>
              <button onClick={() => saveField('defaultLocale', localeVal, 'locale')} style={btnSmall}>
                <Save style={{ width: 10, height: 10, marginRight: 4 }} />Save
              </button>
              <SaveFeedback status={feedback.locale} />
            </div>
          </FieldLabel>
          <FieldLabel label="Wheel picker scroll speed">
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input type="range" min="0.25" max="1" step="0.05" value={wheelSpeedVal} onChange={e => setWheelSpeedVal(e.target.value)} style={{ width: 120 }} />
              <span style={{ fontSize: 11, color: '#666', minWidth: 30 }}>{Number(wheelSpeedVal).toFixed(2)}</span>
              <button onClick={() => { const v = parseFloat(wheelSpeedVal); if (v > 0 && v <= 1) saveField('wheelPickerSpeed', v, 'wheelSpeed'); }} style={btnSmall}>
                <Save style={{ width: 10, height: 10, marginRight: 4 }} />Save
              </button>
              <SaveFeedback status={feedback.wheelSpeed} />
            </div>
          </FieldLabel>
        </div>
      </SectionCard>

      {/* Financial Logic */}
      <SectionCard title="Financial Logic" subtitle="Profit targets and salary markup configuration">
        <FieldLabel label="Essential profit % target">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
            <input type="number" min="0" max="100" step="0.5" value={profitVal} onChange={e => setProfitVal(e.target.value)} style={{ ...inputStyle, width: 80 }} />
            <span style={{ fontSize: 11, color: '#666' }}>%</span>
            <button onClick={() => { const v = parseFloat(profitVal); if (Number.isFinite(v)) saveField('essentialProfitPercentage', v, 'profit'); }} style={btnSmall}>
              <Save style={{ width: 10, height: 10, marginRight: 4 }} />Save
            </button>
            <SaveFeedback status={feedback.profit} />
          </div>
        </FieldLabel>

        <FieldLabel label="Dormancy carrying cost % of overhead pool">
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
            <input type="number" min="0" max="50" step="0.5" value={dormancyVal} onChange={e => setDormancyVal(e.target.value)} style={{ ...inputStyle, width: 80 }} />
            <span style={{ fontSize: 11, color: '#666' }}>%</span>
            <button onClick={() => { const v = parseFloat(dormancyVal); if (Number.isFinite(v) && v >= 0 && v <= 50) saveField('dormancyPoolPct', v, 'dormancy'); }} style={btnSmall}>
              <Save style={{ width: 10, height: 10, marginRight: 4 }} />Save
            </button>
            <SaveFeedback status={feedback.dormancy} />
            <span style={{ fontSize: 10, color: '#888', fontStyle: 'italic' }}>Portion of monthly office overhead reserved for dormant open projects</span>
          </div>
        </FieldLabel>

        <div style={{ marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 400 }}>Manual Salary Markup by Year</span>
          <p style={{ fontSize: 10, color: '#888', margin: '2px 0 8px', fontWeight: 300 }}>Year-to-markup multiplier map. Used in salary cost calculations.</p>
        </div>
        <div style={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 4, overflow: 'hidden', marginBottom: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead><tr style={{ background: '#f8f8f8' }}>
              <th style={thStyle}>Year</th><th style={thStyle}>Markup (manual)</th><th style={{ ...thStyle, color: '#999', fontStyle: 'italic' }}>Calculated</th><th style={{ ...thStyle, width: 50, textAlign: 'center' }}></th>
            </tr></thead>
            <tbody>
              {markupDraft.sort((a, b) => Number(b.year) - Number(a.year)).map(row => {
                const calcVal = calcMarkupForYear(row.year);
                return (
                  <tr key={row.year} style={{ borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                    <td style={tdStyle}>{row.year}</td>
                    <td style={tdStyle}>
                      <input type="number" step="0.01" value={row.value}
                        onChange={e => setMarkupDraft(prev => prev.map(r => r.year === row.year ? { ...r, value: e.target.value } : r))}
                        style={{ ...inputStyle, width: 80 }} />
                    </td>
                    <td style={{ ...tdStyle, color: '#999', fontStyle: 'italic', fontSize: 10 }}>
                      {calcVal != null && Number.isFinite(calcVal) ? `${calcVal.toFixed(3)}×` : '—'}
                    </td>
                    <td style={{ ...tdStyle, textAlign: 'center' }}>
                      <button onClick={() => removeMarkupRow(row.year)} style={{ ...btnIcon, color: '#934948' }}>
                        <Trash2 style={{ width: 11, height: 11 }} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {markupDraft.length === 0 && (
                <tr><td colSpan={4} style={{ ...tdStyle, textAlign: 'center', color: '#999', fontStyle: 'italic' }}>No markup rows yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12 }}>
          <input type="number" placeholder="Year" value={newMarkupYear} onChange={e => setNewMarkupYear(e.target.value)} style={{ ...inputStyle, width: 80 }} />
          <input type="number" step="0.01" placeholder="Markup" value={newMarkupValue} onChange={e => setNewMarkupValue(e.target.value)} style={{ ...inputStyle, width: 80 }} />
          <button onClick={addMarkupRow} style={btnSmall}><Plus style={{ width: 10, height: 10, marginRight: 4 }} />Add</button>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={saveMarkup} style={btnGold}><Save style={{ width: 10, height: 10, marginRight: 4 }} />Save Markup Table</button>
          <SaveFeedback status={feedback.markup} />
        </div>
      </SectionCard>

      {/* Invoicing & Cashflow */}
      <SectionCard title="Invoicing & Cashflow" subtitle="Invoice date rules and payment confirmation logic">
        <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', marginBottom: 16 }}>
          <FieldLabel label="Invoice rule type">
            <select value={invoiceRules.ruleType || 'manual'} onChange={e => setInvoiceRules(prev => ({ ...prev, ruleType: e.target.value }))} style={{ ...inputStyle, width: 160 }}>
              <option value="manual">Manual</option>
              <option value="stage_start">Stage start date</option>
              <option value="monthly">Monthly (fixed day)</option>
            </select>
          </FieldLabel>
          {invoiceRules.ruleType === 'monthly' && (
            <FieldLabel label="Day of month (1\u201328)">
              <input type="number" min="1" max="28" value={invoiceRules.dayOfMonth || 1}
                onChange={e => setInvoiceRules(prev => ({ ...prev, dayOfMonth: parseInt(e.target.value, 10) || 1 }))}
                style={{ ...inputStyle, width: 70 }} />
            </FieldLabel>
          )}
          <FieldLabel label="Paid confirmation mode">
            <select value={paidMode} onChange={e => setPaidMode(e.target.value)} style={{ ...inputStyle, width: 160 }}>
              <option value="manual">Manual</option>
              <option value="bank_match">Bank match (future)</option>
            </select>
          </FieldLabel>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={async () => {
            const r1 = await s.updateSetting('invoiceDateRules', invoiceRules);
            const r2 = await s.updateSetting('paidConfirmationMode', paidMode);
            showFeedback('invoice', r1.success && r2.success);
          }} style={btnGold}><Save style={{ width: 10, height: 10, marginRight: 4 }} />Save Invoice Config</button>
          <SaveFeedback status={feedback.invoice} />
        </div>
      </SectionCard>

      {/* Calendar & Integrations */}
      <SectionCard title="Calendar & Integrations" subtitle="Microsoft Calendar sync status and auto-sync rules">
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 400, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Link2 style={{ width: 12, height: 12 }} /> Microsoft Calendar
          </div>
          <div style={{ fontSize: 10, fontWeight: 300, color: '#666', padding: '8px 12px', background: '#f8f8f8', borderRadius: 4 }}>
            Connection status is managed per-user via the Calendar page. OAuth tokens are never stored in app_settings.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', marginBottom: 16 }}>
          <FieldLabel label="Auto-sync enabled">
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, cursor: 'pointer' }}>
              <input type="checkbox" checked={syncConfig.enabled} onChange={e => setSyncConfig(prev => ({ ...prev, enabled: e.target.checked }))} />
              {syncConfig.enabled ? 'Enabled' : 'Disabled'}
            </label>
          </FieldLabel>
          <FieldLabel label="Frequency">
            <select value={syncConfig.frequency || 'daily'} onChange={e => setSyncConfig(prev => ({ ...prev, frequency: e.target.value }))} style={{ ...inputStyle, width: 120 }}>
              <option value="hourly">Hourly</option>
              <option value="daily">Daily</option>
            </select>
          </FieldLabel>
          <FieldLabel label="Sync range (days)">
            <input type="number" min="7" max="365" value={syncConfig.syncRangeDays || 30}
              onChange={e => setSyncConfig(prev => ({ ...prev, syncRangeDays: parseInt(e.target.value, 10) || 30 }))}
              style={{ ...inputStyle, width: 70 }} />
          </FieldLabel>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={async () => {
            const res = await s.updateSetting('calendarAutoSync', syncConfig);
            showFeedback('sync', res.success);
          }} style={btnGold}><Save style={{ width: 10, height: 10, marginRight: 4 }} />Save Sync Config</button>
          <SaveFeedback status={feedback.sync} />
        </div>
      </SectionCard>

    </div>
  );
}


// ============================================================================
// EMAIL TEMPLATES TAB (admin-editable, stored in email_templates table)
// ============================================================================
// Features:
//   - Template list table showing all custom templates per category
//   - Create / Edit / Duplicate / Delete actions
//   - Set Default: marks one template per type as the active default
//   - Placeholder insertion helper panel
//   - Preview with sample data
//   - Fallback to built-in templates when DB is empty
// ============================================================================

const TEMPLATE_TYPES = [
  { value: 'stage_initiation',        label: 'Stage Initiation' },
  { value: 'timeline_change',         label: 'Timeline Change' },
  { value: 'stage_closure',           label: 'Stage Closure' },
  { value: 'tax_invoice',             label: 'Tax Invoice' },
  { value: 'late_payment_reminder',   label: 'Late Payment Reminder' },
  { value: 'monthly_accountant_report', label: 'Monthly Accountant Report' },
  { value: 'hiring',                  label: 'Hiring' },
  { value: 'client_birthday',         label: 'Client Birthday' },
  { value: 'client_anniversary',      label: 'Client Anniversary' },
];

const SAMPLE_VARS = {
  addressee: 'Mr. van den Berg',
  clientName: 'Example Corp.',
  projectName: 'Villa Horizon',
  projectCode: 'ARC-2026-042',
  stageName: 'Concept Design \u2013 Architecture',
  nextStage: 'Schematic Design \u2013 Architecture',
  previousStage: 'Advance',
  invoiceNumber: 'INV-2026-042-01',
  invoiceDate: '08/03/2026',
  dueDate: '22/03/2026',
  paymentTermsDays: '14',
  amount: '\u20ac 12,500.00',
  meetingDate: '15/03/2026',
  teamList: '  \u2022 Shefali Balwani \u2014 Director\n  \u2022 Rik Buijs \u2014 Associate',
  timeline: 'Project Timeline:\n\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\n  Advance\n    Initiated: 01/01/2026\n    Approved: 15/01/2026\n  Concept Design\n    Initiated: 01/02/2026\n\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500',
  date: '08/03/2026',
  candidateName: 'Priya Sharma',
  jobRole: 'Junior Architect',
  officeLocation: 'Mumbai',
};

// ── Create / Edit Template Modal ──────────────────────────────────────────
function EmailTemplateModal({ initial, onSave, onClose, saving }) {
  const placeholders = getAvailablePlaceholders();
  const [form, setForm] = useState({
    templateName: initial?.templateName || '',
    templateType: initial?.templateType || 'stage_initiation',
    stageKey:     initial?.stageKey || '',
    subject:      initial?.subject || '',
    body:         initial?.body || '',
    isDefault:    initial?.isDefault || false,
  });
  const [showPreview, setShowPreview] = useState(false);
  const bodyRef = useRef(null);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const insertPlaceholder = (p) => {
    const tag = `{${p}}`;
    const ta = bodyRef.current;
    if (ta) {
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const newBody = form.body.slice(0, start) + tag + form.body.slice(end);
      set('body', newBody);
      setTimeout(() => { ta.focus(); ta.setSelectionRange(start + tag.length, start + tag.length); }, 0);
    } else {
      set('body', form.body + tag);
    }
  };

  const previewSubject = form.subject.replace(/\{(\w+)\}/g, (_, k) => SAMPLE_VARS[k] || `{${k}}`);
  const previewBody = form.body.replace(/\{(\w+)\}/g, (_, k) => SAMPLE_VARS[k] || `{${k}}`);

  // Placeholder validation
  const validation = useMemo(() => {
    if (!form.subject && !form.body) return { errors: [], warnings: [] };
    return validateTemplatePlaceholders(form.templateType, form.subject, form.body);
  }, [form.templateType, form.subject, form.body]);

  const [warningOverride, setWarningOverride] = useState(false);
  const canSave = form.templateName.trim() && form.subject.trim() && form.body.trim() && validation.errors.length === 0;

  const handleSave = () => {
    if (validation.warnings.length > 0 && !warningOverride) {
      setWarningOverride(true); // Show warnings, require second click
      return;
    }
    onSave(form);
  };

  return (
    <ModalOverlay onClose={onClose} width={620}>
      <div style={{ padding: 24 }}>
        <h3 style={{ fontFamily: font, fontSize: 14, fontWeight: 400, margin: '0 0 16px' }}>
          {initial?.id ? 'Edit Email Template' : 'Create Email Template'}
        </h3>

        <FieldLabel label="Template Name">
          <input
            type="text" value={form.templateName}
            onChange={e => set('templateName', e.target.value)}
            placeholder="e.g. Stage Approval v2"
            style={{ ...inputStyle, width: '100%' }}
          />
        </FieldLabel>

        <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
          <div style={{ flex: 1 }}>
            <FieldLabel label="Template Type">
              <select value={form.templateType} onChange={e => set('templateType', e.target.value)}
                style={{ ...inputStyle, width: '100%' }}>
                {TEMPLATE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </FieldLabel>
          </div>
          <div style={{ flex: 1 }}>
            <FieldLabel label="Stage (optional)">
              <input type="text" value={form.stageKey} onChange={e => set('stageKey', e.target.value)}
                placeholder="Leave empty for generic" style={{ ...inputStyle, width: '100%' }} />
            </FieldLabel>
          </div>
        </div>

        <div style={{ marginTop: 8 }}>
          <FieldLabel label="Subject">
            <input type="text" value={form.subject} onChange={e => set('subject', e.target.value)}
              placeholder="e.g. {projectName} — {stageName} approved"
              style={{ ...inputStyle, width: '100%' }} />
          </FieldLabel>
        </div>

        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
            <div style={{ fontFamily: font, fontSize: 10, fontWeight: 400, color: '#666' }}>Body</div>
            <button onClick={() => setShowPreview(!showPreview)} style={btnSmall}>
              {showPreview ? 'Edit' : 'Preview'}
            </button>
          </div>
          {showPreview ? (
            <div style={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 4, padding: 14, background: '#fafafa', minHeight: 160 }}>
              <div style={{ fontSize: 10, fontWeight: 300, color: '#999', marginBottom: 4 }}>Subject:</div>
              <div style={{ fontSize: 11, fontWeight: 400, marginBottom: 10 }}>{previewSubject}</div>
              <div style={{ fontSize: 10, fontWeight: 300, color: '#999', marginBottom: 4 }}>Body:</div>
              <pre style={{ fontSize: 11, fontWeight: 300, fontFamily: font, lineHeight: 1.5, whiteSpace: 'pre-wrap', margin: 0, color: '#333' }}>
                {previewBody}
              </pre>
              <div style={{ marginTop: 8, fontSize: 9, fontWeight: 300, color: '#aaa', fontStyle: 'italic' }}>
                Preview rendered with sample data.
              </div>
            </div>
          ) : (
            <textarea ref={bodyRef} value={form.body} onChange={e => set('body', e.target.value)}
              placeholder={'Dear {addressee},\n\n...\n\nKind regards,\nArchitecture BRIO'}
              style={{ ...inputStyle, width: '100%', minHeight: 160, resize: 'vertical' }} rows={8} />
          )}
        </div>

        {/* Placeholder insertion helper */}
        <div style={{ marginTop: 10, padding: 8, background: '#f8f8f6', borderRadius: 4, border: '1px solid rgba(0,0,0,0.06)' }}>
          <div style={{ fontSize: 9, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#999', marginBottom: 6 }}>
            Insert placeholder
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {placeholders.map(p => (
              <button key={p} onClick={() => insertPlaceholder(p)}
                style={{
                  background: '#fff', border: '1px solid #ddd', borderRadius: 3,
                  padding: '2px 7px', fontSize: 9, fontFamily: 'monospace',
                  color: '#555', cursor: 'pointer',
                }}
                title={`Insert {${p}} at cursor`}
              >{p}</button>
            ))}
          </div>
        </div>

        <div style={{ marginTop: 12 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.isDefault} onChange={e => set('isDefault', e.target.checked)} />
            <span style={{ fontFamily: font, fontSize: 10, fontWeight: 400, color: '#555' }}>
              Set as default template for this type
            </span>
          </label>
        </div>

        {/* Validation errors */}
        {validation.errors.length > 0 && (
          <div style={{ marginTop: 10, padding: '6px 10px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4 }}>
            {validation.errors.map((e, i) => (
              <div key={i} style={{ fontSize: 10, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 4 }}>
                <AlertTriangle style={{ width: 10, height: 10, flexShrink: 0 }} /> {e}
              </div>
            ))}
          </div>
        )}

        {/* Validation warnings (overridable) */}
        {validation.warnings.length > 0 && warningOverride && (
          <div style={{ marginTop: 10, padding: '6px 10px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 4 }}>
            {validation.warnings.map((w, i) => (
              <div key={i} style={{ fontSize: 10, color: '#92400e', display: 'flex', alignItems: 'center', gap: 4 }}>
                <AlertTriangle style={{ width: 10, height: 10, flexShrink: 0 }} /> {w}
              </div>
            ))}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16, borderTop: '1px solid rgba(0,0,0,0.06)', paddingTop: 12 }}>
          <button onClick={onClose} style={btnSmall}>Cancel</button>
          <button onClick={handleSave} disabled={!canSave || saving}
            style={{ ...btnGold, opacity: canSave ? 1 : 0.5 }}>
            <Save style={{ width: 10, height: 10, marginRight: 4 }} />
            {saving ? 'Saving...' : warningOverride ? 'Save Anyway' : (initial?.id ? 'Update Template' : 'Create Template')}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

function EmailTemplatesTab() {
  const {
    templates: dbTemplates, loading: tplLoading,
    create, update, remove, setDefault,
  } = useSupabaseEmailTemplates();

  const [modal, setModal] = useState(null); // null | 'create' | { ...editData }
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [previewId, setPreviewId] = useState(null);
  const [filterType, setFilterType] = useState('');

  // Merge DB templates with built-in defaults for display
  const allRows = useMemo(() => {
    const rows = [...dbTemplates];
    // Show built-in templates that don't have a DB row
    const builtinKeys = [
      'project_initiated', 'stage_initiated', 'timeline_change',
      'stage_approved', 'stage_closure', 'invoice_sent',
      'reminder_friendly', 'reminder_due_today', 'reminder_overdue', 'reminder_final',
      'monthly_accountant_report',
      'hiring_offer', 'hiring_rejection', 'hiring_future_potential', 'hiring_contact',
      'client_birthday', 'client_anniversary',
    ];
    builtinKeys.forEach(key => {
      const hasDb = dbTemplates.some(t => t.templateType === key);
      if (!hasDb) {
        const fallback = getDefaultTemplate(key);
        const def = getTemplateDefinition(key);
        if (fallback) {
          rows.push({
            id: null,
            templateName: null,
            templateType: key,
            _category: def?.category || key,
            stageKey: null,
            subject: fallback.subject,
            body: fallback.body,
            isDefault: false,
            createdBy: null,
            createdAt: null,
            updatedAt: null,
            _builtin: true,
          });
        }
      }
    });
    return rows;
  }, [dbTemplates]);

  const filteredRows = filterType
    ? allRows.filter(r => r.templateType === filterType || r._category === filterType)
    : allRows;

  const typeLabel = (type, row) => {
    const match = TEMPLATE_TYPES.find(t => t.value === type);
    if (match) return match.label;
    if (row?._category) {
      const catMatch = TEMPLATE_TYPES.find(t => t.value === row._category);
      if (catMatch) return catMatch.label;
    }
    return type;
  };

  const showFeedback = (msg) => { setFeedback(msg); setTimeout(() => setFeedback(null), 2500); };

  const handleCreate = async (form) => {
    setSaving(true);
    const result = await create({
      templateName: form.templateName.trim(),
      templateType: form.templateType,
      stageKey: form.stageKey.trim() || null,
      subject: form.subject.trim(),
      body: form.body.trim(),
      isDefault: form.isDefault,
    });
    setSaving(false);
    if (result.success) {
      setModal(null);
      showFeedback('created');
    } else {
      showFeedback('error');
    }
  };

  const handleEdit = async (form) => {
    if (!modal?.id) return;
    setSaving(true);
    const result = await update(modal.id, {
      templateName: form.templateName.trim(),
      templateType: form.templateType,
      stageKey: form.stageKey.trim() || null,
      subject: form.subject.trim(),
      body: form.body.trim(),
      isDefault: form.isDefault,
    });
    setSaving(false);
    if (result.success) {
      setModal(null);
      showFeedback('updated');
    } else {
      showFeedback('error');
    }
  };

  const handleEditBuiltin = (row) => {
    setModal({
      _duplicate: true, // routes save to handleCreate (no DB id yet)
      templateName: row.templateName || row.templateType,
      templateType: row._category || row.templateType,
      stageKey: row.stageKey || '',
      subject: row.subject,
      body: row.body,
      isDefault: true,
    });
  };

  const handleDuplicate = (row) => {
    setModal({
      _duplicate: true,
      templateName: (row.templateName || row.templateType) + ' (copy)',
      templateType: row._category || row.templateType,
      stageKey: row.stageKey || '',
      subject: row.subject,
      body: row.body,
      isDefault: false,
    });
  };

  const handleSetDefaultBuiltin = async (row) => {
    setSaving(true);
    const result = await create({
      templateName: row.templateName || row.templateType,
      templateType: row._category || row.templateType,
      stageKey: row.stageKey || null,
      subject: row.subject,
      body: row.body,
      isDefault: true,
    });
    setSaving(false);
    showFeedback(result.success ? 'default_set' : 'error');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template? This cannot be undone.')) return;
    const result = await remove(id);
    showFeedback(result.success ? 'deleted' : 'error');
  };

  const handleSetDefault = async (id) => {
    const result = await setDefault(id);
    showFeedback(result.success ? 'default_set' : 'error');
  };

  if (tplLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 32 }}>
        <RefreshCw style={{ width: 16, height: 16, animation: 'spin 1s linear infinite', color: '#9F902A' }} />
        <p style={{ fontSize: 11, fontWeight: 300, color: '#666', marginTop: 8 }}>Loading email templates...</p>
      </div>
    );
  }

  return (
    <div>
      <SectionCard title="Email Templates" subtitle="Create and manage email templates. Set one template per type as the default. Built-in templates are used as fallback when no custom template exists.">

        {/* Header row: filter + create button */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select value={filterType} onChange={e => setFilterType(e.target.value)}
              style={{ ...inputStyle, width: 200 }}>
              <option value="">All types</option>
              {TEMPLATE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
            <span style={{ fontSize: 9, fontWeight: 300, color: '#aaa' }}>
              {filteredRows.length} template{filteredRows.length !== 1 ? 's' : ''}
            </span>
          </div>
          <button onClick={() => setModal('create')} style={btnGold}>
            <Plus style={{ width: 10, height: 10, marginRight: 4 }} /> Create Template
          </button>
        </div>

        {/* Template list table */}
        <div style={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 4, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: font }}>
            <thead>
              <tr style={{ background: '#f8f8f6' }}>
                <th style={thStyle}>Template Name</th>
                <th style={thStyle}>Type</th>
                <th style={thStyle}>Stage</th>
                <th style={{ ...thStyle, textAlign: 'center', width: 60 }}>Default</th>
                <th style={thStyle}>Last Updated</th>
                <th style={{ ...thStyle, textAlign: 'right', width: 130 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.length === 0 && (
                <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#aaa', fontStyle: 'italic', padding: 24 }}>
                  No templates found. Click "Create Template" to add one.
                </td></tr>
              )}
              {filteredRows.map((row) => {
                const isBuiltin = row._builtin;
                const key = row.id || `builtin-${row.templateType}`;
                const name = row.templateName || row.templateType;
                const updated = row.updatedAt ? new Date(row.updatedAt).toLocaleDateString('en-GB') : '\u2014';
                const isPreviewing = previewId === key;

                return (
                  <React.Fragment key={key}>
                    <tr style={{ borderBottom: '1px solid rgba(0,0,0,0.04)', background: isBuiltin ? '#fefefe' : '#fff' }}>
                      <td style={tdStyle}>
                        <span style={{ fontWeight: 400 }}>{name}</span>
                        {isBuiltin && (
                          <span style={{ fontSize: 8, color: '#aaa', marginLeft: 6, fontWeight: 300, fontStyle: 'italic' }}>built-in</span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        <span style={{ fontSize: 9, padding: '1px 6px', background: '#f0f0f0', borderRadius: 3 }}>
                          {typeLabel(row.templateType, row)}
                        </span>
                      </td>
                      <td style={tdStyle}>
                        <span style={{ color: row.stageKey ? '#333' : '#ccc', fontSize: 10 }}>
                          {row.stageKey || '\u2014'}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'center' }}>
                        {row.isDefault ? (
                          <Star style={{ width: 12, height: 12, color: '#9F902A', fill: '#9F902A' }} />
                        ) : (
                          <span style={{ color: '#ddd' }}>\u2014</span>
                        )}
                      </td>
                      <td style={{ ...tdStyle, fontSize: 10, color: '#888' }}>{updated}</td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        {isBuiltin ? (
                          <div style={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <button onClick={() => setPreviewId(isPreviewing ? null : key)} style={btnIcon} title="Preview">
                              <Eye style={{ width: 12, height: 12 }} />
                            </button>
                            <button onClick={() => handleEditBuiltin(row)} style={btnIcon} title="Edit (saves as custom)">
                              <Edit2 style={{ width: 12, height: 12 }} />
                            </button>
                            <button onClick={() => handleDuplicate(row)} style={btnIcon} title="Duplicate as custom">
                              <Copy style={{ width: 12, height: 12 }} />
                            </button>
                            {!row.isDefault && (
                              <button onClick={() => handleSetDefaultBuiltin(row)} disabled={saving} style={btnIcon} title="Set as default (saves as custom)">
                                <Star style={{ width: 12, height: 12 }} />
                              </button>
                            )}
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                            <button onClick={() => setPreviewId(isPreviewing ? null : key)} style={btnIcon} title="Preview">
                              <Eye style={{ width: 12, height: 12 }} />
                            </button>
                            <button onClick={() => setModal(row)} style={btnIcon} title="Edit">
                              <Edit2 style={{ width: 12, height: 12 }} />
                            </button>
                            <button onClick={() => handleDuplicate(row)} style={btnIcon} title="Duplicate">
                              <Copy style={{ width: 12, height: 12 }} />
                            </button>
                            {!row.isDefault && row.id && (
                              <button onClick={() => handleSetDefault(row.id)} style={btnIcon} title="Set as default">
                                <Star style={{ width: 12, height: 12 }} />
                              </button>
                            )}
                            <button onClick={() => handleDelete(row.id)} style={{ ...btnIcon, color: '#934948' }} title="Delete">
                              <Trash2 style={{ width: 12, height: 12 }} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                    {isPreviewing && (
                      <tr>
                        <td colSpan={6} style={{ padding: '10px 16px', background: '#fafafa', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                          <div style={{ fontSize: 10, fontWeight: 300, color: '#999', marginBottom: 4 }}>Subject:</div>
                          <div style={{ fontSize: 11, fontWeight: 400, marginBottom: 8 }}>
                            {row.subject.replace(/\{(\w+)\}/g, (_, k) => SAMPLE_VARS[k] || `{${k}}`)}
                          </div>
                          <div style={{ fontSize: 10, fontWeight: 300, color: '#999', marginBottom: 4 }}>Body:</div>
                          <pre style={{ fontSize: 11, fontWeight: 300, fontFamily: font, lineHeight: 1.5, whiteSpace: 'pre-wrap', margin: 0, color: '#333' }}>
                            {row.body.replace(/\{(\w+)\}/g, (_, k) => SAMPLE_VARS[k] || `{${k}}`)}
                          </pre>
                          <div style={{ marginTop: 6, fontSize: 9, fontWeight: 300, color: '#aaa', fontStyle: 'italic' }}>
                            Preview with sample data.
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Feedback */}
        {feedback && (
          <div style={{ marginTop: 10, fontSize: 10, color: feedback === 'error' ? '#dc2626' : '#16a34a' }}>
            {feedback === 'created' && '\u2713 Template created'}
            {feedback === 'updated' && '\u2713 Template updated'}
            {feedback === 'deleted' && '\u2713 Template deleted'}
            {feedback === 'default_set' && '\u2713 Default template updated'}
            {feedback === 'error' && '\u2717 An error occurred'}
          </div>
        )}
      </SectionCard>

      {/* Create / Edit / Duplicate modal */}
      {modal && (
        <EmailTemplateModal
          initial={modal === 'create' ? null : modal}
          onSave={modal === 'create' || modal?._duplicate ? handleCreate : handleEdit}
          onClose={() => setModal(null)}
          saving={saving}
        />
      )}
    </div>
  );
}


// ============================================================================
// 1. TIMEZONE
// ============================================================================
function TimezoneSection({ s }) {
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);

  const filtered = useMemo(() => {
    if (!search) return COMMON_TIMEZONES;
    const q = search.toLowerCase();
    return COMMON_TIMEZONES.filter(tz => tz.toLowerCase().includes(q));
  }, [search]);

  const save = async (tz) => { setSaving(true); await s.updateSetting('timezone', tz); setSaving(false); };

  return (
    <SectionCard title="Timezone Configuration" subtitle="Default timezone for date formatting and calculations">
      <FieldLabel label="Current timezone">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <code style={{ fontSize: 12, fontWeight: 400, color: '#111' }}>{s.timezone}</code>
          <button onClick={() => save('Europe/Amsterdam')} disabled={s.timezone === 'Europe/Amsterdam' || saving}
            style={{ ...btnSmall, opacity: s.timezone === 'Europe/Amsterdam' ? 0.4 : 1 }}>
            Reset to default
          </button>
        </div>
      </FieldLabel>
      <div style={{ marginTop: 12 }}>
        <input type="text" value={search} onChange={e => setSearch(e.target.value)}
          placeholder="Search timezone (e.g. Amsterdam, Kolkata)" style={inputStyle} />
      </div>
      <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 4, marginTop: 8, background: '#fafafa' }}>
        {filtered.map(tz => (
          <button key={tz} onClick={() => save(tz)} disabled={saving} style={{
            display: 'block', width: '100%', textAlign: 'left', padding: '5px 12px',
            border: 'none', cursor: 'pointer', fontFamily: font, fontSize: 11,
            fontWeight: tz === s.timezone ? 400 : 300,
            background: tz === s.timezone ? 'rgba(159,144,42,0.08)' : 'transparent',
            color: tz === s.timezone ? '#9F902A' : '#333',
            borderBottom: '1px solid rgba(0,0,0,0.03)'
          }}>
            {tz === s.timezone && <Check style={{ width: 10, height: 10, marginRight: 5, verticalAlign: -1 }} />}
            {tz}
          </button>
        ))}
      </div>
    </SectionCard>
  );
}


// ============================================================================
// 2. CURRENCY & FX RATES (enhanced with auto-refresh)
// ============================================================================
function CurrencySection({ s }) {
  const [modal, setModal] = useState(null);
  const [saving, setSaving] = useState(false);
  const [fxForm, setFxForm] = useState({ month: '', avg_rate: '', notes: '' });
  const [defaultRate, setDefaultRate] = useState(String(s.fxDefaultRate));
  const [refreshStatus, setRefreshStatus] = useState(null);
  const [refreshError, setRefreshError] = useState(null);

  const toggleCurrency = async (c) => { setSaving(true); await s.updateSetting('displayCurrency', c); setSaving(false); };

  const saveDefaultRate = async () => {
    const v = parseFloat(defaultRate);
    if (!Number.isFinite(v) || v <= 0) return;
    setSaving(true);
    await s.updateSetting('fxDefaultEurInr', Math.round(v * 100) / 100);
    setSaving(false);
  };

  const openAdd = () => { setFxForm({ month: '', avg_rate: '', notes: '' }); setModal('add'); };
  const openEdit = (r) => {
    setFxForm({ month: (r.month || '').substring(0, 7), avg_rate: String(Math.round(parseFloat(r.avg_rate) * 100) / 100), notes: r.notes || '' });
    setModal(r);
  };

  const saveFx = async () => {
    const monthVal = fxForm.month ? `${fxForm.month}-01` : null;
    const rateVal = parseFloat(fxForm.avg_rate);
    if (!monthVal || !Number.isFinite(rateVal) || rateVal <= 0) return;
    setSaving(true);
    if (modal !== 'add') {
      await s.updateFxRate(modal.id, { month: monthVal, avg_rate: rateVal, notes: fxForm.notes || null });
    } else {
      await s.createFxRate({ month: monthVal, avg_rate: rateVal, notes: fxForm.notes || null });
    }
    setSaving(false);
    setModal(null);
  };

  const delFx = async (id) => { if (window.confirm('Delete this FX rate?')) await s.deleteFxRate(id); };

  const handleRefresh = async (mode) => {
    setRefreshStatus('loading');
    setRefreshError(null);
    const result = await s.refreshFxRates({ mode });
    if (result.success) {
      setRefreshStatus('success');
      setTimeout(() => setRefreshStatus(null), 3000);
    } else {
      setRefreshStatus('error');
      setRefreshError(result.error || 'Unknown error');
    }
  };

  return (
    <SectionCard title="Currency & FX Rates" subtitle="EUR/INR monthly average rates and display currency">
      <FieldLabel label="Display currency (global)">
        <div style={{ display: 'flex', gap: 0, border: '1px solid rgba(0,0,0,0.15)', borderRadius: 4, overflow: 'hidden', width: 'fit-content' }}>
          {['INR', 'EUR'].map(c => (
            <button key={c} onClick={() => toggleCurrency(c)} disabled={saving} style={{
              padding: '6px 20px', border: 'none', cursor: 'pointer', fontFamily: font, fontSize: 12, fontWeight: 400,
              background: s.displayCurrency === c ? '#111' : '#fff', color: s.displayCurrency === c ? '#fff' : '#333'
            }}>
              {c === 'INR' ? '\u20B9 INR' : '\u20AC EUR'}
            </button>
          ))}
        </div>
      </FieldLabel>
      <p style={{ fontSize: 10, color: '#999', margin: '4px 0 16px', fontWeight: 300 }}>
        All monetary values across the app display in the selected currency.
      </p>

      {/* Debug: Conversion Test (admin verification) */}
      <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 4, padding: 12, marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 500, color: '#0369a1', marginBottom: 6 }}>Conversion Test</div>
        <div style={{ fontSize: 11, fontWeight: 300, color: '#0c4a6e' }}>
          <span style={{ fontFamily: 'monospace' }}>₹1,000</span>
          {' → '}
          <span style={{ fontFamily: 'monospace', fontWeight: 500 }}>
            {s.formatMoneyInDisplayCurrency ? s.formatMoneyInDisplayCurrency(1000, 'INR') : '(formatMoneyInDisplayCurrency not available)'}
          </span>
          <span style={{ color: '#64748b', marginLeft: 8 }}>
            (Rate: {s.fxDefaultRate?.toFixed(2) || '?'} INR per EUR)
          </span>
        </div>
      </div>

      <FieldLabel label="Default fallback rate (EUR/INR)">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input type="number" step="0.01" value={defaultRate} onChange={e => setDefaultRate(e.target.value)} style={{ ...inputStyle, width: 120 }} />
          <button onClick={saveDefaultRate} disabled={saving} style={btnSmall}><Save style={{ width: 10, height: 10, marginRight: 4 }} /> Save</button>
        </div>
      </FieldLabel>
      <p style={{ fontSize: 10, color: '#999', margin: '4px 0 20px', fontWeight: 300 }}>Used when no monthly rate exists.</p>

      {/* Auto-refresh */}
      <div style={{ background: '#f8f8f8', padding: 16, borderRadius: 6, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <Zap style={{ width: 12, height: 12, color: '#9F902A' }} />
          <span style={{ fontSize: 12, fontWeight: 400 }}>Auto-generate FX Rates</span>
        </div>
        <p style={{ fontSize: 10, color: '#666', fontWeight: 300, margin: '0 0 12px' }}>
          Fetch historical EUR/INR rates via a server-side edge function. Rates are averaged per month and upserted.
        </p>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button onClick={() => handleRefresh('backfill')} disabled={refreshStatus === 'loading'} style={btnGold}>
            <Download style={{ width: 10, height: 10, marginRight: 4 }} />
            {refreshStatus === 'loading' ? 'Working\u2026' : 'Backfill since 2018'}
          </button>
          <button onClick={() => handleRefresh('incremental')} disabled={refreshStatus === 'loading'} style={btnSmall}>
            <RefreshCw style={{ width: 10, height: 10, marginRight: 4 }} /> Refresh last month
          </button>
          {refreshStatus === 'success' && <StatusBadge ok label="Refreshed" />}
          {refreshStatus === 'error' && (
            <span style={{ fontSize: 10, color: '#934948' }}>
              <AlertTriangle style={{ width: 10, height: 10, marginRight: 3, verticalAlign: -1 }} />{refreshError}
            </span>
          )}
        </div>
      </div>

      {/* FX table */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 400 }}>Monthly FX Rates ({s.fxRates.length} entries)</span>
        <button onClick={openAdd} style={btnGold}><Plus style={{ width: 10, height: 10, marginRight: 4 }} /> Add Rate</button>
      </div>
      <div style={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 4, overflow: 'hidden', maxHeight: 400, overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
          <thead><tr style={{ background: '#f8f8f8', position: 'sticky', top: 0 }}>
            <th style={thStyle}>Month</th><th style={thStyle}>Pair</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Rate</th><th style={thStyle}>Notes</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Updated</th>
            <th style={{ ...thStyle, width: 70, textAlign: 'center' }}>Actions</th>
          </tr></thead>
          <tbody>
            {s.fxRates.length === 0 && (
              <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#999', fontStyle: 'italic', padding: 20 }}>No FX rates configured yet.</td></tr>
            )}
            {s.fxRates.map(r => (
              <tr key={r.id} style={{ borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                <td style={tdStyle}>{(r.month || '').substring(0, 7)}</td>
                <td style={tdStyle}>{r.pair}</td>
                <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>{(Math.round(parseFloat(r.avg_rate) * 100) / 100).toFixed(2)}</td>
                <td style={{ ...tdStyle, color: '#666', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.notes || '\u2014'}</td>
                <td style={{ ...tdStyle, textAlign: 'right', color: '#999', fontSize: 10 }}>{r.updated_at ? new Date(r.updated_at).toLocaleDateString() : '\u2014'}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <button onClick={() => openEdit(r)} style={btnIcon} title="Edit"><Edit2 style={{ width: 12, height: 12 }} /></button>
                  <button onClick={() => delFx(r.id)} style={{ ...btnIcon, color: '#934948' }} title="Delete"><Trash2 style={{ width: 12, height: 12 }} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <ModalOverlay onClose={() => setModal(null)}>
          <div style={{ padding: 24 }}>
            <h3 style={{ fontFamily: font, fontSize: 13, fontWeight: 400, margin: '0 0 16px' }}>
              {modal === 'add' ? 'Add FX Rate' : 'Edit FX Rate'}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <FieldLabel label="Month">
                <BrioMonthPicker value={fxForm.month} onChange={v => setFxForm(p => ({ ...p, month: v }))} />
              </FieldLabel>
              <FieldLabel label="Average Rate (EUR/INR)">
                <input type="number" step="0.01" value={fxForm.avg_rate} onChange={e => setFxForm(p => ({ ...p, avg_rate: e.target.value }))} placeholder="e.g. 89.50" style={inputStyle} />
                {fxForm.avg_rate && <span style={{ fontSize: 10, color: '#666', marginTop: 2 }}>Rounded: {(Math.round(parseFloat(fxForm.avg_rate) * 100) / 100).toFixed(2)}</span>}
              </FieldLabel>
              <FieldLabel label="Notes (optional)">
                <input type="text" value={fxForm.notes} onChange={e => setFxForm(p => ({ ...p, notes: e.target.value }))} placeholder="Source, methodology" style={inputStyle} />
              </FieldLabel>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button onClick={() => setModal(null)} style={btnSmall}>Cancel</button>
              <button onClick={saveFx} disabled={saving} style={btnGold}>{saving ? 'Saving\u2026' : 'Save'}</button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </SectionCard>
  );
}

// ============================================================================
// INFLATION & COST INDICES
// ============================================================================
// Two series, surfaced here instead of being Supabase-only:
//   1. Construction cost index (boq.cost_index_yearly) — escalates BOQ library
//      rates onto a new pricing date. THIS is what the BOQ rate picker uses.
//   2. General consumer CPI (public.inflation_cpi_yearly) — a labelled fallback,
//      also feeds fee-inflation comparables in Inquiries.
// CPI rows are keyed by (country_code, year); cost-index rows by (region, year)
// with a uuid id. In edit mode the identity columns are locked.
// ============================================================================
const REGION_OPTIONS = ['IN', 'NL'];
const COUNTRY_OPTIONS = ['IN', 'NL'];

function InflationSection({ s }) {
  const [ciModal, setCiModal] = useState(null); // 'add' | row
  const [ciForm, setCiForm] = useState({ region: 'IN', year: '', index_value: '', source: 'manual', note: '' });
  const [cpiModal, setCpiModal] = useState(null); // 'add' | row
  const [cpiForm, setCpiForm] = useState({ country_code: 'IN', year: '', cpi: '', source: 'manual' });
  const [saving, setSaving] = useState(false);

  const costIndex = s.costIndexYearly || [];
  const cpi = s.cpiYearly || [];

  // ---- Construction cost index handlers ----
  const openCiAdd = () => { setCiForm({ region: 'IN', year: '', index_value: '', source: 'manual', note: '' }); setCiModal('add'); };
  const openCiEdit = (r) => {
    setCiForm({ region: r.region, year: String(r.year), index_value: String(r.index_value), source: r.source || 'manual', note: r.note || '' });
    setCiModal(r);
  };
  const saveCi = async () => {
    const year = parseInt(ciForm.year, 10);
    const idx = parseFloat(ciForm.index_value);
    if (!Number.isFinite(year) || !Number.isFinite(idx) || idx <= 0) return;
    setSaving(true);
    await s.upsertCostIndexYear({ series: 'construction', region: ciForm.region.trim().toUpperCase(), year, index_value: idx, source: ciForm.source || 'manual', note: ciForm.note || null });
    setSaving(false);
    setCiModal(null);
  };
  const delCi = async (r) => { if (window.confirm(`Delete construction index ${r.region} ${r.year}?`)) await s.deleteCostIndexYear(r.id); };

  // ---- CPI handlers ----
  const openCpiAdd = () => { setCpiForm({ country_code: 'IN', year: '', cpi: '', source: 'manual' }); setCpiModal('add'); };
  const openCpiEdit = (r) => {
    setCpiForm({ country_code: r.country_code, year: String(r.year), cpi: String(r.cpi), source: r.source || 'manual' });
    setCpiModal(r);
  };
  const saveCpi = async () => {
    const year = parseInt(cpiForm.year, 10);
    const val = parseFloat(cpiForm.cpi);
    if (!Number.isFinite(year) || !Number.isFinite(val) || val <= 0) return;
    setSaving(true);
    await s.upsertCpiYear({ country_code: cpiForm.country_code.trim().toUpperCase(), year, cpi: val, source: cpiForm.source || 'manual' });
    setSaving(false);
    setCpiModal(null);
  };
  const delCpi = async (r) => { if (window.confirm(`Delete CPI ${r.country_code} ${r.year}?`)) await s.deleteCpiYear(r.country_code, r.year); };

  return (
    <>
      <SectionCard
        title="Construction Cost Index"
        subtitle="Yearly index per region used to inflation-adjust BOQ library rates onto a new pricing date (adjusted = original × index(today) ÷ index(rate date)). Replace the firm-estimate seed with WPI/CPWD (India) or CBS bouwkosten (NL) figures — only the ratios between years matter, the base is arbitrary."
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 400 }}>Index points ({costIndex.length} entries)</span>
          <button onClick={openCiAdd} style={btnGold}><Plus style={{ width: 10, height: 10, marginRight: 4 }} /> Add Point</button>
        </div>
        <div style={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 4, overflow: 'hidden', maxHeight: 400, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead><tr style={{ background: '#f8f8f8', position: 'sticky', top: 0 }}>
              <th style={thStyle}>Region</th><th style={thStyle}>Year</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Index</th>
              <th style={thStyle}>Source</th><th style={thStyle}>Note</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Updated</th>
              <th style={{ ...thStyle, width: 70, textAlign: 'center' }}>Actions</th>
            </tr></thead>
            <tbody>
              {costIndex.length === 0 && (
                <tr><td colSpan={7} style={{ ...tdStyle, textAlign: 'center', color: '#999', fontStyle: 'italic', padding: 20 }}>No cost index points yet.</td></tr>
              )}
              {costIndex.map(r => (
                <tr key={r.id} style={{ borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                  <td style={tdStyle}>{r.region}</td>
                  <td style={tdStyle}>{r.year}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>{Number(r.index_value).toFixed(1)}</td>
                  <td style={{ ...tdStyle, color: '#666' }}>{r.source || '—'}</td>
                  <td style={{ ...tdStyle, color: '#666', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.note || '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: '#999', fontSize: 10 }}>{r.updated_at ? new Date(r.updated_at).toLocaleDateString() : '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button onClick={() => openCiEdit(r)} style={btnIcon} title="Edit"><Edit2 style={{ width: 12, height: 12 }} /></button>
                    <button onClick={() => delCi(r)} style={{ ...btnIcon, color: '#934948' }} title="Delete"><Trash2 style={{ width: 12, height: 12 }} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <SectionCard
        title="Consumer Price Index (CPI)"
        subtitle="General consumer inflation (World Bank). Used as a fallback and for fee-inflation comparables in Inquiries — NOT the primary basis for construction rates (its basket tracks construction costs only loosely). 2025+ rows may be extrapolated."
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 400 }}>CPI points ({cpi.length} entries)</span>
          <button onClick={openCpiAdd} style={btnGold}><Plus style={{ width: 10, height: 10, marginRight: 4 }} /> Add Point</button>
        </div>
        <div style={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 4, overflow: 'hidden', maxHeight: 400, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead><tr style={{ background: '#f8f8f8', position: 'sticky', top: 0 }}>
              <th style={thStyle}>Country</th><th style={thStyle}>Year</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>CPI</th>
              <th style={thStyle}>Source</th>
              <th style={{ ...thStyle, textAlign: 'right' }}>Updated</th>
              <th style={{ ...thStyle, width: 70, textAlign: 'center' }}>Actions</th>
            </tr></thead>
            <tbody>
              {cpi.length === 0 && (
                <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#999', fontStyle: 'italic', padding: 20 }}>No CPI points yet.</td></tr>
              )}
              {cpi.map(r => (
                <tr key={`${r.country_code}-${r.year}`} style={{ borderBottom: '1px solid rgba(0,0,0,0.04)' }}>
                  <td style={tdStyle}>{r.country_code}</td>
                  <td style={tdStyle}>{r.year}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', fontFamily: 'monospace' }}>{Number(r.cpi).toFixed(2)}</td>
                  <td style={{ ...tdStyle, color: '#666' }}>{r.source || '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'right', color: '#999', fontSize: 10 }}>{r.updated_at ? new Date(r.updated_at).toLocaleDateString() : '—'}</td>
                  <td style={{ ...tdStyle, textAlign: 'center' }}>
                    <button onClick={() => openCpiEdit(r)} style={btnIcon} title="Edit"><Edit2 style={{ width: 12, height: 12 }} /></button>
                    <button onClick={() => delCpi(r)} style={{ ...btnIcon, color: '#934948' }} title="Delete"><Trash2 style={{ width: 12, height: 12 }} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {ciModal && (
        <ModalOverlay onClose={() => setCiModal(null)}>
          <div style={{ padding: 24 }}>
            <h3 style={{ fontFamily: font, fontSize: 13, fontWeight: 400, margin: '0 0 16px' }}>
              {ciModal === 'add' ? 'Add Cost Index Point' : `Edit Cost Index — ${ciForm.region} ${ciForm.year}`}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <FieldLabel label="Region">
                <select value={ciForm.region} onChange={e => setCiForm(p => ({ ...p, region: e.target.value }))} disabled={ciModal !== 'add'} style={inputStyle}>
                  {REGION_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </FieldLabel>
              <FieldLabel label="Year">
                <input type="number" value={ciForm.year} onChange={e => setCiForm(p => ({ ...p, year: e.target.value }))} disabled={ciModal !== 'add'} placeholder="e.g. 2026" style={inputStyle} />
              </FieldLabel>
              <FieldLabel label="Index value">
                <input type="number" step="0.1" value={ciForm.index_value} onChange={e => setCiForm(p => ({ ...p, index_value: e.target.value }))} placeholder="e.g. 126.0" style={inputStyle} />
              </FieldLabel>
              <FieldLabel label="Source">
                <input type="text" value={ciForm.source} onChange={e => setCiForm(p => ({ ...p, source: e.target.value }))} placeholder="WPI / CPWD / CBS / firm-estimate" style={inputStyle} />
              </FieldLabel>
              <FieldLabel label="Note (optional)">
                <input type="text" value={ciForm.note} onChange={e => setCiForm(p => ({ ...p, note: e.target.value }))} style={inputStyle} />
              </FieldLabel>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button onClick={() => setCiModal(null)} style={btnSmall}>Cancel</button>
              <button onClick={saveCi} disabled={saving} style={btnGold}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {cpiModal && (
        <ModalOverlay onClose={() => setCpiModal(null)}>
          <div style={{ padding: 24 }}>
            <h3 style={{ fontFamily: font, fontSize: 13, fontWeight: 400, margin: '0 0 16px' }}>
              {cpiModal === 'add' ? 'Add CPI Point' : `Edit CPI — ${cpiForm.country_code} ${cpiForm.year}`}
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <FieldLabel label="Country">
                <select value={cpiForm.country_code} onChange={e => setCpiForm(p => ({ ...p, country_code: e.target.value }))} disabled={cpiModal !== 'add'} style={inputStyle}>
                  {COUNTRY_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </FieldLabel>
              <FieldLabel label="Year">
                <input type="number" value={cpiForm.year} onChange={e => setCpiForm(p => ({ ...p, year: e.target.value }))} disabled={cpiModal !== 'add'} placeholder="e.g. 2024" style={inputStyle} />
              </FieldLabel>
              <FieldLabel label="CPI value">
                <input type="number" step="0.01" value={cpiForm.cpi} onChange={e => setCpiForm(p => ({ ...p, cpi: e.target.value }))} placeholder="e.g. 227.60" style={inputStyle} />
              </FieldLabel>
              <FieldLabel label="Source">
                <input type="text" value={cpiForm.source} onChange={e => setCpiForm(p => ({ ...p, source: e.target.value }))} placeholder="worldbank / manual / extrapolated" style={inputStyle} />
              </FieldLabel>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button onClick={() => setCpiModal(null)} style={btnSmall}>Cancel</button>
              <button onClick={saveCpi} disabled={saving} style={btnGold}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </ModalOverlay>
      )}
    </>
  );
}


// ============================================================================
// 3. COLOURS SECTION (roles, project status, category palettes, TEAM COLOURS)
// ============================================================================
function ColorsSection({ s }) {
  const theme = s.themeConfig || {};
  const [editSection, setEditSection] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  const openRoles = () => { setDraft({ ...theme.roles }); setEditSection('roles'); };
  const openPalettes = () => { setDraft(JSON.parse(JSON.stringify(theme.categoryPalettes || {}))); setEditSection('palettes'); };
  const openTeams = () => { setDraft(JSON.parse(JSON.stringify(theme.teams || {}))); setEditSection('teams'); };
  const openTeamPalette = () => {
    // Initialize with existing teamPalette or default
    const currentPalette = theme.teamPalette && Array.isArray(theme.teamPalette) && theme.teamPalette.length === 20
      ? [...theme.teamPalette]
      : [...DEFAULT_TEAM_PALETTE];
    setDraft(currentPalette);
    setEditSection('teamPalette');
  };
  const openMbti = () => {
    // Merge defaults with any saved overrides
    const saved = theme.mbtiColors || {};
    const merged = {};
    Object.keys(MBTI_COLORS).forEach(type => { merged[type] = saved[type] || MBTI_COLORS[type]; });
    setDraft(merged);
    setEditSection('mbti');
  };

  const saveRoles = async () => { setSaving(true); await s.updateThemeConfig({ ...theme, roles: draft }); setSaving(false); setEditSection(null); };
  const savePalettes = async () => { setSaving(true); await s.updateThemeConfig({ ...theme, categoryPalettes: draft }); setSaving(false); setEditSection(null); };
  const saveTeams = async () => { setSaving(true); await s.updateThemeConfig({ ...theme, teams: draft }); setSaving(false); setEditSection(null); };
  const saveTeamPalette = async () => { setSaving(true); await s.updateThemeConfig({ ...theme, teamPalette: draft }); setSaving(false); setEditSection(null); };
  const saveMbti = async () => { setSaving(true); await s.updateThemeConfig({ ...theme, mbtiColors: draft }); setSaving(false); setEditSection(null); };

  const teamEntries = Object.entries(theme.teams || {});
  // Get current team palette (from theme or default)
  const currentTeamPalette = (theme.teamPalette && Array.isArray(theme.teamPalette) && theme.teamPalette.length === 20)
    ? theme.teamPalette
    : DEFAULT_TEAM_PALETTE;

  return (
    <SectionCard title="Colour Scheme" subtitle="Edit role colours, category palettes, and team colours. Changes apply via CSS variables.">
      {/* Role Colours */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 400 }}>Role Colours</span>
          <button onClick={openRoles} style={btnSmall}><Edit2 style={{ width: 10, height: 10, marginRight: 4 }} /> Edit</button>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {ROLE_LIST.map(role => (
            <div key={role} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, fontWeight: 300 }}>
              <ColorSwatch color={theme.roles?.[role] || '#ccc'} size={16} />
              <span>{role}</span>
            </div>
          ))}
        </div>
      </div>

      {/* MBTI Personality Colours */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 400 }}>MBTI Personality Colours</span>
          <button onClick={openMbti} style={btnSmall}><Edit2 style={{ width: 10, height: 10, marginRight: 4 }} /> Edit</button>
        </div>
        <p style={{ fontSize: 10, color: '#666', margin: '0 0 8px', fontWeight: 300 }}>
          16 colours for MBTI personality types, used in team avatars and personality badges.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {MBTI_GROUPS.map(group => (
            <div key={group.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 10, fontWeight: 400, width: 90, color: '#555' }}>{group.label}</span>
              <div style={{ display: 'flex', gap: 6 }}>
                {group.types.map(type => {
                  const c = theme.mbtiColors?.[type] || MBTI_COLORS[type];
                  return (
                    <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 300 }}>
                      <ColorSwatch color={c} size={16} />
                      <span>{type}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Project Status Colours (read-only) */}
      <div style={{ marginBottom: 20 }}>
        <span style={{ fontSize: 12, fontWeight: 400, display: 'block', marginBottom: 8 }}>Project Status Colours</span>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {Object.entries(theme.projectStatus || {}).map(([status, color]) => (
            <div key={status} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, fontWeight: 300 }}>
              <ColorSwatch color={color} size={16} /><span>{status}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Team Palette (NEW - 20 approved colors for team assignment) */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 400 }}>Team Palette</span>
          <button onClick={openTeamPalette} style={btnSmall}><Edit2 style={{ width: 10, height: 10, marginRight: 4 }} /> Edit</button>
        </div>
        <p style={{ fontSize: 10, color: '#666', margin: '0 0 8px', fontWeight: 300 }}>
          20 approved colours that can be assigned to teams in the Team modal.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {currentTeamPalette.map((color, idx) => (
            <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <ColorSwatch color={color} size={20} />
              <span style={{ fontSize: 8, color: '#999' }}>{idx + 1}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Team Colours (Legacy - team name to color mapping, kept for CSS variable injection) */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 400 }}>Team Colours (Legacy)</span>
          <button onClick={openTeams} style={btnSmall}><Edit2 style={{ width: 10, height: 10, marginRight: 4 }} /> Edit</button>
        </div>
        {teamEntries.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {teamEntries.map(([name, color]) => (
              <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 10, fontWeight: 300 }}>
                <ColorSwatch color={color} size={16} /><span>{name}</span>
              </div>
            ))}
          </div>
        ) : (
          <p style={{ fontSize: 10, color: '#999', fontStyle: 'italic', margin: 0 }}>No legacy team colours. Teams now store color directly.</p>
        )}
      </div>

      {/* Category Palettes */}
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <span style={{ fontSize: 12, fontWeight: 400 }}>Category Palettes</span>
          <button onClick={openPalettes} style={btnSmall}><Edit2 style={{ width: 10, height: 10, marginRight: 4 }} /> Edit</button>
        </div>
        {CATEGORY_PALETTE_KEYS.map(cat => {
          const colors = theme.categoryPalettes?.[cat] || [];
          return (
            <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
              <span style={{ fontSize: 10, fontWeight: 400, width: 80, textTransform: 'capitalize' }}>{cat}</span>
              <div style={{ display: 'flex', gap: 2 }}>
                {colors.slice(0, 10).map((c, i) => <ColorSwatch key={i} color={c} size={14} />)}
                {colors.length > 10 && <span style={{ fontSize: 9, color: '#999' }}>+{colors.length - 10}</span>}
              </div>
            </div>
          );
        })}
      </div>

      {/* ROLE EDIT MODAL */}
      {editSection === 'roles' && (
        <ModalOverlay onClose={() => setEditSection(null)} width={480}>
          <div style={{ padding: 24 }}>
            <h3 style={{ fontFamily: font, fontSize: 13, fontWeight: 400, margin: '0 0 16px' }}>Edit Role Colours</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {ROLE_LIST.map(role => (
                <div key={role} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 11, fontWeight: 300, width: 130 }}>{role}</span>
                  <input type="color" value={draft[role] || '#cccccc'} onChange={e => setDraft(d => ({ ...d, [role]: e.target.value }))}
                    style={{ width: 32, height: 24, border: '1px solid rgba(0,0,0,0.15)', borderRadius: 3, padding: 0, cursor: 'pointer' }} />
                  <input type="text" value={draft[role] || ''} onChange={e => setDraft(d => ({ ...d, [role]: e.target.value }))}
                    placeholder="#000000" style={{ ...inputStyle, width: 90, fontFamily: 'monospace', fontSize: 10 }} />
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button onClick={() => setEditSection(null)} style={btnSmall}>Cancel</button>
              <button onClick={saveRoles} disabled={saving} style={btnGold}>{saving ? 'Saving\u2026' : 'Save Roles'}</button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* TEAM COLOUR EDIT MODAL (NEW) */}
      {editSection === 'teams' && (
        <TeamColorsModal draft={draft} setDraft={setDraft} saving={saving} onSave={saveTeams} onClose={() => setEditSection(null)} />
      )}

      {/* PALETTE EDIT MODAL */}
      {editSection === 'palettes' && (
        <ModalOverlay onClose={() => setEditSection(null)} width={560}>
          <div style={{ padding: 24 }}>
            <h3 style={{ fontFamily: font, fontSize: 13, fontWeight: 400, margin: '0 0 16px' }}>Edit Category Palettes</h3>
            <p style={{ fontSize: 10, fontWeight: 300, color: '#666', marginBottom: 16 }}>Enter hex colours separated by commas.</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {CATEGORY_PALETTE_KEYS.map(cat => (
                <FieldLabel key={cat} label={cat.charAt(0).toUpperCase() + cat.slice(1)}>
                  <textarea value={(draft[cat] || []).join(', ')}
                    onChange={e => { const colors = e.target.value.split(',').map(c => c.trim()).filter(c => c); setDraft(d => ({ ...d, [cat]: colors })); }}
                    rows={2} style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 10, resize: 'vertical' }} />
                  <div style={{ display: 'flex', gap: 2, marginTop: 4 }}>
                    {(draft[cat] || []).slice(0, 14).map((c, i) => <ColorSwatch key={i} color={c} size={12} />)}
                  </div>
                </FieldLabel>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button onClick={() => setEditSection(null)} style={btnSmall}>Cancel</button>
              <button onClick={savePalettes} disabled={saving} style={btnGold}>{saving ? 'Saving\u2026' : 'Save Palettes'}</button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* TEAM PALETTE EDIT MODAL (NEW - 20 fixed colors) */}
      {editSection === 'teamPalette' && (
        <ModalOverlay onClose={() => setEditSection(null)} width={560}>
          <div style={{ padding: 24 }}>
            <h3 style={{ fontFamily: font, fontSize: 13, fontWeight: 400, margin: '0 0 6px' }}>Edit Team Palette</h3>
            <p style={{ fontSize: 10, fontWeight: 300, color: '#666', marginBottom: 16 }}>
              Define exactly 20 approved colours that can be assigned to teams. Teams select one of these colours in the Add/Edit Team modal.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 16 }}>
              {Array.isArray(draft) && draft.map((color, idx) => (
                <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <span style={{ fontSize: 9, color: '#999', fontWeight: 500 }}>{idx + 1}</span>
                  <input 
                    type="color" 
                    value={color || '#cccccc'} 
                    onChange={e => {
                      const newPalette = [...draft];
                      newPalette[idx] = e.target.value;
                      setDraft(newPalette);
                    }}
                    style={{ width: 40, height: 32, border: '1px solid rgba(0,0,0,0.15)', borderRadius: 3, padding: 0, cursor: 'pointer' }} 
                  />
                  <input 
                    type="text" 
                    value={color || ''} 
                    onChange={e => {
                      const newPalette = [...draft];
                      newPalette[idx] = e.target.value;
                      setDraft(newPalette);
                    }}
                    placeholder="#000000" 
                    style={{ ...inputStyle, width: 70, fontFamily: 'monospace', fontSize: 9, padding: '3px 4px', textAlign: 'center' }} 
                  />
                </div>
              ))}
            </div>
            <div style={{ background: '#f8f8f8', padding: 12, borderRadius: 4, marginBottom: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 400, color: '#666', marginBottom: 6 }}>Reset to Defaults</div>
              <button 
                onClick={() => setDraft([...DEFAULT_TEAM_PALETTE])} 
                style={btnSmall}
              >
                <RefreshCw style={{ width: 10, height: 10, marginRight: 4 }} />
                Reset All to Default Palette
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditSection(null)} style={btnSmall}>Cancel</button>
              <button onClick={saveTeamPalette} disabled={saving} style={btnGold}>{saving ? 'Saving\u2026' : 'Save Palette'}</button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* MBTI COLOUR EDIT MODAL */}
      {editSection === 'mbti' && (
        <ModalOverlay onClose={() => setEditSection(null)} width={560}>
          <div style={{ padding: 24 }}>
            <h3 style={{ fontFamily: font, fontSize: 13, fontWeight: 400, margin: '0 0 6px' }}>Edit MBTI Personality Colours</h3>
            <p style={{ fontSize: 10, fontWeight: 300, color: '#666', marginBottom: 16 }}>
              One colour per MBTI type. Used for personality badges and HFB avatar mode.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {MBTI_GROUPS.map(group => (
                <div key={group.label}>
                  <span style={{ fontSize: 10, fontWeight: 500, color: '#555', display: 'block', marginBottom: 6 }}>{group.label}</span>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                    {group.types.map(type => (
                      <div key={type} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <span style={{ fontSize: 10, fontWeight: 500 }}>{type}</span>
                        <input
                          type="color"
                          value={draft[type] || '#cccccc'}
                          onChange={e => setDraft(d => ({ ...d, [type]: e.target.value }))}
                          style={{ width: 36, height: 28, border: '1px solid rgba(0,0,0,0.15)', borderRadius: 3, padding: 0, cursor: 'pointer' }}
                        />
                        <input
                          type="text"
                          value={draft[type] || ''}
                          onChange={e => setDraft(d => ({ ...d, [type]: e.target.value }))}
                          placeholder="#000000"
                          style={{ ...inputStyle, width: 76, fontFamily: 'monospace', fontSize: 9, padding: '3px 4px', textAlign: 'center' }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ background: '#f8f8f8', padding: 12, borderRadius: 4, marginTop: 16, marginBottom: 16 }}>
              <div style={{ fontSize: 10, fontWeight: 400, color: '#666', marginBottom: 6 }}>Reset to Defaults</div>
              <button
                onClick={() => setDraft({ ...MBTI_COLORS })}
                style={btnSmall}
              >
                <RefreshCw style={{ width: 10, height: 10, marginRight: 4 }} />
                Reset All to Default MBTI Colours
              </button>
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={() => setEditSection(null)} style={btnSmall}>Cancel</button>
              <button onClick={saveMbti} disabled={saving} style={btnGold}>{saving ? 'Saving\u2026' : 'Save MBTI Colours'}</button>
            </div>
          </div>
        </ModalOverlay>
      )}

    </SectionCard>
  );
}


// ============================================================================
// TEAM COLOURS MODAL — add/edit/remove team name → hex colour mappings
// ============================================================================
function TeamColorsModal({ draft, setDraft, saving, onSave, onClose }) {
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(DEFAULT_TEAM_PALETTE[0]);
  const entries = Object.entries(draft).sort(([a], [b]) => a.localeCompare(b));

  const addTeam = () => {
    const name = newName.trim();
    if (!name) return;
    setDraft(d => ({ ...d, [name]: newColor }));
    setNewName('');
    setNewColor(DEFAULT_TEAM_PALETTE[(entries.length + 1) % DEFAULT_TEAM_PALETTE.length]);
  };

  const removeTeam = (name) => {
    setDraft(d => { const next = { ...d }; delete next[name]; return next; });
  };

  return (
    <ModalOverlay onClose={onClose} width={500}>
      <div style={{ padding: 24 }}>
        <h3 style={{ fontFamily: font, fontSize: 13, fontWeight: 400, margin: '0 0 6px' }}>Edit Team Colours</h3>
        <p style={{ fontSize: 10, fontWeight: 300, color: '#666', marginBottom: 16 }}>
          Each team gets a CSS variable <code>--brio-team-slug</code> for use across the app.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
          {entries.map(([name, color]) => (
            <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 11, fontWeight: 300, flex: 1, minWidth: 100 }}>{name}</span>
              <input type="color" value={color || '#cccccc'} onChange={e => setDraft(d => ({ ...d, [name]: e.target.value }))}
                style={{ width: 32, height: 24, border: '1px solid rgba(0,0,0,0.15)', borderRadius: 3, padding: 0, cursor: 'pointer' }} />
              <input type="text" value={color || ''} onChange={e => setDraft(d => ({ ...d, [name]: e.target.value }))}
                placeholder="#000000" style={{ ...inputStyle, width: 90, fontFamily: 'monospace', fontSize: 10 }} />
              <button onClick={() => removeTeam(name)} style={{ ...btnIcon, color: '#934948' }} title="Remove">
                <Trash2 style={{ width: 11, height: 11 }} />
              </button>
            </div>
          ))}
          {entries.length === 0 && (
            <p style={{ fontSize: 10, color: '#999', fontStyle: 'italic', margin: 0 }}>No teams configured. Add one below.</p>
          )}
        </div>

        {/* Add new team */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', borderTop: '1px solid rgba(0,0,0,0.08)', paddingTop: 12, marginBottom: 16 }}>
          <input type="text" value={newName} onChange={e => setNewName(e.target.value)} placeholder="Team name"
            style={{ ...inputStyle, flex: 1 }}
            onKeyDown={e => { if (e.key === 'Enter') addTeam(); }} />
          <input type="color" value={newColor} onChange={e => setNewColor(e.target.value)}
            style={{ width: 32, height: 24, border: '1px solid rgba(0,0,0,0.15)', borderRadius: 3, padding: 0, cursor: 'pointer' }} />
          <button onClick={addTeam} style={btnSmall}><Plus style={{ width: 10, height: 10, marginRight: 4 }} />Add</button>
        </div>

        {/* Presets */}
        <div style={{ marginBottom: 16 }}>
          <span style={{ fontSize: 10, fontWeight: 400, color: '#666' }}>Default palette:</span>
          <div style={{ display: 'flex', gap: 3, marginTop: 4 }}>
            {DEFAULT_TEAM_PALETTE.map((c, i) => <ColorSwatch key={i} color={c} size={16} onClick={() => setNewColor(c)} selected={newColor === c} />)}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={btnSmall}>Cancel</button>
          <button onClick={onSave} disabled={saving} style={btnGold}>{saving ? 'Saving\u2026' : 'Save Teams'}</button>
        </div>
      </div>
    </ModalOverlay>
  );
}


// ============================================================================
// 4. PROJECT CATEGORIES
// ============================================================================
function CategoriesSection({ s }) {
  const [editId, setEditId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const cats = s.projectCategories;
  const startEdit = (cat) => { setEditId(cat.id); setEditLabel(cat.label); };
  const cancelEdit = () => { setEditId(null); setEditLabel(''); };

  const saveEdit = async () => {
    if (!editLabel.trim()) return;
    setSaving(true); await s.updateCategory(editId, { label: editLabel.trim() }); setSaving(false); cancelEdit();
  };

  const addCategory = async () => {
    if (!newLabel.trim()) return;
    setSaving(true); await s.createCategory(newLabel.trim()); setSaving(false); setNewLabel('');
  };

  const toggleActive = async (cat) => { setSaving(true); await s.updateCategory(cat.id, { isActive: !cat.isActive }); setSaving(false); };

  const handleDelete = async (cat) => {
    if (confirmDeleteId === cat.id) {
      setSaving(true); await s.deleteCategory(cat.id); setSaving(false); setConfirmDeleteId(null);
    } else {
      setConfirmDeleteId(cat.id);
    }
  };

  const moveUp = async (index) => {
    if (index <= 0) return;
    const ids = cats.map(c => c.id);
    [ids[index], ids[index - 1]] = [ids[index - 1], ids[index]];
    await s.reorderCategories(ids);
  };

  const moveDown = async (index) => {
    if (index >= cats.length - 1) return;
    const ids = cats.map(c => c.id);
    [ids[index], ids[index + 1]] = [ids[index + 1], ids[index]];
    await s.reorderCategories(ids);
  };

  return (
    <SectionCard title="Project Categories" subtitle="Add, rename, reorder, or archive project categories.">
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input type="text" value={newLabel} onChange={e => setNewLabel(e.target.value)}
          placeholder="New category name" style={{ ...inputStyle, flex: 1 }}
          onKeyDown={e => { if (e.key === 'Enter') addCategory(); }} />
        <button onClick={addCategory} disabled={saving || !newLabel.trim()} style={btnGold}>
          <Plus style={{ width: 10, height: 10, marginRight: 4 }} /> Add
        </button>
      </div>
      <div style={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 4, overflow: 'hidden' }}>
        {cats.map((cat, i) => (
          <div key={cat.id} style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
            borderBottom: '1px solid rgba(0,0,0,0.04)',
            opacity: cat.isActive ? 1 : 0.5,
            background: editId === cat.id ? 'rgba(159,144,42,0.04)' : 'transparent'
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <button onClick={() => moveUp(i)} disabled={i === 0} style={{ ...btnIcon, padding: 1 }}><ChevronUp style={{ width: 11, height: 11 }} /></button>
              <button onClick={() => moveDown(i)} disabled={i === cats.length - 1} style={{ ...btnIcon, padding: 1 }}><ChevronDown style={{ width: 11, height: 11 }} /></button>
            </div>
            <span style={{ fontSize: 9, fontWeight: 400, color: '#999', width: 18, textAlign: 'center' }}>{cat.order}</span>
            {editId === cat.id ? (
              <div style={{ flex: 1, display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="text" value={editLabel} onChange={e => setEditLabel(e.target.value)}
                  style={{ ...inputStyle, flex: 1 }} autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }} />
                <button onClick={saveEdit} disabled={saving} style={{ ...btnIcon, color: '#49685a' }}><Check style={{ width: 13, height: 13 }} /></button>
                <button onClick={cancelEdit} style={{ ...btnIcon, color: '#934948' }}><X style={{ width: 13, height: 13 }} /></button>
              </div>
            ) : (
              <span style={{ flex: 1, fontSize: 12, fontWeight: 300, color: '#111' }}>
                {cat.label}
                {!cat.isActive && <span style={{ fontSize: 9, color: '#999', marginLeft: 6, fontStyle: 'italic' }}>(archived)</span>}
              </span>
            )}
            {editId !== cat.id && (
              <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <button onClick={() => startEdit(cat)} style={btnIcon} title="Rename"><Edit2 style={{ width: 11, height: 11 }} /></button>
                <button onClick={() => toggleActive(cat)} style={{ ...btnIcon, color: cat.isActive ? '#934948' : '#49685a' }} title={cat.isActive ? 'Archive' : 'Restore'}>
                  {cat.isActive ? <Archive style={{ width: 11, height: 11 }} /> : <RefreshCw style={{ width: 11, height: 11 }} />}
                </button>
                <button onClick={() => handleDelete(cat)} disabled={saving}
                  style={{ ...btnIcon, color: confirmDeleteId === cat.id ? '#fff' : '#934948', background: confirmDeleteId === cat.id ? '#934948' : 'transparent', borderRadius: 3, padding: confirmDeleteId === cat.id ? '2px 6px' : undefined, fontSize: 9 }}
                  title={confirmDeleteId === cat.id ? 'Click again to confirm' : 'Delete'}>
                  {confirmDeleteId === cat.id ? 'Confirm?' : <Trash2 style={{ width: 11, height: 11 }} />}
                </button>
              </div>
            )}
          </div>
        ))}
        {cats.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', fontSize: 11, color: '#999', fontStyle: 'italic' }}>No categories yet.</div>
        )}
      </div>
    </SectionCard>
  );
}


// ============================================================================
// 4a-2. VENDOR SPECIALISMS
// ============================================================================

const VENDOR_CATEGORIES = ['Vendor / Supplier', 'Manufacturer', 'Contractor', 'Consultants', 'Media'];

const VENDOR_CAT_COLORS = {
  'Vendor / Supplier': '#689581',   // brio-teal
  'Contractor':        '#DB6B5D',   // brio-orange
  'Manufacturer':      '#9F902A',   // brio-gold
  'Consultants':       '#84A48C',   // brio-green
  'Media':             '#a5935f',   // brio-yellow
};

function SpecialismsSection() {
  const { allOptions, addOption, updateOption, removeOption, loading } = useSpecialismOptions();
  const [activeCategory, setActiveCategory] = useState(VENDOR_CATEGORIES[0]);
  const [editId, setEditId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const filtered = useMemo(() =>
    allOptions.filter(o => o.category === activeCategory).sort((a, b) => a.label.localeCompare(b.label)),
    [allOptions, activeCategory]
  );

  const startEdit = (item) => { setEditId(item.id); setEditLabel(item.label); };
  const cancelEdit = () => { setEditId(null); setEditLabel(''); };

  const saveEdit = async () => {
    if (!editLabel.trim() || !editId) return;
    setSaving(true);
    await updateOption(editId, editLabel.trim());
    setSaving(false);
    cancelEdit();
  };

  const handleAdd = async () => {
    if (!newLabel.trim()) return;
    setSaving(true);
    const result = await addOption(newLabel.trim(), activeCategory);
    setSaving(false);
    if (result.success) setNewLabel('');
  };

  const handleDelete = async (item) => {
    if (confirmDeleteId === item.id) {
      setSaving(true);
      await removeOption(item.label, item.category);
      setSaving(false);
      setConfirmDeleteId(null);
    } else {
      setConfirmDeleteId(item.id);
    }
  };

  if (loading) return <SectionCard title="Vendor Specialisms"><div style={{ padding: 24, textAlign: 'center', fontSize: 11, color: '#999' }}>Loading...</div></SectionCard>;

  return (
    <SectionCard title="Vendor Specialisms" subtitle="Manage the specialism options available per vendor category in the Contacts Directory.">
      {/* Category tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
        {VENDOR_CATEGORIES.map(cat => (
          <button key={cat} onClick={() => { setActiveCategory(cat); setConfirmDeleteId(null); cancelEdit(); }}
            style={{
              padding: '4px 12px', fontSize: 11, fontWeight: activeCategory === cat ? 600 : 400,
              border: '1px solid ' + (activeCategory === cat ? (VENDOR_CAT_COLORS[cat] || '#9F902A') : 'rgba(0,0,0,0.12)'),
              borderRadius: 3, cursor: 'pointer', fontFamily: "'Raleway', sans-serif",
              background: activeCategory === cat ? `${VENDOR_CAT_COLORS[cat] || '#9F902A'}14` : 'transparent',
              color: activeCategory === cat ? (VENDOR_CAT_COLORS[cat] || '#9F902A') : '#666',
            }}
          >{cat}</button>
        ))}
      </div>

      {/* Add form */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input type="text" value={newLabel} onChange={e => setNewLabel(e.target.value)}
          placeholder={`New specialism for ${activeCategory}`} style={{ ...inputStyle, flex: 1 }}
          onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }} />
        <button onClick={handleAdd} disabled={saving || !newLabel.trim()} style={btnGold}>
          <Plus style={{ width: 10, height: 10, marginRight: 4 }} /> Add
        </button>
      </div>

      {/* List */}
      <div style={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 4, overflow: 'hidden' }}>
        {filtered.map(item => (
          <div key={item.id} style={{
            display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px',
            borderBottom: '1px solid rgba(0,0,0,0.04)',
            background: editId === item.id ? 'rgba(159,144,42,0.04)' : 'transparent',
          }}>
            {editId === item.id ? (
              <div style={{ flex: 1, display: 'flex', gap: 6, alignItems: 'center' }}>
                <input type="text" value={editLabel} onChange={e => setEditLabel(e.target.value)}
                  style={{ ...inputStyle, flex: 1 }} autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }} />
                <button onClick={saveEdit} disabled={saving} style={{ ...btnIcon, color: '#49685a' }}><Check style={{ width: 13, height: 13 }} /></button>
                <button onClick={cancelEdit} style={{ ...btnIcon, color: '#934948' }}><X style={{ width: 13, height: 13 }} /></button>
              </div>
            ) : (
              <>
                <span style={{ flex: 1, fontSize: 12, fontWeight: 300, color: '#111' }}>{item.label}</span>
                <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <button onClick={() => startEdit(item)} style={btnIcon} title="Rename"><Edit2 style={{ width: 11, height: 11 }} /></button>
                  <button onClick={() => handleDelete(item)} disabled={saving}
                    style={{ ...btnIcon, color: confirmDeleteId === item.id ? '#fff' : '#934948', background: confirmDeleteId === item.id ? '#934948' : 'transparent', borderRadius: 3, padding: confirmDeleteId === item.id ? '2px 6px' : undefined, fontSize: 9 }}
                    title={confirmDeleteId === item.id ? 'Click again to confirm' : 'Delete'}>
                    {confirmDeleteId === item.id ? 'Confirm?' : <Trash2 style={{ width: 11, height: 11 }} />}
                  </button>
                </div>
              </>
            )}
          </div>
        ))}
        {filtered.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', fontSize: 11, color: '#999', fontStyle: 'italic' }}>No specialisms for {activeCategory} yet.</div>
        )}
      </div>

      <div style={{ marginTop: 8, fontSize: 10, color: '#999' }}>
        {filtered.length} specialism{filtered.length !== 1 ? 's' : ''} in {activeCategory}
      </div>
    </SectionCard>
  );
}


// ============================================================================
// 4b. PRIORITY OPTIONS
// ============================================================================
function PrioritiesSection({ s }) {
  const items = s.priorityOptions || [];
  const [newItem, setNewItem] = useState('');
  const [editIdx, setEditIdx] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [confirmDeleteIdx, setConfirmDeleteIdx] = useState(null);

  const showFb = (ok) => {
    setFeedback(ok ? 'saved' : 'error');
    setTimeout(() => setFeedback(null), 2000);
  };

  const persist = async (next) => {
    setSaving(true);
    const res = await s.savePriorityOptions(next);
    setSaving(false);
    showFb(res.success);
  };

  const isDuplicate = (name, excludeIdx) => {
    const lower = name.trim().toLowerCase();
    return items.some((r, i) => i !== excludeIdx && r.toLowerCase() === lower);
  };

  const handleAdd = async () => {
    const trimmed = newItem.trim();
    if (!trimmed || trimmed.length > 50) return;
    if (isDuplicate(trimmed, -1)) return;
    await persist([...items, trimmed]);
    setNewItem('');
  };

  const handleDelete = async (idx) => {
    if (confirmDeleteIdx === idx) {
      const next = items.filter((_, i) => i !== idx);
      await persist(next);
      if (editIdx === idx) { setEditIdx(null); setEditVal(''); }
      setConfirmDeleteIdx(null);
    } else {
      setConfirmDeleteIdx(idx);
    }
  };

  const startEdit = (idx) => {
    setEditIdx(idx);
    setEditVal(items[idx]);
    setConfirmDeleteIdx(null);
  };

  const saveEdit = async () => {
    const trimmed = editVal.trim();
    if (!trimmed || trimmed.length > 50) return;
    if (isDuplicate(trimmed, editIdx)) return;
    const next = items.map((r, i) => i === editIdx ? trimmed : r);
    await persist(next);
    setEditIdx(null);
    setEditVal('');
  };

  const cancelEdit = () => { setEditIdx(null); setEditVal(''); };

  const moveUp = async (idx) => {
    if (idx === 0) return;
    const next = [...items];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    await persist(next);
  };

  const moveDown = async (idx) => {
    if (idx >= items.length - 1) return;
    const next = [...items];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    await persist(next);
  };

  return (
    <SectionCard
      title="Priority Options"
      subtitle="Manage priority levels used in Project Tasks and Inquiries. Changes apply immediately."
    >
      {/* Add new priority */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
        <input
          type="text"
          value={newItem}
          onChange={e => setNewItem(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
          placeholder="New priority level…"
          maxLength={50}
          style={{ ...inputStyle, width: 240 }}
        />
        <button onClick={handleAdd} disabled={saving || !newItem.trim()} style={btnGold}>
          <Plus style={{ width: 10, height: 10, marginRight: 4 }} />Add
        </button>
        <SaveFeedback status={feedback} />
      </div>

      {/* Priority list */}
      <div style={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 4, overflow: 'hidden' }}>
        {items.map((item, idx) => (
          <div key={idx} style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
            borderBottom: idx < items.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
            background: editIdx === idx ? 'rgba(159,144,42,0.06)' : 'transparent',
          }}>
            {/* Reorder buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              <button onClick={() => moveUp(idx)} disabled={idx === 0 || saving}
                style={{ ...btnIcon, padding: '1px 4px', opacity: idx === 0 ? 0.25 : 1 }}>
                <ChevronUp style={{ width: 11, height: 11 }} />
              </button>
              <button onClick={() => moveDown(idx)} disabled={idx >= items.length - 1 || saving}
                style={{ ...btnIcon, padding: '1px 4px', opacity: idx >= items.length - 1 ? 0.25 : 1 }}>
                <ChevronDown style={{ width: 11, height: 11 }} />
              </button>
            </div>

            {/* Label or inline editor */}
            {editIdx === idx ? (
              <div style={{ flex: 1, display: 'flex', gap: 6, alignItems: 'center' }}>
                <input
                  type="text"
                  value={editVal}
                  onChange={e => setEditVal(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                  maxLength={50}
                  autoFocus
                  style={{ ...inputStyle, width: 220 }}
                />
                <button onClick={saveEdit} disabled={saving} style={{ ...btnIcon, color: '#49685a' }}>
                  <Check style={{ width: 13, height: 13 }} />
                </button>
                <button onClick={cancelEdit} style={{ ...btnIcon, color: '#934948' }}>
                  <X style={{ width: 13, height: 13 }} />
                </button>
              </div>
            ) : (
              <span style={{ flex: 1, fontSize: 12, fontWeight: 300, color: '#111' }}>
                {item}
              </span>
            )}

            {/* Actions */}
            {editIdx !== idx && (
              <div style={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <button onClick={() => startEdit(idx)} disabled={saving} style={btnIcon} title="Edit">
                  <Edit2 style={{ width: 11, height: 11 }} />
                </button>
                <button onClick={() => handleDelete(idx)} disabled={saving}
                  style={{ ...btnIcon, color: confirmDeleteIdx === idx ? '#fff' : '#934948', background: confirmDeleteIdx === idx ? '#934948' : 'transparent', borderRadius: 3, padding: confirmDeleteIdx === idx ? '2px 6px' : undefined, fontSize: 9 }}
                  title={confirmDeleteIdx === idx ? 'Click again to confirm' : 'Delete'}>
                  {confirmDeleteIdx === idx ? 'Confirm?' : <Trash2 style={{ width: 11, height: 11 }} />}
                </button>
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && (
          <div style={{ padding: 24, textAlign: 'center', fontSize: 11, color: '#999', fontStyle: 'italic' }}>
            No priority levels configured. Add one above.
          </div>
        )}
      </div>

      <p style={{ fontSize: 10, fontWeight: 300, color: '#888', margin: '12px 0 0', fontStyle: 'italic' }}>
        {items.length} priority level{items.length !== 1 ? 's' : ''} configured.
        Used in Project Tasks and Inquiries.
      </p>
    </SectionCard>
  );
}


// ============================================================================
// 5. TASK CATEGORIES (Project Tasks — category column)
// ============================================================================
// Default colors offered in the color picker
const TASK_CAT_PALETTE = [
  '#7B6B3A','#9f902a','#5A7A8A','#8B5E3C','#5C8A5A',
  '#8A5A7A','#6A6A8A','#934948','#6B9B7B','#5B7A99',
  '#8B7BA3','#CC9966','#9B7A8B','#7A9B9B','#DB6B5D'
];

function TaskCategoriesSection({ s }) {
  const cats = s.taskCategories || [];
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(TASK_CAT_PALETTE[0]);
  const [editIdx, setEditIdx] = useState(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const showFb = (ok) => {
    setFeedback(ok ? 'saved' : 'error');
    setTimeout(() => setFeedback(null), 2000);
  };

  const persist = async (next) => {
    setSaving(true);
    const res = await s.saveTaskCategories(next);
    setSaving(false);
    showFb(res.success);
  };

  const isDuplicate = (name, excludeIdx) => {
    const lower = name.trim().toLowerCase();
    return cats.some((c, i) => i !== excludeIdx && c.name.toLowerCase() === lower);
  };

  const handleAdd = async () => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed.length > 60) return;
    if (isDuplicate(trimmed, -1)) return;
    await persist([...cats, { name: trimmed, color: newColor }]);
    setNewName('');
    setNewColor(TASK_CAT_PALETTE[0]);
  };

  const handleDelete = async (idx) => {
    await persist(cats.filter((_, i) => i !== idx));
    if (editIdx === idx) { setEditIdx(null); setEditName(''); setEditColor(''); }
  };

  const startEdit = (idx) => {
    setEditIdx(idx);
    setEditName(cats[idx].name);
    setEditColor(cats[idx].color);
  };

  const saveEdit = async () => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed.length > 60) return;
    if (isDuplicate(trimmed, editIdx)) return;
    await persist(cats.map((c, i) => i === editIdx ? { name: trimmed, color: editColor } : c));
    setEditIdx(null);
    setEditName('');
    setEditColor('');
  };

  const cancelEdit = () => { setEditIdx(null); setEditName(''); setEditColor(''); };

  const moveUp = async (idx) => {
    if (idx === 0) return;
    const next = [...cats];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    await persist(next);
  };

  const moveDown = async (idx) => {
    if (idx >= cats.length - 1) return;
    const next = [...cats];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    await persist(next);
  };

  return (
    <div>
      <SectionCard
        title="Task Categories"
        subtitle="Manage the category list shown in the Project Tasks column. Changes apply immediately."
      >
        {/* Add new category */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
          <input
            type="text"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
            placeholder="New category name…"
            maxLength={60}
            style={{ ...inputStyle, width: 220 }}
          />
          {/* Color picker row */}
          <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
            {TASK_CAT_PALETTE.map(c => (
              <button
                key={c}
                onClick={() => setNewColor(c)}
                title={c}
                style={{
                  width: 18, height: 18, borderRadius: 3, background: c, border: 'none',
                  cursor: 'pointer', outline: newColor === c ? '2px solid #333' : '2px solid transparent',
                  outlineOffset: 1, flexShrink: 0
                }}
              />
            ))}
            <input
              type="color"
              value={newColor}
              onChange={e => setNewColor(e.target.value)}
              title="Custom color"
              style={{ width: 22, height: 22, padding: 0, border: '1px solid rgba(0,0,0,0.15)', borderRadius: 3, cursor: 'pointer' }}
            />
          </div>
          <button onClick={handleAdd} disabled={saving || !newName.trim()} style={btnGold}>
            <Plus style={{ width: 10, height: 10, marginRight: 4 }} />Add
          </button>
          <SaveFeedback status={feedback} />
        </div>

        {/* Category list */}
        <div style={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 4, overflow: 'hidden' }}>
          {cats.map((cat, idx) => (
            <div key={idx} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
              borderBottom: idx < cats.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
              background: editIdx === idx ? 'rgba(159,144,42,0.06)' : 'transparent',
            }}>
              {/* Reorder */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                <button onClick={() => moveUp(idx)} disabled={idx === 0 || saving}
                  style={{ ...btnIcon, padding: '1px 4px', opacity: idx === 0 ? 0.25 : 1 }}>
                  <ChevronUp style={{ width: 11, height: 11 }} />
                </button>
                <button onClick={() => moveDown(idx)} disabled={idx >= cats.length - 1 || saving}
                  style={{ ...btnIcon, padding: '1px 4px', opacity: idx >= cats.length - 1 ? 0.25 : 1 }}>
                  <ChevronDown style={{ width: 11, height: 11 }} />
                </button>
              </div>

              {/* Color swatch */}
              {editIdx !== idx && (
                <div style={{
                  width: 12, height: 12, borderRadius: 2, flexShrink: 0,
                  background: cat.color, border: '1px solid rgba(0,0,0,0.15)'
                }} />
              )}

              {/* Name / inline editor */}
              {editIdx === idx ? (
                <div style={{ flex: 1, display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="text"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                    maxLength={60}
                    autoFocus
                    style={{ ...inputStyle, width: 200 }}
                  />
                  {/* Edit color palette */}
                  <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
                    {TASK_CAT_PALETTE.map(c => (
                      <button
                        key={c}
                        onClick={() => setEditColor(c)}
                        title={c}
                        style={{
                          width: 16, height: 16, borderRadius: 2, background: c, border: 'none',
                          cursor: 'pointer', outline: editColor === c ? '2px solid #333' : '2px solid transparent',
                          outlineOffset: 1, flexShrink: 0
                        }}
                      />
                    ))}
                    <input
                      type="color"
                      value={editColor}
                      onChange={e => setEditColor(e.target.value)}
                      title="Custom color"
                      style={{ width: 20, height: 20, padding: 0, border: '1px solid rgba(0,0,0,0.15)', borderRadius: 3, cursor: 'pointer' }}
                    />
                  </div>
                  <button onClick={saveEdit} disabled={saving} style={{ ...btnIcon, color: '#49685a' }}>
                    <Check style={{ width: 13, height: 13 }} />
                  </button>
                  <button onClick={cancelEdit} style={{ ...btnIcon, color: '#934948' }}>
                    <X style={{ width: 13, height: 13 }} />
                  </button>
                </div>
              ) : (
                <span style={{ flex: 1, fontSize: 12, fontWeight: 300, color: '#111' }}>{cat.name}</span>
              )}

              {/* Actions */}
              {editIdx !== idx && (
                <div style={{ display: 'flex', gap: 2 }}>
                  <button onClick={() => startEdit(idx)} disabled={saving} style={btnIcon} title="Edit">
                    <Edit2 style={{ width: 11, height: 11 }} />
                  </button>
                  <button onClick={() => handleDelete(idx)} disabled={saving}
                    style={{ ...btnIcon, color: '#934948' }} title="Delete">
                    <Trash2 style={{ width: 11, height: 11 }} />
                  </button>
                </div>
              )}
            </div>
          ))}
          {cats.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 11, color: '#999', fontStyle: 'italic' }}>
              No categories configured. Add one above.
            </div>
          )}
        </div>

        <p style={{ fontSize: 10, fontWeight: 300, color: '#888', margin: '12px 0 0', fontStyle: 'italic' }}>
          {cats.length} categor{cats.length !== 1 ? 'ies' : 'y'} configured.
          These are used as the category options in the Project Tasks status tracker.
        </p>
      </SectionCard>
    </div>
  );
}


// ============================================================================
// 5. HIRING ROLES / CANDIDATE CATEGORIES
// ============================================================================
function HiringRolesSection({ s }) {
  const roles = s.candidateRoles || [];
  const [newRole, setNewRole] = useState('');
  const [editIdx, setEditIdx] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const showFb = (ok) => {
    setFeedback(ok ? 'saved' : 'error');
    setTimeout(() => setFeedback(null), 2000);
  };

  const persist = async (next) => {
    setSaving(true);
    const res = await s.saveCandidateRoles(next);
    setSaving(false);
    showFb(res.success);
  };

  const isDuplicate = (name, excludeIdx) => {
    const lower = name.trim().toLowerCase();
    return roles.some((r, i) => i !== excludeIdx && r.toLowerCase() === lower);
  };

  const handleAdd = async () => {
    const trimmed = newRole.trim();
    if (!trimmed || trimmed.length > 50) return;
    if (isDuplicate(trimmed, -1)) return;
    await persist([...roles, trimmed]);
    setNewRole('');
  };

  const handleDelete = async (idx) => {
    const next = roles.filter((_, i) => i !== idx);
    await persist(next);
    if (editIdx === idx) { setEditIdx(null); setEditVal(''); }
  };

  const startEdit = (idx) => {
    setEditIdx(idx);
    setEditVal(roles[idx]);
  };

  const saveEdit = async () => {
    const trimmed = editVal.trim();
    if (!trimmed || trimmed.length > 50) return;
    if (isDuplicate(trimmed, editIdx)) return;
    const next = roles.map((r, i) => i === editIdx ? trimmed : r);
    await persist(next);
    setEditIdx(null);
    setEditVal('');
  };

  const cancelEdit = () => { setEditIdx(null); setEditVal(''); };

  const moveUp = async (idx) => {
    if (idx === 0) return;
    const next = [...roles];
    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
    await persist(next);
  };

  const moveDown = async (idx) => {
    if (idx >= roles.length - 1) return;
    const next = [...roles];
    [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
    await persist(next);
  };

  return (
    <div>
      <SectionCard
        title="Hiring Roles / Candidate Categories"
        subtitle="Manage the candidate category list used in the Hiring page. Changes apply immediately."
      >
        {/* Add new role */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
          <input
            type="text"
            value={newRole}
            onChange={e => setNewRole(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
            placeholder="New role name…"
            maxLength={50}
            style={{ ...inputStyle, width: 240 }}
          />
          <button onClick={handleAdd} disabled={saving || !newRole.trim()} style={btnGold}>
            <Plus style={{ width: 10, height: 10, marginRight: 4 }} />Add
          </button>
          <SaveFeedback status={feedback} />
        </div>

        {/* Roles list */}
        <div style={{ border: '1px solid rgba(0,0,0,0.08)', borderRadius: 4, overflow: 'hidden' }}>
          {roles.map((role, idx) => (
            <div key={idx} style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px',
              borderBottom: idx < roles.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none',
              background: editIdx === idx ? 'rgba(159,144,42,0.06)' : 'transparent',
            }}>
              {/* Reorder buttons */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                <button onClick={() => moveUp(idx)} disabled={idx === 0 || saving}
                  style={{ ...btnIcon, padding: '1px 4px', opacity: idx === 0 ? 0.25 : 1 }}>
                  <ChevronUp style={{ width: 11, height: 11 }} />
                </button>
                <button onClick={() => moveDown(idx)} disabled={idx >= roles.length - 1 || saving}
                  style={{ ...btnIcon, padding: '1px 4px', opacity: idx >= roles.length - 1 ? 0.25 : 1 }}>
                  <ChevronDown style={{ width: 11, height: 11 }} />
                </button>
              </div>

              {/* Role name or inline editor */}
              {editIdx === idx ? (
                <div style={{ flex: 1, display: 'flex', gap: 6, alignItems: 'center' }}>
                  <input
                    type="text"
                    value={editVal}
                    onChange={e => setEditVal(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                    maxLength={50}
                    autoFocus
                    style={{ ...inputStyle, width: 220 }}
                  />
                  <button onClick={saveEdit} disabled={saving} style={{ ...btnIcon, color: '#49685a' }}>
                    <Check style={{ width: 13, height: 13 }} />
                  </button>
                  <button onClick={cancelEdit} style={{ ...btnIcon, color: '#934948' }}>
                    <X style={{ width: 13, height: 13 }} />
                  </button>
                </div>
              ) : (
                <span style={{ flex: 1, fontSize: 12, fontWeight: 300, color: '#111' }}>
                  {role}
                </span>
              )}

              {/* Actions */}
              {editIdx !== idx && (
                <div style={{ display: 'flex', gap: 2 }}>
                  <button onClick={() => startEdit(idx)} disabled={saving} style={btnIcon} title="Edit">
                    <Edit2 style={{ width: 11, height: 11 }} />
                  </button>
                  <button onClick={() => handleDelete(idx)} disabled={saving}
                    style={{ ...btnIcon, color: '#934948' }} title="Delete">
                    <Trash2 style={{ width: 11, height: 11 }} />
                  </button>
                </div>
              )}
            </div>
          ))}
          {roles.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', fontSize: 11, color: '#999', fontStyle: 'italic' }}>
              No roles configured. Add one above.
            </div>
          )}
        </div>

        <p style={{ fontSize: 10, fontWeight: 300, color: '#888', margin: '12px 0 0', fontStyle: 'italic' }}>
          {roles.length} role{roles.length !== 1 ? 's' : ''} configured.
          Roles are used as candidate category filters in Hiring.
        </p>
      </SectionCard>
    </div>
  );
}


// ============================================================================
// 6b. INVOICE PDF SETTINGS (office-specific bank details + header images, global signature)
// ============================================================================
function InvoicePdfSection({ s }) {
  const branding = s.invoiceBranding || { offices: { rotterdam: {}, mumbai: {} }, signatureBlock: {} };
  const [local, setLocal] = useState(JSON.parse(JSON.stringify(branding)));
  const [saveStatus, setSaveStatus] = useState(null);
  const [activeOffice, setActiveOffice] = useState('rotterdam');

  // Re-sync local draft when settings load from Supabase (async)
  const brandingRef = useRef(null);
  useEffect(() => {
    const incoming = s.invoiceBranding;
    if (!incoming) return;
    const key = JSON.stringify(incoming);
    if (key !== brandingRef.current) {
      brandingRef.current = key;
      setLocal(JSON.parse(key));
    }
  }, [s.invoiceBranding]);

  // ── Office-specific field setters ──
  const setOfficeBank = (field, value) => setLocal(prev => ({
    ...prev,
    offices: {
      ...prev.offices,
      [activeOffice]: {
        ...prev.offices?.[activeOffice],
        bankDetails: { ...(prev.offices?.[activeOffice]?.bankDetails || {}), [field]: value }
      }
    }
  }));

  // Letterheads and the signature go to the `branding` Storage bucket rather
  // than being inlined as base64 — see utils/brandingImageUpload.js.
  const handleHeaderUpload = async (file) => {
    if (!file) return;
    setSaveStatus('uploading header image…');
    try {
      const headerImage = await uploadBrandingImage(file, `${activeOffice}-header`);
      setLocal(prev => ({
        ...prev,
        offices: {
          ...prev.offices,
          [activeOffice]: {
            ...prev.offices?.[activeOffice],
            headerImage
          }
        }
      }));
      setSaveStatus(null);
    } catch (err) {
      setSaveStatus(`error: ${err.message}`);
    }
  };

  const removeHeader = () => {
    setLocal(prev => ({
      ...prev,
      offices: {
        ...prev.offices,
        [activeOffice]: {
          ...prev.offices?.[activeOffice],
          headerImage: { url: '', dataUrl: '', filename: '' }
        }
      }
    }));
  };

  // ── Signature (global) ──
  const setSig = (field, value) => setLocal(prev => ({
    ...prev,
    signatureBlock: { ...prev.signatureBlock, [field]: value }
  }));

  // ── Common SAC codes (global — reusable list for the invoice SAC picker) ──
  const sacCodes = Array.isArray(local.sacCodes) ? local.sacCodes : [];
  const setSacCode = (idx, field, value) => setLocal(prev => {
    const list = Array.isArray(prev.sacCodes) ? [...prev.sacCodes] : [];
    list[idx] = { ...(list[idx] || { code: '', label: '' }), [field]: value };
    return { ...prev, sacCodes: list };
  });
  const addSacCode = () => setLocal(prev => ({
    ...prev,
    sacCodes: [...(Array.isArray(prev.sacCodes) ? prev.sacCodes : []), { code: '', label: '' }]
  }));
  const removeSacCode = (idx) => setLocal(prev => ({
    ...prev,
    sacCodes: (Array.isArray(prev.sacCodes) ? prev.sacCodes : []).filter((_, i) => i !== idx)
  }));

  const handleSave = async () => {
    setSaveStatus('saving');
    const result = await s.saveInvoiceBranding(local);
    setSaveStatus(result.success ? 'saved' : 'error');
    setTimeout(() => setSaveStatus(null), 2000);
  };

  const bankFields = [
    { key: 'accountName',   label: 'Account Name' },
    { key: 'accountNumber', label: 'Account Number' },
    { key: 'bankName',      label: 'Bank Name' },
    { key: 'branch',        label: 'Branch' },
    { key: 'ifsc',          label: 'IFSC' },
    { key: 'swift',         label: 'SWIFT' },
    { key: 'iban',          label: 'IBAN' },
    { key: 'pan',           label: 'PAN' },
    { key: 'vatOrGst',      label: 'VAT / GST' },
    { key: 'kvk',           label: 'KvK Number' },
    { key: 'udyam',         label: 'Udyam Registration Number' },
  ];

  const officeData = local.offices?.[activeOffice] || {};
  const headerImg = officeData.headerImage || {};

  return (
    <div>
      {/* Office selector tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid rgba(0,0,0,0.1)', marginBottom: 16 }}>
        {['rotterdam', 'mumbai'].map(key => {
          const active = activeOffice === key;
          return (
            <button key={key} onClick={() => setActiveOffice(key)} style={{
              padding: '7px 18px', border: 'none', background: 'none', cursor: 'pointer',
              fontFamily: font, fontSize: 11, fontWeight: active ? 400 : 300,
              color: active ? '#111' : '#666', textTransform: 'capitalize',
              borderBottom: active ? '2px solid #9F902A' : '2px solid transparent',
              marginBottom: -1, transition: 'all 0.15s ease'
            }}>
              {key}
            </button>
          );
        })}
      </div>

      {/* A) Header Image (per office) */}
      <SectionCard title={`Header Image — ${activeOffice.charAt(0).toUpperCase() + activeOffice.slice(1)}`} subtitle="Used at the top of invoice PDFs for this office.">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {resolveBrandingImageSrc(headerImg) ? (
            <>
              <img src={resolveBrandingImageSrc(headerImg)} alt={`${activeOffice} header`}
                style={{ height: 44, maxWidth: 220, border: '1px solid rgba(0,0,0,0.1)', borderRadius: 3, objectFit: 'contain' }} />
              <span style={{ fontSize: 10, color: '#666' }}>{headerImg.filename}</span>
              <button onClick={removeHeader} style={{ ...btnSmall, fontSize: 9, padding: '2px 8px', color: '#934948' }}>Remove</button>
            </>
          ) : (
            <span style={{ fontSize: 10, color: '#999', fontStyle: 'italic' }}>No image set</span>
          )}
          <label style={{ ...btnSmall, fontSize: 9, padding: '3px 10px', cursor: 'pointer' }}>
            <Upload style={{ width: 10, height: 10, marginRight: 3 }} /> Choose…
            <input type="file" accept="image/*" style={{ display: 'none' }}
              onChange={e => handleHeaderUpload(e.target.files?.[0])} />
          </label>
        </div>
      </SectionCard>

      {/* B) Bank Details (per office) */}
      <SectionCard title={`Bank Details — ${activeOffice.charAt(0).toUpperCase() + activeOffice.slice(1)}`} subtitle="Shown in the footer of exported invoice PDFs for this office.">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
          {bankFields.map(f => (
            <FieldLabel key={f.key} label={f.label}>
              <input type="text"
                value={officeData.bankDetails?.[f.key] || ''}
                onChange={e => setOfficeBank(f.key, e.target.value)}
                style={inputStyle} placeholder={f.label} />
            </FieldLabel>
          ))}
        </div>
      </SectionCard>

      {/* C) Signature Block (global — shared across offices) */}
      <SectionCard title="Signature Block" subtitle="Global — rendered at the bottom of all invoice PDFs regardless of office.">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 16px' }}>
          <FieldLabel label="Company Line">
            <input type="text" value={local.signatureBlock?.companyLine || ''}
              onChange={e => setSig('companyLine', e.target.value)}
              style={inputStyle} placeholder="e.g. For Studio BRIO Pvt. Ltd." />
          </FieldLabel>
          <FieldLabel label="Signatory Name">
            <input type="text" value={local.signatureBlock?.signatoryName || ''}
              onChange={e => setSig('signatoryName', e.target.value)}
              style={inputStyle} placeholder="e.g. Robert Verrijt" />
          </FieldLabel>
          <FieldLabel label="Signatory Title">
            <input type="text" value={local.signatureBlock?.signatoryTitle || ''}
              onChange={e => setSig('signatoryTitle', e.target.value)}
              style={inputStyle} placeholder="e.g. Director" />
          </FieldLabel>
        </div>
        <div style={{ marginTop: 12 }}>
          <div style={{ fontFamily: font, fontSize: 10, fontWeight: 400, color: '#666', marginBottom: 4 }}>Signature Image</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {resolveBrandingImageSrc(local.signatureBlock?.signatureImage) ? (
              <>
                <img src={resolveBrandingImageSrc(local.signatureBlock.signatureImage)} alt="Signature"
                  style={{ height: 48, maxWidth: 180, border: '1px solid rgba(0,0,0,0.1)', borderRadius: 3, objectFit: 'contain', background: '#fff' }} />
                <span style={{ fontSize: 10, color: '#666' }}>{local.signatureBlock.signatureImage.filename}</span>
                <button onClick={() => setLocal(prev => ({
                  ...prev, signatureBlock: { ...prev.signatureBlock, signatureImage: { url: '', dataUrl: '', filename: '' } }
                }))} style={{ ...btnSmall, fontSize: 9, padding: '2px 8px', color: '#934948' }}>Remove</button>
              </>
            ) : (
              <span style={{ fontSize: 10, color: '#999', fontStyle: 'italic' }}>No signature image</span>
            )}
            <label style={{ ...btnSmall, fontSize: 9, padding: '3px 10px', cursor: 'pointer' }}>
              <Upload style={{ width: 10, height: 10, marginRight: 3 }} /> Choose…
              <input type="file" accept="image/*" style={{ display: 'none' }}
                onChange={async e => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  setSaveStatus('uploading signature image…');
                  try {
                    const signatureImage = await uploadBrandingImage(file, 'signature');
                    setLocal(prev => ({
                      ...prev, signatureBlock: { ...prev.signatureBlock, signatureImage }
                    }));
                    setSaveStatus(null);
                  } catch (err) {
                    setSaveStatus(`error: ${err.message}`);
                  }
                }} />
            </label>
          </div>
        </div>
      </SectionCard>

      {/* D) Common SAC Codes (global — reusable picker list for invoices) */}
      <SectionCard title="Common SAC Codes" subtitle="Reusable Service Accounting Codes. Pick one per invoice in the invoice editor; the client's SAC is used as the fallback when none is chosen.">
        <div style={{ display: 'grid', gap: 8 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '150px 1fr auto', gap: 10, alignItems: 'center' }}>
            <div style={{ fontFamily: font, fontSize: 10, fontWeight: 400, color: '#666' }}>SAC Code</div>
            <div style={{ fontFamily: font, fontSize: 10, fontWeight: 400, color: '#666' }}>Service description</div>
            <div />
          </div>
          {sacCodes.length === 0 && (
            <div style={{ fontSize: 10, color: '#999', fontStyle: 'italic' }}>
              No SAC codes yet. Add the codes your firm uses — e.g. 998321 (Architectural services), 998391 (Interior / specialty design).
            </div>
          )}
          {sacCodes.map((row, idx) => (
            <div key={idx} style={{ display: 'grid', gridTemplateColumns: '150px 1fr auto', gap: 10, alignItems: 'center' }}>
              <input type="text" value={row?.code || ''} onChange={e => setSacCode(idx, 'code', e.target.value)}
                style={inputStyle} placeholder="998321" />
              <input type="text" value={row?.label || ''} onChange={e => setSacCode(idx, 'label', e.target.value)}
                style={inputStyle} placeholder="Architectural services" />
              <button onClick={() => removeSacCode(idx)} style={{ ...btnSmall, color: '#934948', padding: '4px 12px' }}>Remove</button>
            </div>
          ))}
          <div>
            <button onClick={addSacCode} style={btnSmall}>+ Add SAC code</button>
          </div>
        </div>
      </SectionCard>

      {/* Save */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginTop: 8 }}>
        <SaveFeedback status={saveStatus} />
        <button onClick={handleSave} style={btnGold} disabled={saveStatus === 'saving'}>
          <Save style={{ width: 11, height: 11, marginRight: 4 }} />
          {saveStatus === 'saving' ? 'Saving…' : 'Save Invoice PDF Settings'}
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// STORAGE — SharePoint Document Library Configuration
// ============================================================================
// Admin configures the SharePoint site and document library where invoice PDFs
// are archived during the month-end finance workflow.
//
// NOTE: This is NOT used in the daily supervisor invoice workflow (which is
// fast: create → download → email). SharePoint is for the monthly archive
// workflow on the Invoices page: "Archive Previous Month Tax Invoices".
//
// The config is stored in app_settings as key 'sharepointStorage':
//   { siteId, driveId, siteName, libraryName, baseFolderPath }
//
// Prerequisites:
//   1. Azure Entra app registration must include Sites.ReadWrite.All scope
//   2. Admin must grant tenant consent for the app
//   3. Admin must connect Microsoft 365 in the Calendar settings
//   4. Then configure SharePoint site/library IDs here
// ============================================================================
function StorageSection({ s }) {
  const config = s.sharepointStorage || {};
  const [siteId, setSiteId] = useState(config.siteId || '');
  const [driveId, setDriveId] = useState(config.driveId || '');
  const [siteName, setSiteName] = useState(config.siteName || '');
  const [libraryName, setLibraryName] = useState(config.libraryName || '');
  const [baseFolderPath, setBaseFolderPath] = useState(config.baseFolderPath || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const isConfigured = !!(siteId && driveId);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    const result = await s.saveSharepointStorage({
      siteId: siteId.trim(),
      driveId: driveId.trim(),
      siteName: siteName.trim(),
      libraryName: libraryName.trim(),
      baseFolderPath: baseFolderPath.trim(),
    });
    setSaving(false);
    if (result?.error) {
      setError(result.error);
    } else {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  const fieldStyle = { ...inputStyle, marginBottom: 0 };
  const labelSt = { fontSize: 10, fontWeight: 400, color: '#666', display: 'block', marginBottom: 3 };

  return (
    <div>
      <h3 style={{ fontSize: 13, fontWeight: 400, margin: '0 0 10px', color: '#111' }}>
        SharePoint Document Library
      </h3>
      <p style={{ fontSize: 10, fontStyle: 'italic', color: '#666', margin: '0 0 14px' }}>
        Configure the Microsoft 365 SharePoint site and document library where invoice PDFs are archived
        during the monthly finance workflow. This does not affect the daily invoice sending workflow.
      </p>

      {/* Prerequisites checklist */}
      <div style={{ padding: '10px 14px', background: '#fafafa', border: '1px solid #e5e5e5', marginBottom: 14, fontSize: 10, color: '#555' }}>
        <div style={{ fontWeight: 500, marginBottom: 6 }}>Prerequisites</div>
        <ol style={{ margin: 0, paddingLeft: 18 }}>
          <li>Azure Entra app registration must include <code>Sites.ReadWrite.All</code> scope</li>
          <li>A tenant admin must grant consent for the app</li>
          <li>An admin must connect Microsoft 365 (Calendar settings)</li>
          <li>Get the Site ID and Drive ID from Microsoft Graph Explorer or admin portal</li>
        </ol>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={labelSt}>Site Name <span style={{ color: '#999' }}>(display only)</span></label>
          <input style={fieldStyle} value={siteName} onChange={e => setSiteName(e.target.value)}
            placeholder="e.g. Architecture BRIO" />
        </div>
        <div>
          <label style={labelSt}>Library Name <span style={{ color: '#999' }}>(display only)</span></label>
          <input style={fieldStyle} value={libraryName} onChange={e => setLibraryName(e.target.value)}
            placeholder="e.g. Shared Documents" />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={labelSt}>Graph Site ID <span style={{ color: '#c00' }}>*</span></label>
          <input style={fieldStyle} value={siteId} onChange={e => setSiteId(e.target.value)}
            placeholder="contoso.sharepoint.com,guid1,guid2" />
          <div style={{ fontSize: 9, color: '#999', marginTop: 2 }}>
            From Graph Explorer: GET /sites/{'<hostname>'}:/sites/{'<site-path>'} → id
          </div>
        </div>
        <div>
          <label style={labelSt}>Graph Drive ID <span style={{ color: '#c00' }}>*</span></label>
          <input style={fieldStyle} value={driveId} onChange={e => setDriveId(e.target.value)}
            placeholder="b!abcdef1234567890..." />
          <div style={{ fontSize: 9, color: '#999', marginTop: 2 }}>
            From Graph Explorer: GET /sites/{'<site-id>'}/drives → select the document library → id
          </div>
        </div>
        <div>
          <label style={labelSt}>Base Folder Path <span style={{ color: '#999' }}>(optional prefix)</span></label>
          <input style={fieldStyle} value={baseFolderPath} onChange={e => setBaseFolderPath(e.target.value)}
            placeholder="e.g. Finance/Invoices (leave blank for root)" />
          <div style={{ fontSize: 9, color: '#999', marginTop: 2 }}>
            Invoices will be stored at: {baseFolderPath ? `${baseFolderPath}/` : ''}Invoices/ClientName/ProjectCode/Year/Tax|Proforma/
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={handleSave} disabled={saving || !siteId.trim() || !driveId.trim()}
          style={{ ...btnGold, opacity: saving || !siteId.trim() || !driveId.trim() ? 0.5 : 1 }}>
          <Save style={{ width: 10, height: 10, marginRight: 4 }} />
          {saving ? 'Saving…' : 'Save Configuration'}
        </button>
        {saved && <span style={{ fontSize: 10, color: '#16a34a' }}>Saved successfully</span>}
        {error && <span style={{ fontSize: 10, color: '#c00' }}>{error}</span>}
      </div>

      {/* Status */}
      <div style={{ marginTop: 14, padding: '8px 14px', fontSize: 10, border: '1px solid #e5e5e5', background: isConfigured ? '#f0fdf4' : '#fef2f2' }}>
        <strong>Status:</strong>{' '}
        {isConfigured ? (
          <span style={{ color: '#16a34a' }}>Configured — monthly archive workflow will upload to SharePoint</span>
        ) : (
          <span style={{ color: '#666' }}>Not configured — monthly archive workflow will generate PDFs locally only (no upload)</span>
        )}
      </div>

      {/* Dropbox Config */}
      <DropboxConfigSection s={s} />
    </div>
  );
}

// ============================================================================
// DROPBOX CONFIGURATION (reimbursement receipt upload)
// ============================================================================
function DropboxConfigSection({ s }) {
  const config = s.dropboxConfig || {};
  const [accessToken, setAccessToken] = useState(config.accessToken || '');
  const [refreshToken, setRefreshToken] = useState(config.refreshToken || '');
  const [appKey, setAppKey] = useState(config.appKey || '');
  const [appSecret, setAppSecret] = useState(config.appSecret || '');
  const [baseFolderPath, setBaseFolderPath] = useState(config.baseFolderPath || '/BRIO/Reimbursements');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [showSecrets, setShowSecrets] = useState(false);
  const [authCode, setAuthCode] = useState('');
  const [exchanging, setExchanging] = useState(false);
  const [exchangeError, setExchangeError] = useState('');
  const [exchangeSuccess, setExchangeSuccess] = useState(false);

  const isConfigured = !!(accessToken && refreshToken && appKey && appSecret);

  const handleAuthorize = () => {
    if (!appKey.trim()) {
      setExchangeError('Enter your App Key first');
      return;
    }
    window.open(
      `https://www.dropbox.com/oauth2/authorize?client_id=${appKey.trim()}&response_type=code&token_access_type=offline`,
      '_blank'
    );
  };

  const handleExchangeCode = async () => {
    if (!authCode.trim() || !appKey.trim() || !appSecret.trim()) {
      setExchangeError('App Key, App Secret, and Authorization Code are all required');
      return;
    }
    setExchanging(true);
    setExchangeError('');
    setExchangeSuccess(false);
    try {
      const resp = await fetch('https://api.dropboxapi.com/oauth2/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          code: authCode.trim(),
          grant_type: 'authorization_code',
          client_id: appKey.trim(),
          client_secret: appSecret.trim(),
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data.error_description || data.error_summary || `HTTP ${resp.status}`);
      }
      if (data.access_token) setAccessToken(data.access_token);
      if (data.refresh_token) setRefreshToken(data.refresh_token);
      setAuthCode('');
      setExchangeSuccess(true);
      setTimeout(() => setExchangeSuccess(false), 5000);
    } catch (err) {
      setExchangeError(err.message || 'Token exchange failed');
    } finally {
      setExchanging(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    const result = await s.saveDropboxConfig({
      accessToken: accessToken.trim(),
      refreshToken: refreshToken.trim(),
      appKey: appKey.trim(),
      appSecret: appSecret.trim(),
      baseFolderPath: baseFolderPath.trim(),
    });
    setSaving(false);
    if (result?.error) {
      setError(result.error);
    } else {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  const fieldStyle = { ...inputStyle, marginBottom: 0 };
  const labelSt = { fontSize: 10, fontWeight: 400, color: '#666', display: 'block', marginBottom: 3 };
  const secretFieldStyle = { ...fieldStyle, fontFamily: showSecrets ? 'inherit' : 'monospace' };

  return (
    <div style={{ marginTop: 28, borderTop: '1px solid #e5e5e5', paddingTop: 20 }}>
      <h3 style={{ fontSize: 13, fontWeight: 400, margin: '0 0 10px', color: '#111' }}>
        Dropbox — Reimbursement Receipts
      </h3>
      <p style={{ fontSize: 10, fontStyle: 'italic', color: '#666', margin: '0 0 14px' }}>
        Configure Dropbox API credentials for uploading reimbursement receipts.
        Receipts are uploaded automatically when a team member submits a reimbursement with an attachment.
      </p>

      {/* Prerequisites */}
      <div style={{ padding: '10px 14px', background: '#fafafa', border: '1px solid #e5e5e5', marginBottom: 14, fontSize: 10, color: '#555' }}>
        <div style={{ fontWeight: 500, marginBottom: 6 }}>Setup steps</div>
        <ol style={{ margin: 0, paddingLeft: 18 }}>
          <li>Create a Dropbox app at dropbox.com/developers/apps (Scoped access, Full Dropbox)</li>
          <li>Enable permissions: files.content.write, files.content.read, sharing.write, sharing.read</li>
          <li>Generate an offline refresh token via the OAuth2 authorization code flow</li>
          <li>Enter the App Key, App Secret, and tokens below</li>
        </ol>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <button onClick={() => setShowSecrets(!showSecrets)}
          style={{ ...btnSmall, fontSize: 9 }}>
          <Eye style={{ width: 9, height: 9, marginRight: 3 }} />
          {showSecrets ? 'Hide secrets' : 'Show secrets'}
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={labelSt}>App Key <span style={{ color: '#c00' }}>*</span></label>
          <input style={secretFieldStyle} value={appKey} onChange={e => setAppKey(e.target.value)}
            type={showSecrets ? 'text' : 'password'} placeholder="e.g. abc123xyz" />
        </div>
        <div>
          <label style={labelSt}>App Secret <span style={{ color: '#c00' }}>*</span></label>
          <input style={secretFieldStyle} value={appSecret} onChange={e => setAppSecret(e.target.value)}
            type={showSecrets ? 'text' : 'password'} placeholder="e.g. secret456" />
        </div>
      </div>

      {/* Connect to Dropbox — token exchange flow */}
      <div style={{ padding: '12px 14px', background: '#f8f6ee', border: '1px solid #e5e0c0', marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 500, marginBottom: 8, color: '#333' }}>
          <Link2 style={{ width: 11, height: 11, marginRight: 4, verticalAlign: -1 }} />
          Connect to Dropbox
        </div>
        <div style={{ fontSize: 10, color: '#666', marginBottom: 10 }}>
          Step 1: Enter App Key and App Secret above, then click "Authorize". Step 2: Allow access in Dropbox. Step 3: Paste the authorization code below and click "Exchange".
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
          <div style={{ flex: 1 }}>
            <label style={labelSt}>Authorization Code</label>
            <input style={fieldStyle} value={authCode} onChange={e => setAuthCode(e.target.value)}
              placeholder="Paste code from Dropbox here" />
          </div>
          <button onClick={handleAuthorize} disabled={!appKey.trim()}
            style={{ ...btnSmall, fontSize: 9, whiteSpace: 'nowrap', opacity: !appKey.trim() ? 0.5 : 1 }}>
            <Globe style={{ width: 9, height: 9, marginRight: 3 }} />
            1. Authorize
          </button>
          <button onClick={handleExchangeCode} disabled={exchanging || !authCode.trim() || !appKey.trim() || !appSecret.trim()}
            style={{ ...btnGold, fontSize: 9, whiteSpace: 'nowrap', opacity: (exchanging || !authCode.trim() || !appKey.trim() || !appSecret.trim()) ? 0.5 : 1 }}>
            <RefreshCw style={{ width: 9, height: 9, marginRight: 3, animation: exchanging ? 'spin 1s linear infinite' : 'none' }} />
            {exchanging ? 'Exchanging…' : '2. Exchange for Tokens'}
          </button>
        </div>
        {exchangeSuccess && (
          <div style={{ fontSize: 10, color: '#16a34a', marginTop: 6 }}>
            <Check style={{ width: 10, height: 10, marginRight: 3, verticalAlign: -1 }} />
            Tokens obtained successfully. Click "Save Dropbox Configuration" below to persist.
          </div>
        )}
        {exchangeError && (
          <div style={{ fontSize: 10, color: '#c00', marginTop: 6 }}>
            <AlertTriangle style={{ width: 10, height: 10, marginRight: 3, verticalAlign: -1 }} />
            {exchangeError}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12, marginBottom: 14 }}>
        <div>
          <label style={labelSt}>Access Token <span style={{ color: '#c00' }}>*</span></label>
          <input style={secretFieldStyle} value={accessToken} onChange={e => setAccessToken(e.target.value)}
            type={showSecrets ? 'text' : 'password'} placeholder="sl.xxxxx..." />
          <div style={{ fontSize: 9, color: '#999', marginTop: 2 }}>
            Short-lived token from the OAuth2 token exchange. Auto-refreshed using the refresh token.
          </div>
        </div>
        <div>
          <label style={labelSt}>Refresh Token <span style={{ color: '#c00' }}>*</span></label>
          <input style={secretFieldStyle} value={refreshToken} onChange={e => setRefreshToken(e.target.value)}
            type={showSecrets ? 'text' : 'password'} placeholder="xxxxx..." />
          <div style={{ fontSize: 9, color: '#999', marginTop: 2 }}>
            Long-lived offline refresh token. Used to automatically obtain new access tokens.
          </div>
        </div>
        <div>
          <label style={labelSt}>Base Folder Path</label>
          <input style={fieldStyle} value={baseFolderPath} onChange={e => setBaseFolderPath(e.target.value)}
            placeholder="/BRIO/Reimbursements" />
          <div style={{ fontSize: 9, color: '#999', marginTop: 2 }}>
            Receipts will be stored at: {baseFolderPath || '/BRIO/Reimbursements'}/{'{projectCode}'}/{'{YYYY-MM}'}/{'{filename}'}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={handleSave} disabled={saving || !isConfigured}
          style={{ ...btnGold, opacity: saving || !isConfigured ? 0.5 : 1 }}>
          <Save style={{ width: 10, height: 10, marginRight: 4 }} />
          {saving ? 'Saving…' : 'Save Dropbox Configuration'}
        </button>
        {saved && <span style={{ fontSize: 10, color: '#16a34a' }}>Saved successfully</span>}
        {error && <span style={{ fontSize: 10, color: '#c00' }}>{error}</span>}
      </div>

      {/* Status */}
      <div style={{ marginTop: 14, padding: '8px 14px', fontSize: 10, border: '1px solid #e5e5e5', background: isConfigured ? '#f0fdf4' : '#fef2f2' }}>
        <strong>Status:</strong>{' '}
        {isConfigured ? (
          <span style={{ color: '#16a34a' }}>Configured — reimbursement receipts will be uploaded to Dropbox</span>
        ) : (
          <span style={{ color: '#666' }}>Not configured — reimbursement receipt upload is disabled</span>
        )}
      </div>

      <DropboxFolderWatchSection isConfigured={isConfigured} />
    </div>
  );
}

// ============================================================================
// DROPBOX FOLDER WATCH (press-kit structure alerts)
// ============================================================================
// Dropbox pushes a notification whenever anything in the account changes; the
// dropbox-webhook function turns that into a re-audit of the projects whose
// folders moved, and the drift shows up on the Comms page.
//
// There is no API for registering a webhook — the URI is typed into the Dropbox
// App Console once, by a person. So this panel exists to hand over that URI, and
// then to answer the only question that matters afterwards: is anything actually
// arriving through it?
function DropboxFolderWatchSection({ isConfigured }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'scan' | 'baseline'
  const [result, setResult] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    fetchDropboxWatchState()
      .then(setState)
      .catch(e => setError(e.message || String(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (mode) => {
    setBusy(mode);
    setError('');
    setResult('');
    try {
      const json = await runDropboxWatchScan(mode);
      setResult(json.skipped
        // Nothing was read, so the usual "0 changes, 0 alerts" line would read
        // as all-clear when in fact this run never looked. The scan already
        // running will cover the same queue.
        ? 'A scan was already running, so this one stood down. Its results will appear when it finishes.'
        : json.baselined
        ? 'Watching from now. Changes made before this moment were never visible to Dropbox\u2019s change feed.'
        : [
            `Read ${json.entries ?? 0} change(s) \u00b7 ${json.audited ?? 0} project(s) re-checked \u00b7 ${json.opened ?? 0} new alert(s), ${json.resolved ?? 0} cleared.`,
            // Both mean the run stopped short of the full picture, so saying
            // nothing would read as "all clear" when it is not.
            json.truncated ? 'There are more changes to read — run it again.' : '',
            json.deferred?.length ? `${json.deferred.length} project(s) queued for the next run.` : '',
          ].filter(Boolean).join(' '));
      load();
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(null);
    }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(DROPBOX_WEBHOOK_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Clipboard is blocked in some browsers/contexts; the URL is on screen
      // and selectable either way, so this is not worth an error message.
    }
  };

  const watching = !!state?.watching;
  const when = iso => (iso ? new Date(iso).toLocaleString() : 'never');

  return (
    <div style={{ marginTop: 24, borderTop: '1px solid #e5e5e5', paddingTop: 18 }}>
      <h3 style={{ fontSize: 13, fontWeight: 400, margin: '0 0 10px', color: '#111' }}>
        Dropbox — Press folder change alerts
      </h3>
      <p style={{ fontSize: 10, fontStyle: 'italic', color: '#666', margin: '0 0 14px' }}>
        Without this, the app only learns that a press folder was renamed, moved or deleted
        when somebody presses “Check Dropbox structure”. With it, Dropbox says so within
        seconds and the drift appears on the Comms page.
      </p>

      <div style={{ padding: '10px 14px', background: '#fafafa', border: '1px solid #e5e5e5', marginBottom: 14, fontSize: 10, color: '#555' }}>
        <div style={{ fontWeight: 500, marginBottom: 6 }}>Setup steps</div>
        <ol style={{ margin: 0, paddingLeft: 18 }}>
          <li>Open the same app at dropbox.com/developers/apps → Settings → Webhooks</li>
          <li>Paste the URI below and press Add. Dropbox verifies it immediately.</li>
          <li>Come back here and press “Start watching” to take the first cursor.</li>
        </ol>
      </div>

      <label style={{ fontSize: 10, fontWeight: 400, color: '#666', display: 'block', marginBottom: 3 }}>Webhook URI</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 14 }}>
        <input readOnly value={DROPBOX_WEBHOOK_URL} onFocus={e => e.target.select()}
          style={{ ...inputStyle, marginBottom: 0, fontFamily: 'monospace' }} />
        <button onClick={copyUrl} style={{ ...btnSmall, fontSize: 9, whiteSpace: 'nowrap' }}>
          <Copy style={{ width: 9, height: 9, marginRight: 3 }} />
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <button onClick={() => run('baseline')} disabled={!isConfigured || !!busy}
          style={{ ...(watching ? btnSmall : btnGold), fontSize: 9, opacity: (!isConfigured || busy) ? 0.5 : 1 }}>
          <RefreshCw style={{ width: 9, height: 9, marginRight: 3, animation: busy === 'baseline' ? 'spin 1s linear infinite' : 'none' }} />
          {busy === 'baseline' ? 'Starting…' : (watching ? 'Restart from now' : 'Start watching')}
        </button>
        <button onClick={() => run('scan')} disabled={!isConfigured || !watching || !!busy}
          style={{ ...btnSmall, fontSize: 9, opacity: (!isConfigured || !watching || busy) ? 0.5 : 1 }}>
          <RefreshCw style={{ width: 9, height: 9, marginRight: 3, animation: busy === 'scan' ? 'spin 1s linear infinite' : 'none' }} />
          {busy === 'scan' ? 'Checking…' : 'Check for changes now'}
        </button>
        {result && <span style={{ fontSize: 10, color: '#16a34a' }}>{result}</span>}
        {error && <span style={{ fontSize: 10, color: '#c00' }}>{error}</span>}
      </div>

      <div style={{ padding: '8px 14px', fontSize: 10, border: '1px solid #e5e5e5', background: watching ? '#f0fdf4' : '#fef2f2', lineHeight: 1.7 }}>
        {loading ? 'Reading status…' : (
          <>
            <div>
              <strong>Status:</strong>{' '}
              {watching
                ? <span style={{ color: '#16a34a' }}>Watching since {when(state?.cursor?.baselined_at)}</span>
                : <span style={{ color: '#666' }}>Not watching — press “Start watching” once the URI is registered</span>}
            </div>
            {/* The distinction that matters when this looks broken: a URI that
                was never registered shows notifications "never", while a
                registered one that cannot be scanned shows a recent
                notification and a stale scan. */}
            <div><strong>Last notification from Dropbox:</strong> {when(state?.cursor?.last_event_at)}</div>
            <div><strong>Last successful scan:</strong> {when(state?.cursor?.last_scan_at)}</div>
            {state?.pending > 0 && (
              <div style={{ color: '#7A5C00' }}>
                {state.pending} notification(s) still to process — the hourly retry will pick them up.
              </div>
            )}
            {state?.cursor?.last_error && (
              <div style={{ color: '#c00' }}>
                <AlertTriangle style={{ width: 10, height: 10, marginRight: 3, verticalAlign: -1 }} />
                {state.cursor.last_error}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// EXCEL EXPORT STYLING
// ============================================================================
function ExcelExportSection({ s }) {
  const defaults = {
    style1: { fontFamily: 'Itcavantgardestd-bk', fontSize: 10 },
    style2: { fontFamily: 'Itcavantgardestd-bk', fontSize: 12 },
    style3: { fontFamily: 'Itcavantgardestd-bk', fontSize: 16 },
  };
  const current = s.excelExportStyles || defaults;
  const [draft, setDraft] = useState({
    style1: { ...defaults.style1, ...current.style1 },
    style2: { ...defaults.style2, ...current.style2 },
    style3: { ...defaults.style3, ...current.style3 },
  });
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // 'saved' | 'error'

  const update = (key, field, value) => {
    setDraft(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: field === 'fontSize' ? (Number(value) || 0) : value }
    }));
  };

  const save = async () => {
    setSaving(true);
    const res = await s.saveExcelExportStyles(draft);
    setFeedback(res.success ? 'saved' : 'error');
    setSaving(false);
    setTimeout(() => setFeedback(null), 2500);
  };

  const reset = () => {
    setDraft({ ...defaults });
  };

  const STYLE_LABELS = { style1: 'Style 1 — Body / Detail', style2: 'Style 2 — Subheading', style3: 'Style 3 — Title / Header' };

  return (
    <SectionCard title="Excel Export Styling" subtitle="Configure reusable font styles applied to all XLSX exports">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {['style1', 'style2', 'style3'].map(key => (
          <div key={key} style={{
            padding: 14, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 5,
            background: '#fafaf8'
          }}>
            <div style={{ fontFamily: font, fontSize: 11, fontWeight: 400, color: '#111', marginBottom: 10 }}>
              {STYLE_LABELS[key]}
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <FieldLabel label="Font Family">
                  <input
                    type="text"
                    value={draft[key].fontFamily}
                    onChange={e => update(key, 'fontFamily', e.target.value)}
                    style={inputStyle}
                    placeholder="e.g. Itcavantgardestd-bk"
                  />
                </FieldLabel>
              </div>
              <div style={{ width: 80 }}>
                <FieldLabel label="Font Size">
                  <input
                    type="number"
                    min={6}
                    max={72}
                    value={draft[key].fontSize}
                    onChange={e => update(key, 'fontSize', e.target.value)}
                    style={{ ...inputStyle, textAlign: 'center' }}
                  />
                </FieldLabel>
              </div>
            </div>
            <div style={{
              marginTop: 8, fontFamily: draft[key].fontFamily || 'sans-serif',
              fontSize: draft[key].fontSize || 12, color: '#333', padding: '4px 0',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
            }}>
              Preview: The quick brown fox
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
        <button onClick={save} disabled={saving} style={btnGold}>
          {saving ? 'Saving…' : 'Save Styles'}
        </button>
        <button onClick={reset} style={btnSmall}>
          Reset to Defaults
        </button>
        {feedback === 'saved' && (
          <span style={{ fontFamily: font, fontSize: 10, color: '#3a7d44', fontWeight: 400 }}>
            <Check style={{ width: 10, height: 10, marginRight: 3, verticalAlign: -1 }} />Saved
          </span>
        )}
        {feedback === 'error' && (
          <span style={{ fontFamily: font, fontSize: 10, color: '#934948', fontWeight: 400 }}>
            <AlertTriangle style={{ width: 10, height: 10, marginRight: 3, verticalAlign: -1 }} />Save failed
          </span>
        )}
      </div>
    </SectionCard>
  );
}

// ============================================================================
// SCOPE PDF EXPORT STYLING
// ============================================================================
function ScopePdfExportSection({ s }) {
  const defaults = {
    body:    { fontFamily: 'Raleway', fontSize: 11 },
    heading: { fontFamily: 'Raleway', fontSize: 12 },
    title:   { fontFamily: 'Raleway', fontSize: 18 },
    notes:   { fontFamily: 'Raleway', fontSize: 10 },
  };
  const current = s.scopePdfStyles || defaults;
  const [draft, setDraft] = useState({
    body:    { ...defaults.body,    ...current.body },
    heading: { ...defaults.heading, ...current.heading },
    title:   { ...defaults.title,   ...current.title },
    notes:   { ...defaults.notes,   ...current.notes },
  });
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const update = (key, field, value) => {
    setDraft(prev => ({
      ...prev,
      [key]: { ...prev[key], [field]: field === 'fontSize' ? (Number(value) || 0) : value }
    }));
  };

  const save = async () => {
    setSaving(true);
    const res = await s.saveScopePdfStyles(draft);
    setFeedback(res.success ? 'saved' : 'error');
    setSaving(false);
    setTimeout(() => setFeedback(null), 2500);
  };

  const reset = () => { setDraft({ ...defaults }); };

  const STYLE_LABELS = {
    body:    'Body — Stage content & deliverables',
    heading: 'Heading — Stage titles',
    title:   'Title — Document title',
    notes:   'Notes — Stage notes & fine print',
  };

  return (
    <SectionCard title="Scope PDF Export Styling" subtitle="Configure font styles for the Scope of Work PDF export. Uses the same Invoice header image (IN office).">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {['title', 'heading', 'body', 'notes'].map(key => (
          <div key={key} style={{
            padding: 14, border: '1px solid rgba(0,0,0,0.08)', borderRadius: 5,
            background: '#fafaf8'
          }}>
            <div style={{ fontFamily: font, fontSize: 11, fontWeight: 400, color: '#111', marginBottom: 10 }}>
              {STYLE_LABELS[key]}
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <FieldLabel label="Font Family">
                  <input
                    type="text"
                    value={draft[key].fontFamily}
                    onChange={e => update(key, 'fontFamily', e.target.value)}
                    style={inputStyle}
                    placeholder="e.g. Raleway"
                  />
                </FieldLabel>
              </div>
              <div style={{ width: 80 }}>
                <FieldLabel label="Font Size (pt)">
                  <input
                    type="number"
                    min={6}
                    max={72}
                    value={draft[key].fontSize}
                    onChange={e => update(key, 'fontSize', e.target.value)}
                    style={{ ...inputStyle, textAlign: 'center' }}
                  />
                </FieldLabel>
              </div>
            </div>
            <div style={{
              marginTop: 8, fontFamily: draft[key].fontFamily || 'sans-serif',
              fontSize: draft[key].fontSize || 12, color: '#333', padding: '4px 0',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
            }}>
              Preview: The quick brown fox
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 16 }}>
        <button onClick={save} disabled={saving} style={btnGold}>
          {saving ? 'Saving…' : 'Save Styles'}
        </button>
        <button onClick={reset} style={btnSmall}>
          Reset to Defaults
        </button>
        {feedback === 'saved' && (
          <span style={{ fontFamily: font, fontSize: 10, color: '#3a7d44', fontWeight: 400 }}>
            <Check style={{ width: 10, height: 10, marginRight: 3, verticalAlign: -1 }} />Saved
          </span>
        )}
        {feedback === 'error' && (
          <span style={{ fontFamily: font, fontSize: 10, color: '#934948', fontWeight: 400 }}>
            <AlertTriangle style={{ width: 10, height: 10, marginRight: 3, verticalAlign: -1 }} />Save failed
          </span>
        )}
      </div>

      <div style={{ marginTop: 16, fontSize: 10, color: 'var(--brio-text-muted)', fontStyle: 'italic' }}>
        Header image is sourced from the Invoice PDF branding (IN office). Margins: 1.5cm top/bottom below header, 2cm left/right.
      </div>
    </SectionCard>
  );
}

// ============================================================================
// SOW MARKUP RENDERER
// ============================================================================
// Renders simple markup: **bold**, *italic*, newlines → <br/>
// Also resolves {{token}} quantity placeholders when deliverables provided.
function SowMarkupText({ text, deliverables, style: extraStyle }) {
  if (!text) return null;
  // Resolve quantity tokens if deliverables provided
  let resolved = text;
  if (deliverables && deliverables.length > 0) {
    const tokenMap = {};
    deliverables.forEach(d => {
      if (d.quantityToken && d.quantity) tokenMap[d.quantityToken] = d.quantity;
    });
    resolved = resolved.replace(/\{\{([^}]+)\}\}/g, (match, token) => {
      const val = tokenMap[token.trim()];
      return val !== undefined ? val : match;
    });
  }
  // Parse markup into React elements
  const lines = resolved.split('\n');
  const elements = [];
  lines.forEach((line, li) => {
    if (li > 0) elements.push(<br key={`br-${li}`} />);
    // Split by **bold** and *italic* patterns
    const parts = line.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
    parts.forEach((part, pi) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        elements.push(<strong key={`${li}-${pi}`}>{part.slice(2, -2)}</strong>);
      } else if (part.startsWith('*') && part.endsWith('*') && part.length > 2) {
        elements.push(<em key={`${li}-${pi}`}>{part.slice(1, -1)}</em>);
      } else {
        elements.push(<span key={`${li}-${pi}`}>{part}</span>);
      }
    });
  });
  return <div style={{ fontSize: 11, lineHeight: 1.6, fontWeight: 300, color: 'var(--brio-text-primary)', ...extraStyle }}>{elements}</div>;
}


// ============================================================================
// MOM TEMPLATES SECTION
// ============================================================================
function MOMTemplatesSection() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [editingId, setEditingId] = useState(null); // null = list view; 'new' = creating; number = editing
  const [editName, setEditName]   = useState('');
  const editorRef = useRef(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    const { data, error: err } = await supabase
      .from('mom_templates')
      .select('id, name, content_html, is_default, created_at')
      .is('project_id', null)
      .order('created_at', { ascending: true });
    setLoading(false);
    if (err) { setError(err.message); return; }
    setTemplates(data || []);
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const openNew = () => {
    setEditingId('new');
    setEditName('New Template');
    setTimeout(() => {
      if (editorRef.current) editorRef.current.innerHTML = DEFAULT_TEMPLATE_HTML;
    }, 0);
  };

  const openEdit = (tpl) => {
    setEditingId(tpl.id);
    setEditName(tpl.name);
    setTimeout(() => {
      if (editorRef.current) editorRef.current.innerHTML = tpl.content_html || '';
    }, 0);
  };

  const handleSave = async () => {
    if (!editName.trim()) return;
    const contentHtml = editorRef.current ? editorRef.current.innerHTML : '';
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) { setError('Not signed in'); return; }
    if (editingId === 'new') {
      const { data, error: err } = await supabase.from('mom_templates').insert({
        project_id: null, name: editName.trim(), content_html: contentHtml,
        is_default: templates.length === 0, created_by: session.user.id,
      }).select().single();
      if (err) { setError(err.message); return; }
      setTemplates(prev => [...prev, data]);
    } else {
      const { error: err } = await supabase.from('mom_templates')
        .update({ name: editName.trim(), content_html: contentHtml, updated_at: new Date().toISOString() })
        .eq('id', editingId);
      if (err) { setError(err.message); return; }
      setTemplates(prev => prev.map(t => t.id === editingId ? { ...t, name: editName.trim(), content_html: contentHtml } : t));
    }
    setEditingId(null);
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    const { error: err } = await supabase.from('mom_templates').delete().eq('id', id);
    if (err) { setError(err.message); return; }
    setTemplates(prev => prev.filter(t => t.id !== id));
  };

  const setDefault = async (id) => {
    await supabase.from('mom_templates').update({ is_default: false }).is('project_id', null);
    await supabase.from('mom_templates').update({ is_default: true }).eq('id', id);
    setTemplates(prev => prev.map(t => ({ ...t, is_default: t.id === id })));
  };

  const sectionStyle = { fontFamily: font, fontSize: 11, fontWeight: 300 };
  const btnGold = { fontFamily: font, fontSize: 11, fontWeight: 400, padding: '5px 14px', background: '#9F902A', color: '#fff', border: 'none', borderRadius: 3, cursor: 'pointer' };
  const btnGrey = { fontFamily: font, fontSize: 11, fontWeight: 300, padding: '5px 14px', background: 'transparent', color: '#666', border: '1px solid #ccc', borderRadius: 3, cursor: 'pointer' };

  if (loading) return <div style={sectionStyle}>Loading templates…</div>;

  if (editingId !== null) {
    return (
      <div style={sectionStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <button onClick={() => setEditingId(null)} style={btnGrey}>← Back</button>
          <span style={{ fontWeight: 400, fontSize: 12, color: '#333' }}>{editingId === 'new' ? 'New Template' : 'Edit Template'}</span>
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={{ display: 'block', marginBottom: 4, color: '#555' }}>Template Name</label>
          <input value={editName} onChange={e => setEditName(e.target.value)}
            style={{ fontFamily: font, fontSize: 12, fontWeight: 300, padding: '4px 8px', border: '1px solid #D1D5DB', borderRadius: 3, width: 300, outline: 'none' }} />
        </div>
        <div style={{ marginBottom: 10 }}>
          <label style={{ display: 'block', marginBottom: 4, color: '#555' }}>Template Content</label>
          <style>{`
            .mom-tpl-editor { outline: none; font-family: Raleway, sans-serif; font-size: 12px; font-weight: 300; color: #222; line-height: 1.7; min-height: 220px; }
            .mom-tpl-editor table { border-collapse: collapse; width: 100%; margin: 8px 0; font-size: 11px; }
            .mom-tpl-editor th, .mom-tpl-editor td { padding: 4px 8px; border: 1px solid #D1D5DB; }
            .mom-tpl-editor th { font-weight: 400; background: transparent; }
            .mom-tpl-editor ul, .mom-tpl-editor ol { margin: 4px 0 6px 20px; padding: 0; }
            .mom-tpl-editor h3 { font-size: 12px; font-weight: 600; margin: 10px 0 4px; }
          `}</style>
          <div
            ref={editorRef}
            className="mom-tpl-editor"
            contentEditable
            suppressContentEditableWarning
            style={{ border: '1px solid #D1D5DB', borderRadius: 3, padding: '10px 12px', background: '#fff', minHeight: 220 }}
          />
        </div>
        {error && <div style={{ color: '#c44', fontSize: 11, marginBottom: 8 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={handleSave} style={btnGold}>Save Template</button>
          <button onClick={() => setEditingId(null)} style={btnGrey}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <div style={sectionStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
        <div>
          <div style={{ fontWeight: 400, fontSize: 12, color: '#333', marginBottom: 2 }}>Global MOM Templates</div>
          <div style={{ fontWeight: 300, color: '#666' }}>Templates available in the MOM Builder for all projects. The default template is used when creating a new MOM document.</div>
        </div>
        <button onClick={openNew} style={btnGold}>+ New Template</button>
      </div>
      {error && <div style={{ color: '#c44', marginBottom: 8 }}>{error}</div>}
      {templates.length === 0 ? (
        <div style={{ color: '#888', padding: '24px 0', textAlign: 'center' }}>No global templates yet. Create one to make it available in all projects.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {templates.map(tpl => (
            <div key={tpl.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 14px', border: '1px solid #E5E7EB', borderRadius: 4, background: tpl.is_default ? 'rgba(159,144,42,0.06)' : '#fff' }}>
              <Star style={{ width: 12, height: 12, color: tpl.is_default ? '#9F902A' : '#ccc', cursor: 'pointer', flexShrink: 0 }}
                title={tpl.is_default ? 'Default template' : 'Set as default'}
                onClick={() => setDefault(tpl.id)} />
              <span style={{ flex: 1, fontWeight: tpl.is_default ? 400 : 300, color: '#222' }}>{tpl.name}</span>
              <span style={{ color: '#aaa', fontSize: 10 }}>{new Date(tpl.created_at).toLocaleDateString()}</span>
              <button onClick={() => openEdit(tpl)} style={{ ...btnGrey, padding: '3px 10px' }}>Edit</button>
              <button onClick={() => handleDelete(tpl.id, tpl.name)} style={{ ...btnGrey, padding: '3px 10px', color: '#c44', borderColor: '#f0c0c0' }}>Delete</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SOW TEMPLATES SECTION
// ============================================================================
function SOWTemplatesSection({ scopeTemplatesHook }) {
  const h = scopeTemplatesHook;
  // scope_templates is left out of the initial settings fetch because of its
  // size — pull it now that this tab is open.
  const ensureTemplates = h?.ensureLoaded;
  useEffect(() => { ensureTemplates?.(); }, [ensureTemplates]);
  const [newName, setNewName] = useState('');
  const [expandedStages, setExpandedStages] = useState({});
  const [addDelivInputs, setAddDelivInputs] = useState({});
  const [editingDelivId, setEditingDelivId] = useState(null);
  const [editingDelivLabel, setEditingDelivLabel] = useState('');
  const [activeTemplateId, setActiveTemplateId] = useState(() => h?.templates?.[0]?.id || '');
  const [editingIntroKey, setEditingIntroKey] = useState(null);
  const [editingIntroText, setEditingIntroText] = useState('');
  const [editingNotesKey, setEditingNotesKey] = useState(null);
  const [editingNotesText, setEditingNotesText] = useState('');
  const [draggingStageKey, setDraggingStageKey] = useState(null);
  const [draggingDelivIdx, setDraggingDelivIdx] = useState(null);
  const [draggingDelivType, setDraggingDelivType] = useState(null);
  const [editingTemplateIntro, setEditingTemplateIntro] = useState(false);
  const [templateIntroText, setTemplateIntroText] = useState('');
  const [editingTemplateName, setEditingTemplateName] = useState(false);
  const [templateNameText, setTemplateNameText] = useState('');
  const [editingStageMetaKey, setEditingStageMetaKey] = useState(null);
  const [stageMetaForm, setStageMetaForm] = useState({ name: '', fullName: '', description: '' });
  const [newStageName, setNewStageName] = useState('');

  const activeTemplate = h?.templates?.find(t => t.id === activeTemplateId) || null;
  const isConfirmed = activeTemplate?.status === 'confirmed';

  const handleCreate = () => {
    const name = newName.trim() || 'SOW Template';
    const tpl = h.createTemplate(name);
    setActiveTemplateId(tpl.id);
    setNewName('');
  };

  const handleConfirm = () => {
    if (!activeTemplate) return;
    h.confirmTemplate(activeTemplate.id);
  };

  const handleDelete = () => {
    if (!activeTemplate) return;
    if (!window.confirm(`Delete template "${activeTemplate.name}"?`)) return;
    h.deleteTemplate(activeTemplate.id);
    const remaining = h.templates.filter(t => t.id !== activeTemplate.id);
    setActiveTemplateId(remaining[0]?.id || '');
  };

  const handleDuplicate = () => {
    if (!activeTemplate) return;
    const copy = h.duplicateTemplate(activeTemplate.id);
    if (copy?.id) setActiveTemplateId(copy.id);
  };

  const handleAddDeliverable = (stageKey, type) => {
    const inputKey = `${stageKey}-${type}`;
    const label = (addDelivInputs[inputKey] || '').trim();
    if (!label || !activeTemplate) return;
    h.addDeliverable(activeTemplate.id, stageKey, label, type);
    setAddDelivInputs(prev => ({ ...prev, [inputKey]: '' }));
  };

  const startEditDeliv = (delivId, label) => {
    setEditingDelivId(delivId);
    setEditingDelivLabel(label);
  };

  const saveEditDeliv = (stageKey, delivId, type) => {
    if (!activeTemplate || !editingDelivLabel.trim()) return;
    h.renameDeliverable(activeTemplate.id, stageKey, delivId, editingDelivLabel.trim(), type);
    setEditingDelivId(null);
    setEditingDelivLabel('');
  };

  // Introduction editing
  const startEditIntro = (stageKey, text) => {
    setEditingIntroKey(stageKey);
    setEditingIntroText(text || '');
  };
  const saveIntro = (stageKey) => {
    if (!activeTemplate) return;
    h.updateTemplateStage(activeTemplate.id, stageKey, { introduction: editingIntroText });
    setEditingIntroKey(null);
    setEditingIntroText('');
  };

  // Notes editing
  const startEditNotes = (stageKey, text) => {
    setEditingNotesKey(stageKey);
    setEditingNotesText(text || '');
  };
  const saveNotes = (stageKey) => {
    if (!activeTemplate) return;
    h.updateTemplateStage(activeTemplate.id, stageKey, { notes: editingNotesText });
    setEditingNotesKey(null);
    setEditingNotesText('');
  };

  // Quantity editing for deliverables
  const handleQuantityChange = (stageKey, delivId, type, newQuantity) => {
    if (!activeTemplate) return;
    const listKey = type === 'optional' ? 'optionalDeliverables' : 'standardDeliverables';
    const stage = activeTemplate.stages.find(s => s.key === stageKey);
    if (!stage) return;
    const updatedList = stage[listKey].map(d =>
      d.id === delivId ? { ...d, quantity: newQuantity } : d
    );
    h.updateTemplateStage(activeTemplate.id, stageKey, { [listKey]: updatedList });
  };

  // Reorder deliverables within a stage via drag
  const handleDelivDragStart = (stageKey, idx, type) => {
    setDraggingStageKey(stageKey);
    setDraggingDelivIdx(idx);
    setDraggingDelivType(type);
  };
  const handleDelivDragOver = (e) => { e.preventDefault(); };
  const handleDelivDrop = (stageKey, dropIdx, type) => {
    if (!activeTemplate || draggingStageKey !== stageKey || draggingDelivType !== type || draggingDelivIdx === null) return;
    const listKey = type === 'optional' ? 'optionalDeliverables' : 'standardDeliverables';
    const stage = activeTemplate.stages.find(s => s.key === stageKey);
    if (!stage) return;
    const items = [...stage[listKey]];
    const [moved] = items.splice(draggingDelivIdx, 1);
    items.splice(dropIdx, 0, moved);
    h.updateTemplateStage(activeTemplate.id, stageKey, { [listKey]: items });
    setDraggingStageKey(null);
    setDraggingDelivIdx(null);
    setDraggingDelivType(null);
  };
  const handleDelivDragEnd = () => {
    setDraggingStageKey(null);
    setDraggingDelivIdx(null);
    setDraggingDelivType(null);
  };

  // Template rename
  const startEditTemplateName = () => {
    if (!activeTemplate) return;
    setTemplateNameText(activeTemplate.name || '');
    setEditingTemplateName(true);
  };
  const saveTemplateName = () => {
    if (!activeTemplate) return;
    const name = templateNameText.trim();
    if (!name) return;
    h.updateTemplate(activeTemplate.id, { name });
    setEditingTemplateName(false);
  };

  // Stage metadata (name / fullName / description) edit
  const startEditStageMeta = (stage) => {
    setEditingStageMetaKey(stage.key);
    setStageMetaForm({
      name: stage.name || '',
      fullName: stage.fullName || '',
      description: stage.description || '',
    });
  };
  const saveStageMeta = (stageKey) => {
    if (!activeTemplate) return;
    h.updateTemplateStage(activeTemplate.id, stageKey, {
      name: stageMetaForm.name.trim() || 'Untitled',
      fullName: stageMetaForm.fullName.trim(),
      description: stageMetaForm.description,
    });
    setEditingStageMetaKey(null);
  };

  // Add / remove / move stages
  const handleAddStage = () => {
    if (!activeTemplate) return;
    const name = newStageName.trim() || 'New Stage';
    const stage = h.addStage(activeTemplate.id, { name });
    setNewStageName('');
    if (stage?.key) setExpandedStages(prev => ({ ...prev, [stage.key]: true }));
  };
  const handleRemoveStage = (stageKey, stageName) => {
    if (!activeTemplate) return;
    if (!window.confirm(`Remove stage "${stageName}" from this template?`)) return;
    h.removeStage(activeTemplate.id, stageKey);
  };
  const handleMoveStage = (stageKey, delta) => {
    if (!activeTemplate) return;
    h.moveStage(activeTemplate.id, stageKey, delta);
  };

  if (!h) return <SectionCard title="SOW Templates"><p style={{ fontSize: 11, color: '#888' }}>Template hook not available.</p></SectionCard>;

  return (
    <div>
      <SectionCard title="Base Templates" subtitle="Create and manage base templates for scope of work documents. Each template can serve a different purpose (e.g. Architecture, Masterplanning, country-specific).">
        {/* Template list / selector */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
          {h.templates.map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTemplateId(t.id)}
              style={{
                ...btnSmall,
                background: t.id === activeTemplateId ? 'var(--brio-gold)' : 'var(--brio-white)',
                color: t.id === activeTemplateId ? 'var(--brio-white)' : '#333',
                border: t.id === activeTemplateId ? '1px solid var(--brio-gold)' : '1px solid rgba(0,0,0,0.15)',
              }}
            >
              {t.name}
              <span style={{
                marginLeft: 6, fontSize: 9, padding: '1px 5px', borderRadius: 8,
                background: t.status === 'confirmed' ? 'var(--brio-green-light)' : 'rgba(0,0,0,0.08)',
                color: t.status === 'confirmed' ? 'var(--brio-green)' : '#888',
              }}>
                {t.status}
              </span>
            </button>
          ))}
        </div>

        {/* Create new */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          <input
            style={{ ...inputStyle, maxWidth: 260 }}
            placeholder="New template name…"
            value={newName}
            onChange={e => setNewName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleCreate(); }}
          />
          <button style={btnGold} onClick={handleCreate}>
            <Plus style={{ width: 12, height: 12, marginRight: 4 }} /> Create Template
          </button>
        </div>
      </SectionCard>

      {/* Active template editor */}
      {activeTemplate && (
        <SectionCard title={activeTemplate.name} subtitle={`Status: ${activeTemplate.status} · ${activeTemplate.stages.length} stages`}>
          {/* Template name editor */}
          <div style={{ border: '1px solid var(--brio-border)', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--brio-text-secondary)' }}>Template Name</span>
              {!editingTemplateName && (
                <button style={btnIcon} onClick={startEditTemplateName} title="Rename template">
                  <Edit2 style={{ width: 11, height: 11 }} />
                </button>
              )}
            </div>
            {editingTemplateName ? (
              <div style={{ display: 'flex', gap: 4 }}>
                <input
                  style={{ ...inputStyle, flex: 1, fontSize: 12, padding: '4px 8px' }}
                  value={templateNameText}
                  onChange={e => setTemplateNameText(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveTemplateName(); }}
                  autoFocus
                />
                <button style={btnGold} onClick={saveTemplateName}>
                  <Check style={{ width: 10, height: 10, marginRight: 3 }} /> Save
                </button>
                <button style={btnSmall} onClick={() => setEditingTemplateName(false)}>Cancel</button>
              </div>
            ) : (
              <div style={{ fontSize: 12, fontWeight: 400, color: 'var(--brio-text-primary)' }}>{activeTemplate.name}</div>
            )}
          </div>

          {/* Actions bar */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
            {!isConfirmed && (
              <button style={btnGold} onClick={handleConfirm}>
                <Lock style={{ width: 12, height: 12, marginRight: 4 }} /> Confirm Template
              </button>
            )}
            <button style={btnSmall} onClick={handleDuplicate} title="Create an editable draft copy of this template">
              <Copy style={{ width: 12, height: 12, marginRight: 4 }} /> Duplicate
            </button>
            <button style={btnDanger} onClick={handleDelete}>
              <Trash2 style={{ width: 12, height: 12, marginRight: 4 }} /> Delete
            </button>
          </div>

          {isConfirmed && (
            <div style={{ fontSize: 10, color: 'var(--brio-text-muted)', fontStyle: 'italic', marginBottom: 12 }}>
              This template is confirmed and available in Contracts &amp; Scope. Editing is still allowed.
            </div>
          )}

          {/* Template-level Introduction */}
          <div style={{ border: '1px solid var(--brio-border)', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--brio-text-secondary)' }}>Introduction</span>
              {!editingTemplateIntro && (
                <button style={btnIcon} onClick={() => { setEditingTemplateIntro(true); setTemplateIntroText(activeTemplate.templateIntroduction || ''); }} title="Edit introduction">
                  <Edit2 style={{ width: 11, height: 11 }} />
                </button>
              )}
            </div>
            {editingTemplateIntro ? (
              <div>
                <textarea
                  style={{ ...inputStyle, width: '100%', minHeight: 100, fontSize: 11, padding: '6px 8px', lineHeight: 1.5, fontFamily: font, resize: 'vertical' }}
                  value={templateIntroText}
                  onChange={e => setTemplateIntroText(e.target.value)}
                />
                <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                  <button style={btnGold} onClick={() => { h.updateTemplate(activeTemplate.id, { templateIntroduction: templateIntroText }); setEditingTemplateIntro(false); }}>
                    <Check style={{ width: 10, height: 10, marginRight: 3 }} /> Save
                  </button>
                  <button style={btnSmall} onClick={() => setEditingTemplateIntro(false)}>Cancel</button>
                </div>
              </div>
            ) : (
              activeTemplate.templateIntroduction ? (
                <SowMarkupText text={activeTemplate.templateIntroduction} />
              ) : (
                <div style={{ fontSize: 10, color: 'var(--brio-text-muted)', fontStyle: 'italic' }}>No introduction text. Click edit to add.</div>
              )
            )}
          </div>

          {/* Summary of Interior Deliverables — split into Warm Shell / FF&E */}
          <div style={{ border: '1px solid var(--brio-border)', borderRadius: 6, padding: '8px 12px', marginBottom: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 400, color: 'var(--brio-text-secondary)', marginBottom: 6 }}>Summary of Interior Deliverables</div>
            <div style={{ fontSize: 9, color: 'var(--brio-text-muted)', marginBottom: 8 }}>
              Items are grouped into <b>Warm Shell</b> and <b>FF&amp;E</b>. On each scope, users can pick one section or both.
            </div>
            {INTERIOR_SECTION_ORDER.map(sectionKey => {
              const sectionItems = (activeTemplate.interiorDeliverables || []).filter(d => (d.section || INTERIOR_SECTIONS.WARM_SHELL) === sectionKey);
              const otherSectionKey = sectionKey === INTERIOR_SECTIONS.WARM_SHELL ? INTERIOR_SECTIONS.FFE : INTERIOR_SECTIONS.WARM_SHELL;
              const addInputKey = `interior-deliv-${sectionKey}`;
              const handleAdd = () => {
                const label = (addDelivInputs[addInputKey] || '').trim();
                if (!label) return;
                const id = `intd-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
                const updated = [...(activeTemplate.interiorDeliverables || []), { id, label, included: true, section: sectionKey }];
                h.updateTemplate(activeTemplate.id, { interiorDeliverables: updated });
                setAddDelivInputs(prev => ({ ...prev, [addInputKey]: '' }));
              };
              return (
                <div key={sectionKey} style={{ marginTop: sectionKey === INTERIOR_SECTIONS.WARM_SHELL ? 0 : 10 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--brio-text-primary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>
                    {INTERIOR_SECTION_LABELS[sectionKey]}
                    <span style={{ marginLeft: 6, fontWeight: 400, color: 'var(--brio-text-muted)', textTransform: 'none', letterSpacing: 0 }}>
                      ({sectionItems.length})
                    </span>
                  </div>
                  {sectionItems.length === 0 ? (
                    <div style={{ fontSize: 10, color: 'var(--brio-text-muted)', fontStyle: 'italic', padding: '2px 0' }}>
                      No {INTERIOR_SECTION_LABELS[sectionKey]} items yet.
                    </div>
                  ) : (
                    sectionItems.map((item, idx) => (
                      <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '2px 0', fontSize: 11 }}>
                        <span style={{ width: 18, textAlign: 'right', fontSize: 10, color: 'var(--brio-text-muted)', flexShrink: 0 }}>{idx + 1}.</span>
                        <span style={{ flex: 1, fontWeight: 300, color: 'var(--brio-text-primary)' }}>{item.label}</span>
                        <button
                          style={btnIcon}
                          title={`Move to ${INTERIOR_SECTION_LABELS[otherSectionKey]}`}
                          onClick={() => {
                            const updated = (activeTemplate.interiorDeliverables || []).map(d =>
                              d.id === item.id ? { ...d, section: otherSectionKey } : d
                            );
                            h.updateTemplate(activeTemplate.id, { interiorDeliverables: updated });
                          }}
                        >
                          <span style={{ fontSize: 9, color: 'var(--brio-gold)', fontWeight: 600 }}>→ {INTERIOR_SECTION_LABELS[otherSectionKey]}</span>
                        </button>
                        <button style={btnIcon} onClick={() => {
                          const updated = (activeTemplate.interiorDeliverables || []).filter(d => d.id !== item.id);
                          h.updateTemplate(activeTemplate.id, { interiorDeliverables: updated });
                        }} title="Remove"><Trash2 style={{ width: 11, height: 11, color: 'var(--brio-red)' }} /></button>
                      </div>
                    ))
                  )}
                  {/* Add new item to this section */}
                  <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                    <input
                      style={{ ...inputStyle, flex: 1, fontSize: 10, padding: '3px 6px' }}
                      placeholder={`Add ${INTERIOR_SECTION_LABELS[sectionKey]} deliverable…`}
                      value={addDelivInputs[addInputKey] || ''}
                      onChange={e => setAddDelivInputs(prev => ({ ...prev, [addInputKey]: e.target.value }))}
                      onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }}
                    />
                    <button style={btnIcon} onClick={handleAdd}>
                      <Plus style={{ width: 12, height: 12, color: 'var(--brio-gold)' }} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Stages */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {activeTemplate.stages.map((stage, stageIdx) => {
              const isExpanded = !!expandedStages[stage.key];
              const allDelivs = [...stage.standardDeliverables, ...stage.optionalDeliverables];
              const isFirstStage = stageIdx === 0;
              const isLastStage = stageIdx === activeTemplate.stages.length - 1;
              return (
                <div key={stage.key} style={{ border: '1px solid var(--brio-border)', borderRadius: 6, overflow: 'hidden' }}>
                  {/* Stage header */}
                  <div
                    onClick={() => setExpandedStages(prev => ({ ...prev, [stage.key]: !prev[stage.key] }))}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                      cursor: 'pointer', background: 'var(--brio-grey-light)', userSelect: 'none',
                    }}
                  >
                    <span style={{ color: 'var(--brio-text-muted)', flexShrink: 0 }}>
                      {isExpanded ? <ChevronDown style={{ width: 12, height: 12 }} /> : <ChevronUp style={{ width: 12, height: 12, transform: 'rotate(90deg)' }} />}
                    </span>
                    <span style={{ fontFamily: font, fontSize: 12, fontWeight: 400, color: 'var(--brio-text-primary)', flex: 1 }}>
                      {stage.name}
                      {stage.fullName && stage.fullName !== stage.name && (
                        <span style={{ fontSize: 10, color: 'var(--brio-text-muted)', marginLeft: 6 }}>{stage.fullName}</span>
                      )}
                    </span>
                    <span style={{ fontSize: 10, color: 'var(--brio-text-muted)' }}>
                      {stage.standardDeliverables.length + stage.optionalDeliverables.length} deliverables
                    </span>
                    <button
                      style={{ ...btnIcon, opacity: isFirstStage ? 0.3 : 1, cursor: isFirstStage ? 'not-allowed' : 'pointer' }}
                      onClick={e => { e.stopPropagation(); if (!isFirstStage) handleMoveStage(stage.key, -1); }}
                      disabled={isFirstStage}
                      title="Move up"
                    >
                      <ChevronUp style={{ width: 12, height: 12 }} />
                    </button>
                    <button
                      style={{ ...btnIcon, opacity: isLastStage ? 0.3 : 1, cursor: isLastStage ? 'not-allowed' : 'pointer' }}
                      onClick={e => { e.stopPropagation(); if (!isLastStage) handleMoveStage(stage.key, 1); }}
                      disabled={isLastStage}
                      title="Move down"
                    >
                      <ChevronDown style={{ width: 12, height: 12 }} />
                    </button>
                    <button
                      style={btnIcon}
                      onClick={e => { e.stopPropagation(); handleRemoveStage(stage.key, stage.name); }}
                      title="Remove stage"
                    >
                      <Trash2 style={{ width: 12, height: 12, color: 'var(--brio-red)' }} />
                    </button>
                  </div>

                  {/* Expanded: stage content editor */}
                  {isExpanded && (
                    <div style={{ padding: '8px 12px 12px 32px' }}>
                      {/* Stage metadata: name / fullName / description */}
                      <div style={{ marginBottom: 12, padding: '8px', background: 'var(--brio-grey-light)', borderRadius: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--brio-text-secondary)' }}>Stage Details</span>
                          {editingStageMetaKey !== stage.key && (
                            <button style={btnIcon} onClick={() => startEditStageMeta(stage)} title="Edit stage details">
                              <Edit2 style={{ width: 11, height: 11 }} />
                            </button>
                          )}
                        </div>
                        {editingStageMetaKey === stage.key ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <div>
                              <div style={{ fontSize: 9, color: 'var(--brio-text-muted)', marginBottom: 2 }}>Name (short code)</div>
                              <input
                                style={{ ...inputStyle, width: '100%', fontSize: 11, padding: '3px 6px' }}
                                value={stageMetaForm.name}
                                onChange={e => setStageMetaForm(prev => ({ ...prev, name: e.target.value }))}
                              />
                            </div>
                            <div>
                              <div style={{ fontSize: 9, color: 'var(--brio-text-muted)', marginBottom: 2 }}>Full Name</div>
                              <input
                                style={{ ...inputStyle, width: '100%', fontSize: 11, padding: '3px 6px' }}
                                value={stageMetaForm.fullName}
                                onChange={e => setStageMetaForm(prev => ({ ...prev, fullName: e.target.value }))}
                              />
                            </div>
                            <div>
                              <div style={{ fontSize: 9, color: 'var(--brio-text-muted)', marginBottom: 2 }}>Description</div>
                              <textarea
                                style={{ ...inputStyle, width: '100%', minHeight: 60, fontSize: 11, padding: '6px 8px', lineHeight: 1.5, fontFamily: font, resize: 'vertical' }}
                                value={stageMetaForm.description}
                                onChange={e => setStageMetaForm(prev => ({ ...prev, description: e.target.value }))}
                              />
                            </div>
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button style={btnGold} onClick={() => saveStageMeta(stage.key)}>
                                <Check style={{ width: 10, height: 10, marginRight: 3 }} /> Save
                              </button>
                              <button style={btnSmall} onClick={() => setEditingStageMetaKey(null)}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <div style={{ fontSize: 10, color: 'var(--brio-text-secondary)', lineHeight: 1.5 }}>
                            <div><span style={{ color: 'var(--brio-text-muted)' }}>Name:</span> {stage.name || <em>(not set)</em>}</div>
                            <div><span style={{ color: 'var(--brio-text-muted)' }}>Full Name:</span> {stage.fullName || <em style={{ color: 'var(--brio-text-muted)' }}>(not set)</em>}</div>
                            <div><span style={{ color: 'var(--brio-text-muted)' }}>Description:</span> {stage.description || <em style={{ color: 'var(--brio-text-muted)' }}>(not set)</em>}</div>
                          </div>
                        )}
                      </div>

                      {/* Introduction text — editable */}
                      <div style={{ marginBottom: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--brio-text-secondary)' }}>Introduction</span>
                          {editingIntroKey !== stage.key && (
                            <button style={btnIcon} onClick={() => startEditIntro(stage.key, stage.introduction)} title="Edit introduction">
                              <Edit2 style={{ width: 11, height: 11 }} />
                            </button>
                          )}
                        </div>
                        {editingIntroKey === stage.key ? (
                          <div>
                            <textarea
                              style={{ ...inputStyle, width: '100%', minHeight: 140, fontSize: 11, padding: '6px 8px', lineHeight: 1.5, fontFamily: font, resize: 'vertical' }}
                              value={editingIntroText}
                              onChange={e => setEditingIntroText(e.target.value)}
                            />
                            <div style={{ fontSize: 9, color: 'var(--brio-text-muted)', marginTop: 2, marginBottom: 4 }}>
                              Markup: **bold**, *italic*. Use {'{{token}}'} for quantity placeholders linked to deliverables.
                            </div>
                            <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                              <button style={btnGold} onClick={() => saveIntro(stage.key)}>
                                <Check style={{ width: 10, height: 10, marginRight: 3 }} /> Save
                              </button>
                              <button style={btnSmall} onClick={() => setEditingIntroKey(null)}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          stage.introduction ? (
                            <SowMarkupText text={stage.introduction} deliverables={allDelivs} style={{ marginBottom: 0 }} />
                          ) : (
                            <div style={{ fontSize: 10, color: 'var(--brio-text-muted)', fontStyle: 'italic' }}>No introduction text. Click edit to add.</div>
                          )
                        )}
                      </div>

                      {/* Standard deliverables — numbered */}
                      <div style={{ fontSize: 10, fontWeight: 400, color: 'var(--brio-text-secondary)', marginBottom: 4 }}>Standard Deliverables</div>
                      {stage.standardDeliverables.map((d, idx) => (
                        <div
                          key={d.id}
                          draggable
                          onDragStart={() => handleDelivDragStart(stage.key, idx, 'standard')}
                          onDragOver={handleDelivDragOver}
                          onDrop={() => handleDelivDrop(stage.key, idx, 'standard')}
                          onDragEnd={handleDelivDragEnd}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', fontSize: 11,
                            cursor: 'grab', opacity: draggingStageKey === stage.key && draggingDelivIdx === idx && draggingDelivType === 'standard' ? 0.4 : 1,
                          }}
                        >
                          {/* Numbering */}
                          <span style={{ width: 18, textAlign: 'right', fontSize: 10, color: 'var(--brio-text-muted)', flexShrink: 0 }}>{idx + 1}.</span>
                          {editingDelivId === d.id ? (
                            <>
                              <input
                                style={{ ...inputStyle, flex: 1, fontSize: 11, padding: '3px 6px' }}
                                value={editingDelivLabel}
                                onChange={e => setEditingDelivLabel(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') saveEditDeliv(stage.key, d.id, 'standard'); }}
                                autoFocus
                              />
                              <button style={btnIcon} onClick={() => saveEditDeliv(stage.key, d.id, 'standard')}><Check style={{ width: 12, height: 12, color: 'var(--brio-gold)' }} /></button>
                              <button style={btnIcon} onClick={() => setEditingDelivId(null)}><X style={{ width: 12, height: 12 }} /></button>
                            </>
                          ) : (
                            <>
                              <span style={{ flex: 1, fontWeight: 300, color: 'var(--brio-text-primary)' }}>{resolveQuantityTokens(d.label, allDelivs) || d.label}</span>
                              {/* Quantity field */}
                              {d.quantity !== undefined && (
                                <input
                                  style={{ ...inputStyle, width: 48, fontSize: 10, padding: '2px 4px', textAlign: 'center' }}
                                  value={d.quantity}
                                  title={d.quantityToken ? `Linked to {{${d.quantityToken}}} in introduction` : 'Quantity'}
                                  onChange={e => handleQuantityChange(stage.key, d.id, 'standard', e.target.value)}
                                  onClick={e => e.stopPropagation()}
                                />
                              )}
                              <button style={btnIcon} onClick={() => startEditDeliv(d.id, d.label)} title="Edit"><Edit2 style={{ width: 11, height: 11 }} /></button>
                              <button style={btnIcon} onClick={() => h.removeDeliverable(activeTemplate.id, stage.key, d.id, 'standard')} title="Remove"><Trash2 style={{ width: 11, height: 11, color: 'var(--brio-red)' }} /></button>
                            </>
                          )}
                        </div>
                      ))}
                      {/* Add standard */}
                      <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                        <input
                          style={{ ...inputStyle, flex: 1, fontSize: 10, padding: '3px 6px' }}
                          placeholder="Add standard deliverable…"
                          value={addDelivInputs[`${stage.key}-standard`] || ''}
                          onChange={e => setAddDelivInputs(prev => ({ ...prev, [`${stage.key}-standard`]: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') handleAddDeliverable(stage.key, 'standard'); }}
                        />
                        <button style={btnIcon} onClick={() => handleAddDeliverable(stage.key, 'standard')}><Plus style={{ width: 12, height: 12, color: 'var(--brio-gold)' }} /></button>
                      </div>

                      {/* Optional deliverables — numbered continuing from standard */}
                      <div style={{ fontSize: 10, fontWeight: 400, color: 'var(--brio-text-secondary)', marginTop: 12, marginBottom: 4 }}>Optional Deliverables</div>
                      {stage.optionalDeliverables.map((d, idx) => (
                        <div
                          key={d.id}
                          draggable
                          onDragStart={() => handleDelivDragStart(stage.key, idx, 'optional')}
                          onDragOver={handleDelivDragOver}
                          onDrop={() => handleDelivDrop(stage.key, idx, 'optional')}
                          onDragEnd={handleDelivDragEnd}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0', fontSize: 11,
                            cursor: 'grab', opacity: draggingStageKey === stage.key && draggingDelivIdx === idx && draggingDelivType === 'optional' ? 0.4 : 1,
                          }}
                        >
                          <span style={{ width: 18, textAlign: 'right', fontSize: 10, color: 'var(--brio-text-muted)', flexShrink: 0 }}>{stage.standardDeliverables.length + idx + 1}.</span>
                          {editingDelivId === d.id ? (
                            <>
                              <input
                                style={{ ...inputStyle, flex: 1, fontSize: 11, padding: '3px 6px' }}
                                value={editingDelivLabel}
                                onChange={e => setEditingDelivLabel(e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') saveEditDeliv(stage.key, d.id, 'optional'); }}
                                autoFocus
                              />
                              <button style={btnIcon} onClick={() => saveEditDeliv(stage.key, d.id, 'optional')}><Check style={{ width: 12, height: 12, color: 'var(--brio-gold)' }} /></button>
                              <button style={btnIcon} onClick={() => setEditingDelivId(null)}><X style={{ width: 12, height: 12 }} /></button>
                            </>
                          ) : (
                            <>
                              <span style={{ flex: 1, fontWeight: 300, color: 'var(--brio-text-muted)', fontStyle: 'italic' }}>{resolveQuantityTokens(d.label, allDelivs) || d.label}</span>
                              {d.quantity !== undefined && (
                                <input
                                  style={{ ...inputStyle, width: 48, fontSize: 10, padding: '2px 4px', textAlign: 'center' }}
                                  value={d.quantity}
                                  title={d.quantityToken ? `Linked to {{${d.quantityToken}}} in introduction` : 'Quantity'}
                                  onChange={e => handleQuantityChange(stage.key, d.id, 'optional', e.target.value)}
                                  onClick={e => e.stopPropagation()}
                                />
                              )}
                              <button style={btnIcon} onClick={() => startEditDeliv(d.id, d.label)} title="Edit"><Edit2 style={{ width: 11, height: 11 }} /></button>
                              <button style={btnIcon} onClick={() => h.removeDeliverable(activeTemplate.id, stage.key, d.id, 'optional')} title="Remove"><Trash2 style={{ width: 11, height: 11, color: 'var(--brio-red)' }} /></button>
                            </>
                          )}
                        </div>
                      ))}
                      {/* Add optional */}
                      <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                        <input
                          style={{ ...inputStyle, flex: 1, fontSize: 10, padding: '3px 6px' }}
                          placeholder="Add optional deliverable…"
                          value={addDelivInputs[`${stage.key}-optional`] || ''}
                          onChange={e => setAddDelivInputs(prev => ({ ...prev, [`${stage.key}-optional`]: e.target.value }))}
                          onKeyDown={e => { if (e.key === 'Enter') handleAddDeliverable(stage.key, 'optional'); }}
                        />
                        <button style={btnIcon} onClick={() => handleAddDeliverable(stage.key, 'optional')}><Plus style={{ width: 12, height: 12, color: 'var(--brio-gold)' }} /></button>
                      </div>

                      {/* Notes — editable */}
                      <div style={{ marginTop: 12, borderTop: '1px solid var(--brio-border)', paddingTop: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                          <span style={{ fontSize: 10, fontWeight: 400, color: 'var(--brio-text-secondary)' }}>Notes</span>
                          {editingNotesKey !== stage.key && (
                            <button style={btnIcon} onClick={() => startEditNotes(stage.key, stage.notes)} title="Edit notes">
                              <Edit2 style={{ width: 11, height: 11 }} />
                            </button>
                          )}
                        </div>
                        {editingNotesKey === stage.key ? (
                          <div>
                            <textarea
                              style={{ ...inputStyle, width: '100%', minHeight: 80, fontSize: 11, padding: '6px 8px', lineHeight: 1.5, fontFamily: font, resize: 'vertical' }}
                              value={editingNotesText}
                              onChange={e => setEditingNotesText(e.target.value)}
                            />
                            <div style={{ fontSize: 9, color: 'var(--brio-text-muted)', marginTop: 2, marginBottom: 4 }}>
                              Markup: **bold**, *italic*. Use {'{{revisions}}'} and {'{{meetings}}'} for count placeholders.
                            </div>
                            <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                              <button style={btnGold} onClick={() => saveNotes(stage.key)}>
                                <Check style={{ width: 10, height: 10, marginRight: 3 }} /> Save
                              </button>
                              <button style={btnSmall} onClick={() => setEditingNotesKey(null)}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          stage.notes ? (
                            <SowMarkupText text={resolveNotesTokens(stage.notes, stage)} deliverables={allDelivs} style={{ fontStyle: 'italic', color: 'var(--brio-text-secondary)' }} />
                          ) : (
                            <div style={{ fontSize: 10, color: 'var(--brio-text-muted)', fontStyle: 'italic' }}>No notes. Click edit to add.</div>
                          )
                        )}
                        {/* Revisions / Meetings count inputs (design stages only) */}
                        {stage.notesRevisions !== undefined && (
                          <div style={{ display: 'flex', gap: 12, marginTop: 8, alignItems: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ fontSize: 10, color: 'var(--brio-text-secondary)' }}>Revisions:</span>
                              <input
                                style={{ ...inputStyle, width: 48, fontSize: 10, padding: '2px 4px', textAlign: 'center' }}
                                value={stage.notesRevisions}
                                title="Number of revisions — linked to {{revisions}} in notes"
                                onChange={e => h.updateTemplateStage(activeTemplate.id, stage.key, { notesRevisions: e.target.value })}
                                onClick={e => e.stopPropagation()}
                              />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                              <span style={{ fontSize: 10, color: 'var(--brio-text-secondary)' }}>Meetings:</span>
                              <input
                                style={{ ...inputStyle, width: 48, fontSize: 10, padding: '2px 4px', textAlign: 'center' }}
                                value={stage.notesMeetings}
                                title="Number of meetings — linked to {{meetings}} in notes"
                                onChange={e => h.updateTemplateStage(activeTemplate.id, stage.key, { notesMeetings: e.target.value })}
                                onClick={e => e.stopPropagation()}
                              />
                            </div>
                          </div>
                        )}
                      </div>

                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Add new stage */}
          <div style={{ display: 'flex', gap: 4, marginTop: 12 }}>
            <input
              style={{ ...inputStyle, flex: 1, fontSize: 11, padding: '4px 8px' }}
              placeholder="New stage name…"
              value={newStageName}
              onChange={e => setNewStageName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleAddStage(); }}
            />
            <button style={btnGold} onClick={handleAddStage}>
              <Plus style={{ width: 12, height: 12, marginRight: 4 }} /> Add Stage
            </button>
          </div>
        </SectionCard>
      )}

      {h.templates.length === 0 && (
        <SectionCard>
          <p style={{ fontSize: 11, color: 'var(--brio-text-muted)', textAlign: 'center', padding: 20 }}>
            No SOW templates yet. Create one to get started.
          </p>
        </SectionCard>
      )}
    </div>
  );
}


// ============================================================================
// 6. DATABASE EXPORT / IMPORT
// ============================================================================
function DatabaseSection({ s }) {
  const [exportChecks, setExportChecks] = useState(() => {
    const map = {};
    EXPORT_TABLES.forEach(t => { map[t.key] = t.def; });
    return map;
  });
  const [exporting, setExporting] = useState(false);
  const [importMode, setImportMode] = useState('merge');
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [replaceConfirm, setReplaceConfirm] = useState('');
  const fileRef = useRef(null);

  // ── Legacy Salary CSV Import ──
  const { importLegacyCSV, loading: salaryImporting } = useSalaryLedger();
  const [salaryFile, setSalaryFile] = useState(null);
  const [salaryResult, setSalaryResult] = useState(null);
  const salaryFileRef = useRef(null);

  const handleSalaryFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSalaryFile(file);
    setSalaryResult(null);
  };

  const handleSalaryImport = async () => {
    if (!salaryFile) return;
    const result = await importLegacyCSV(salaryFile);
    setSalaryResult(result);
  };

  const handleSalaryClear = () => {
    setSalaryFile(null);
    setSalaryResult(null);
    if (salaryFileRef.current) salaryFileRef.current.value = '';
  };

  const handleExport = async () => {
    const selected = Object.entries(exportChecks).filter(([, v]) => v).map(([k]) => k);
    if (selected.length === 0) return;
    setExporting(true);
    const result = await s.exportSettings(selected);
    setExporting(false);
    if (result.success) {
      const str = JSON.stringify(result.data, null, 2);
      const blob = new Blob([str], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `brio-settings-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 200);
      // The file still downloads on a partial export, but say so plainly: a
      // silently incomplete export is worse than a noisy one.
      if (result.failures?.length) {
        alert(
          `Export finished with ${result.failures.length} table(s) missing:\n\n` +
          result.failures.map(f => `• ${f.table}: ${f.message}`).join('\n') +
          '\n\nThe downloaded file is incomplete and lists these under "failures".'
        );
      }
    } else {
      alert('Export failed: ' + (result.error || 'Unknown error'));
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        setImportPreview(data);
      } catch (err) {
        alert('Invalid JSON file: ' + err.message);
        setImportFile(null);
      }
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!importPreview) return;
    if (importMode === 'replace' && replaceConfirm !== 'REPLACE') {
      alert('Type REPLACE to confirm destructive import.');
      return;
    }
    setImporting(true);
    const result = await s.importSettings(importPreview, importMode);
    setImporting(false);
    if (result.success) {
      const summary = result.summary;
      alert(`Import complete!\n\nRecords processed: ${summary.inserts}\n${summary.errors.length > 0 ? 'Errors: ' + summary.errors.join('; ') : 'No errors.'}`);
      setImportFile(null);
      setImportPreview(null);
      setReplaceConfirm('');
    } else {
      alert('Import failed: ' + (result.error || 'Unknown error'));
    }
  };

  return (
    <div>
      <SectionCard title="Export Database" subtitle="Download a JSON snapshot of selected database tables">
        <div style={{ marginBottom: 12 }}>
          {(() => {
            const groups = [...new Set(EXPORT_TABLES.map(t => t.group))];
            return groups.map(group => {
              const items = EXPORT_TABLES.filter(t => t.group === group);
              const allChecked = items.every(t => exportChecks[t.key]);
              const someChecked = items.some(t => exportChecks[t.key]);
              return (
                <div key={group} style={{ marginBottom: 10 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, fontSize: 11, fontWeight: 600, cursor: 'pointer', color: 'var(--brio-text-primary, #111)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                    <input type="checkbox" checked={allChecked} ref={el => { if (el) el.indeterminate = someChecked && !allChecked; }}
                      onChange={e => setExportChecks(p => {
                        const next = { ...p };
                        items.forEach(t => { next[t.key] = e.target.checked; });
                        return next;
                      })} style={{ borderRadius: 0, WebkitAppearance: 'none', appearance: 'none', width: 14, height: 14, border: '1px solid var(--brio-border, #ccc)', backgroundColor: allChecked ? 'var(--brio-gold, #9F902A)' : 'var(--brio-white, #fff)', cursor: 'pointer' }} />
                    {group}
                  </label>
                  <div style={{ paddingLeft: 22 }}>
                    {items.map(t => (
                      <label key={t.key} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, fontSize: 11, fontWeight: 300, cursor: 'pointer' }}>
                        <input type="checkbox" checked={exportChecks[t.key] || false}
                          onChange={e => setExportChecks(p => ({ ...p, [t.key]: e.target.checked }))}
                          style={{ borderRadius: 0, WebkitAppearance: 'none', appearance: 'none', width: 14, height: 14, border: '1px solid var(--brio-border, #ccc)', backgroundColor: exportChecks[t.key] ? 'var(--brio-gold, #9F902A)' : 'var(--brio-white, #fff)', cursor: 'pointer' }} />
                        {t.label}
                      </label>
                    ))}
                  </div>
                </div>
              );
            });
          })()}
        </div>
        <button onClick={handleExport} disabled={exporting} style={btnGold}>
          <Download style={{ width: 11, height: 11, marginRight: 5 }} />
          {exporting ? 'Exporting\u2026' : 'Export Selected'}
        </button>
      </SectionCard>

      <SectionCard title="Import Settings" subtitle="Upload a previously exported JSON file to merge or replace settings">
        <FieldLabel label="Import mode">
          <div style={{ display: 'flex', gap: 0, border: '1px solid rgba(0,0,0,0.15)', borderRadius: 4, overflow: 'hidden', width: 'fit-content', marginBottom: 12 }}>
            {['merge', 'replace'].map(m => (
              <button key={m} onClick={() => setImportMode(m)} style={{
                padding: '5px 16px', border: 'none', cursor: 'pointer', fontFamily: font,
                fontSize: 11, fontWeight: 400, textTransform: 'capitalize',
                background: importMode === m ? (m === 'replace' ? '#934948' : '#111') : '#fff',
                color: importMode === m ? '#fff' : '#333'
              }}>{m}</button>
            ))}
          </div>
        </FieldLabel>

        {importMode === 'replace' && (
          <div style={{ background: 'rgba(147,73,72,0.08)', padding: 12, borderRadius: 4, marginBottom: 12, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <AlertTriangle style={{ width: 14, height: 14, color: '#934948', flexShrink: 0, marginTop: 1 }} />
            <div style={{ fontSize: 10, fontWeight: 300, color: '#6b2d2d' }}>
              <strong style={{ fontWeight: 500 }}>Warning:</strong> Replace mode deletes all existing data before importing.
              <div style={{ marginTop: 6 }}>
                <input type="text" value={replaceConfirm} onChange={e => setReplaceConfirm(e.target.value)}
                  placeholder="Type REPLACE to confirm" style={{ ...inputStyle, width: 200, borderColor: 'rgba(147,73,72,0.3)' }} />
              </div>
            </div>
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <input type="file" accept=".json" ref={fileRef} onChange={handleFileSelect} style={{ display: 'none' }} />
          <button onClick={() => fileRef.current?.click()} style={btnSmall}>
            <Upload style={{ width: 10, height: 10, marginRight: 4 }} />
            {importFile ? importFile.name : 'Select JSON File'}
          </button>
        </div>

        {importPreview && (
          <div style={{ marginBottom: 12, background: '#f8f8f8', padding: 12, borderRadius: 4, fontSize: 10, fontWeight: 300 }}>
            <div style={{ fontWeight: 400, marginBottom: 6 }}>Import Preview</div>
            <div>Version: {importPreview.version || 'unknown'}</div>
            <div>Export Date: {importPreview.exportDate || 'unknown'}</div>
            {importPreview.tables && Object.entries(importPreview.tables).map(([table, rows]) => (
              <div key={table}>{table}: {Array.isArray(rows) ? rows.length : 0} rows</div>
            ))}
          </div>
        )}

        <button onClick={handleImport} disabled={importing || !importPreview}
          style={importMode === 'replace' ? btnDanger : btnGold}>
          {importing ? 'Importing\u2026' : `Import (${importMode})`}
        </button>
      </SectionCard>

      <SectionCard title="Import Legacy Salary CSV" subtitle="Upload a CSV with monthly salary data (pre-2026) to populate the salary ledger. Columns: month rows as YYYY-MM, user auth UUIDs as headers.">
        <div style={{ marginBottom: 12 }}>
          <input type="file" accept=".csv,.txt" ref={salaryFileRef} onChange={handleSalaryFileSelect} style={{ display: 'none' }} />
          <button onClick={() => salaryFileRef.current?.click()} style={btnSmall}>
            <Upload style={{ width: 10, height: 10, marginRight: 4 }} />
            {salaryFile ? salaryFile.name : 'Select CSV File'}
          </button>
          {salaryFile && (
            <button onClick={handleSalaryClear} style={{ ...btnIcon, marginLeft: 4 }} title="Clear">
              <X style={{ width: 10, height: 10 }} />
            </button>
          )}
        </div>

        {salaryFile && !salaryResult && (
          <div style={{ marginBottom: 12, background: '#f8f8f8', padding: 12, borderRadius: 4, fontSize: 10, fontWeight: 300 }}>
            <div>File: {salaryFile.name}</div>
            <div>Size: {(salaryFile.size / 1024).toFixed(1)} KB</div>
          </div>
        )}

        {salaryResult && (
          <div style={{
            marginBottom: 12, padding: 12, borderRadius: 4, fontSize: 10, fontWeight: 300,
            background: salaryResult.success ? 'rgba(159,144,42,0.08)' : 'rgba(147,73,72,0.08)',
            border: `1px solid ${salaryResult.success ? 'rgba(159,144,42,0.2)' : 'rgba(147,73,72,0.2)'}`
          }}>
            <div style={{ fontWeight: 500, marginBottom: 6, color: salaryResult.success ? '#6b6520' : '#6b2d2d' }}>
              {salaryResult.success ? 'Import Successful' : 'Import Failed'}
            </div>
            {salaryResult.rows_parsed != null && <div>Rows parsed: {salaryResult.rows_parsed}</div>}
            {salaryResult.rows_imported != null && <div>Rows imported: {salaryResult.rows_imported}</div>}
            {salaryResult.unique_users != null && <div>Unique users: {salaryResult.unique_users}</div>}
            {salaryResult.period_start && <div>Period: {salaryResult.period_start} to {salaryResult.period_end}</div>}
            {salaryResult.batch_id != null && <div>Batch ID: {salaryResult.batch_id}</div>}
            {salaryResult.staging_rows != null && <div>Staging rows: {salaryResult.staging_rows}</div>}
            {salaryResult.errors?.length > 0 && (
              <div style={{ marginTop: 6, color: '#934948' }}>
                <div style={{ fontWeight: 500 }}>Errors:</div>
                {salaryResult.errors.map((e, i) => <div key={i}>{e}</div>)}
              </div>
            )}
            {salaryResult.warnings?.length > 0 && (
              <div style={{ marginTop: 6, color: '#8a7a20' }}>
                <div style={{ fontWeight: 500 }}>Warnings ({salaryResult.warnings.length}):</div>
                {salaryResult.warnings.slice(0, 20).map((w, i) => <div key={i}>{w}</div>)}
                {salaryResult.warnings.length > 20 && <div>...and {salaryResult.warnings.length - 20} more</div>}
              </div>
            )}
            {salaryResult.error && !salaryResult.success && (
              <div style={{ marginTop: 4, color: '#934948' }}>{salaryResult.error}</div>
            )}
          </div>
        )}

        <button onClick={handleSalaryImport} disabled={salaryImporting || !salaryFile} style={btnGold}>
          <Upload style={{ width: 11, height: 11, marginRight: 5 }} />
          {salaryImporting ? 'Importing\u2026' : 'Import Salary CSV'}
        </button>

        <div style={{ marginTop: 10, fontSize: 9, fontWeight: 300, color: '#999', lineHeight: '1.5' }}>
          CSV format: First column = period key (YYYY-MM for monthly salary, YYYY-bonus for annual bonus).
          Remaining columns = user auth_user_id UUIDs. Delimiter auto-detected (comma or semicolon).
          Only months up to 2025-12 are imported. Existing rows are updated (upsert on auth_user_id + month).
        </div>
      </SectionCard>
    </div>
  );
}

// ============================================================================
// RADAR SETTINGS — Rule thresholds, enable/disable, severity
// ============================================================================

const DOMAIN_LABELS = {
  finance: 'Finance', projects: 'Projects', bd: 'Business Development',
  hiring: 'Hiring', team: 'Team', system: 'System / Data',
};

const SEVERITY_OPTIONS = ['critical', 'warning', 'signal'];

function RadarSettingsSection({ radarSettingsHook }) {
  const hook = radarSettingsHook;
  const [saving, setSaving] = useState(null);
  const [feedback, setFeedback] = useState({});
  const [editingConfig, setEditingConfig] = useState(null); // ruleKey being edited
  const [configDraft, setConfigDraft] = useState({});

  if (!hook || hook.loading) {
    return (
      <div style={{ textAlign: 'center', padding: 32 }}>
        <RefreshCw style={{ width: 20, height: 20, animation: 'spin 1s linear infinite', color: '#9F902A', margin: '0 auto 8px' }} />
        <p style={{ fontSize: 11, fontWeight: 300, color: '#666' }}>Loading radar settings...</p>
      </div>
    );
  }

  const rulesByDomain = {};
  for (const [ruleKey, meta] of Object.entries(RULE_META)) {
    const domain = meta.domain || 'system';
    if (!rulesByDomain[domain]) rulesByDomain[domain] = [];
    rulesByDomain[domain].push({ ruleKey, ...meta });
  }

  const handleToggle = async (ruleKey, enabled) => {
    setSaving(ruleKey);
    const res = await hook.updateRule(ruleKey, { enabled });
    setFeedback(f => ({ ...f, [ruleKey]: res.success ? 'saved' : 'error' }));
    setSaving(null);
    setTimeout(() => setFeedback(f => ({ ...f, [ruleKey]: null })), 2000);
  };

  const handleSeverityChange = async (ruleKey, severity) => {
    setSaving(ruleKey);
    const res = await hook.updateRule(ruleKey, { severity });
    setFeedback(f => ({ ...f, [ruleKey]: res.success ? 'saved' : 'error' }));
    setSaving(null);
    setTimeout(() => setFeedback(f => ({ ...f, [ruleKey]: null })), 2000);
  };

  const handleEditConfig = (ruleKey) => {
    const current = hook.getRuleConfig(ruleKey);
    setConfigDraft({ ...current.config });
    setEditingConfig(ruleKey);
  };

  const handleSaveConfig = async () => {
    if (!editingConfig) return;
    setSaving(editingConfig);
    const res = await hook.updateRule(editingConfig, { config: configDraft });
    setFeedback(f => ({ ...f, [editingConfig]: res.success ? 'saved' : 'error' }));
    setSaving(null);
    setEditingConfig(null);
    setTimeout(() => setFeedback(f => ({ ...f, [editingConfig]: null })), 2000);
  };

  return (
    <div>
      <SectionCard title="Radar Settings" subtitle="Configure detection rules, thresholds, and severity levels for the Radar operational dashboard">
        <p style={{ fontSize: 11, fontWeight: 300, color: '#888', margin: '0 0 16px' }}>
          Enable or disable rules, adjust severity, and configure thresholds for each detection rule. Changes take effect immediately.
        </p>

        {Object.entries(DOMAIN_LABELS).map(([domain, domainLabel]) => {
          const rules = rulesByDomain[domain] || [];
          if (rules.length === 0) return null;

          return (
            <div key={domain} style={{ marginBottom: 20 }}>
              <h4 style={{ fontSize: 12, fontWeight: 500, color: '#333', margin: '0 0 8px', borderBottom: '1px solid #eee', paddingBottom: 4 }}>
                {domainLabel}
              </h4>

              {rules.map(({ ruleKey, label, description }) => {
                const config = hook.getRuleConfig(ruleKey);
                const isEditing = editingConfig === ruleKey;
                const isSaving = saving === ruleKey;
                const fb = feedback[ruleKey];

                return (
                  <div key={ruleKey} style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    padding: '8px 0', borderBottom: '1px solid #f5f5f5',
                    opacity: config.enabled ? 1 : 0.5,
                  }}>
                    {/* Enable/disable toggle */}
                    <label style={{ display: 'flex', alignItems: 'center', gap: 0, cursor: 'pointer', marginTop: 2, flexShrink: 0 }}>
                      <input
                        type="checkbox"
                        checked={config.enabled}
                        onChange={(e) => handleToggle(ruleKey, e.target.checked)}
                        style={{ cursor: 'pointer' }}
                      />
                    </label>

                    {/* Rule info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 12, fontWeight: 400, color: '#222' }}>{label}</div>
                      <div style={{ fontSize: 10, color: '#888', marginTop: 1 }}>{description}</div>

                      {/* Config thresholds (inline) */}
                      {config.config && Object.keys(config.config).length > 0 && !isEditing && (
                        <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                          {Object.entries(config.config).map(([k, v]) => (
                            <span key={k} style={{ fontSize: 10, color: '#666', background: '#f7f7f7', padding: '1px 6px', borderRadius: 4 }}>
                              {k.replace(/([A-Z])/g, ' $1').toLowerCase()}: {String(v)}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Inline config editor */}
                      {isEditing && (
                        <div style={{ marginTop: 6, padding: 8, background: '#fafafa', borderRadius: 6 }}>
                          {Object.entries(configDraft).map(([k, v]) => (
                            <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                              <label style={{ fontSize: 10, color: '#555', width: 140, textAlign: 'right' }}>
                                {k.replace(/([A-Z])/g, ' $1').toLowerCase()}
                              </label>
                              <input
                                type={typeof v === 'number' ? 'number' : 'text'}
                                value={v}
                                onChange={(e) => setConfigDraft(prev => ({
                                  ...prev,
                                  [k]: typeof v === 'number' ? Number(e.target.value) : e.target.value
                                }))}
                                style={{ ...inputStyle, width: 100, padding: '2px 6px', fontSize: 11 }}
                              />
                            </div>
                          ))}
                          <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                            <button style={{ ...btnGold, fontSize: 10, padding: '3px 10px' }} onClick={handleSaveConfig}>Save</button>
                            <button style={{ ...btnSmall, fontSize: 10, padding: '3px 10px' }} onClick={() => setEditingConfig(null)}>Cancel</button>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Severity selector */}
                    <select
                      value={config.severity}
                      onChange={(e) => handleSeverityChange(ruleKey, e.target.value)}
                      disabled={!config.enabled}
                      style={{ ...inputStyle, width: 90, padding: '2px 4px', fontSize: 10 }}
                    >
                      {SEVERITY_OPTIONS.map(sev => (
                        <option key={sev} value={sev}>{sev}</option>
                      ))}
                    </select>

                    {/* Edit thresholds button */}
                    {config.config && Object.keys(config.config).length > 0 && !isEditing && config.enabled && (
                      <button
                        style={{ ...btnSmall, fontSize: 10, padding: '2px 6px' }}
                        onClick={() => handleEditConfig(ruleKey)}
                      >
                        <Edit2 style={{ width: 10, height: 10 }} />
                      </button>
                    )}

                    {/* Feedback */}
                    {isSaving && <RefreshCw style={{ width: 12, height: 12, animation: 'spin 1s linear infinite', color: '#9F902A', flexShrink: 0 }} />}
                    {fb === 'saved' && <Check style={{ width: 12, height: 12, color: '#38a169', flexShrink: 0 }} />}
                    {fb === 'error' && <X style={{ width: 12, height: 12, color: '#C53030', flexShrink: 0 }} />}
                  </div>
                );
              })}
            </div>
          );
        })}
      </SectionCard>
    </div>
  );
}
